import { QueryClient, QueryClientProvider, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CSSProperties, FormEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input, Textarea, Select, DateInputVN } from "./shared/ui/form";
import { PrimaryBtn, GhostBtn, ConfirmBtn } from "./shared/ui/buttons";
import { Modal } from "./shared/ui/modal";
import { ErrorBox } from "./shared/ui/feedback";
import { SectionHeader } from "./shared/ui/section";
export { Input, Textarea, Select, DateInputVN, PrimaryBtn, GhostBtn, ConfirmBtn, Modal, ErrorBox, SectionHeader };
import { api, ApiError, API_BASE } from "@/lib/api";
import { fmtDate, fmtDateTime, todayVN, nowVNDate, addDaysISO } from "@/lib/utils";
import {
  Sparkles, Home, LogOut, Menu, Package, KanbanSquare, UserCircle2, BarChart3,
  Wallet, Image as ImageIcon, Tag, Type, Clock, Users, Download, Upload, Camera, Boxes,
  TrendingUp, TrendingDown, AlertTriangle, Plus, Pencil, Trash2,
  ArrowDownCircle, ArrowUpCircle, Folder, FolderPlus, ChevronRight, ChevronLeft,
  Eye, EyeOff, Star, X, LayoutGrid, Rows3, ImagePlus, ArrowUpDown, ArrowUp, ArrowDown, Search,
  ExternalLink, Link2, Loader2, Server, List, CalendarDays, GanttChartSquare,
  FolderKanban, CheckCircle2, Circle, ChevronDown, MoreHorizontal, Flag, Undo2, User,
  Facebook, Instagram, Phone, Handshake, PieChart as PieIcon,
} from "lucide-react";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import FileSaver from "file-saver";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, rectSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CheckSquare, Square, RefreshCw } from "lucide-react";
const { saveAs: mediaSaveAs } = FileSaver;
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from "recharts";
import { EditModeProvider, EditModeToggle } from "@/static/EditMode";
import { useSiteText } from "@/static/EditMode";
import { upsertSiteText } from "@/lib/site-texts-service";
import { StickerCanvas } from "@/static/Sticker";
// GalleryPage (public) đã bị thay bằng AdminGalleryPage cho route admin "gallery".
// Public dùng PublicCollectionPage. Giữ import để tránh dead-code remove khác file.
import { GalleryPage as _GalleryPage } from "@/static/Gallery";
void _GalleryPage;
import { PushToggle } from "@/static/Push";
import { PublicHomePage, PublicCollectionPage } from "@/static/Public";
import { FinancePage } from "./features/finance/FinancePage";
import { TasksPage } from "./features/tasks/TasksPage";
import { AdminAppearanceToggle } from "./shared/theme/AdminAppearanceToggle";
import { AdminThemeSwitcher } from "./shared/theme/AdminThemeSwitcher";
import { buildAdminThemeStyle, useAdminTheme } from "./shared/theme/adminTheme";
import {
  FINANCE_CSV_COLUMNS as _FINANCE_CSV_COLUMNS,
  exportFinanceCsv as _exportFinanceCsv,
  importFinanceCsv as _importFinanceCsv,
} from "./shared/csv/csvExport";
import { downloadCsvTemplate as _downloadCsvTemplate } from "./shared/csv/csvDownload";
import { importCsvViaApi as _importCsvViaApi } from "./shared/csv/csvImportClient";
import type { CsvImportKind as _CsvImportKind, CsvImportResult as _CsvImportResult } from "./shared/csv/csvTemplates";
// Re-export để các page cũ vẫn import từ "../../AppStatic" hoạt động (behavior unchanged).
export const FINANCE_CSV_COLUMNS = _FINANCE_CSV_COLUMNS;
export const exportFinanceCsv = _exportFinanceCsv;
export const importFinanceCsv = _importFinanceCsv;
export const downloadCsvTemplate = _downloadCsvTemplate;
export const importCsvViaApi = _importCsvViaApi;
export type CsvImportKind = _CsvImportKind;
export type CsvImportResult = _CsvImportResult;

/* ============================================================ Types */

type PhpUser = { id: string; email: string; display_name?: string | null; role?: string | null };

export type Customer = {
  id: string;
  name: string;
  phone?: string | null;
  address?: string | null;
  facebook?: string | null;
  instagram?: string | null;
  note?: string | null;
  blacklist?: boolean;
  tags?: string[] | null;
  vip_score?: number | null;
  /** Phase R1 — vùng khách: 'hcm' (3 ngày giữ đồ) | 'tinh' (7 ngày, gồm vận chuyển). */
  region?: "hcm" | "tinh" | null;
  /** Mảng nhiều định danh — chống trùng khách. Backend tự mirror phần tử [0] vào primary. */
  phones?: string[];
  facebooks?: string[];
  instagrams?: string[];
  /** V3 sync — tên thật của khách (tách khỏi nickname / tên gọi quen). */
  real_name?: string | null;
  /** V3 sync — danh sách tên người chuyển khoản (theo sao kê ngân hàng). */
  bank_transfer_names?: string[] | null;
  /** Lý do khớp khi server trả candidate. */
  _match_reasons?: string[];
};

type CustomerHistory = {
  tasks: Array<{ id: string; title: string; status?: string | null; start_date?: string | null; due_date?: string | null; completed_at?: string | null; created_at?: string | null }>;
  finance: FinanceEntry[];
  totalIncome: number;
  totalExpense: number;
  netSpend: number;
  taskCount: number;
};


export type TaskProjectGroup = "cho-thue" | "lam-do" | "chup-anh" | "order" | "luu-tru";
export type Project = { id: string; key?: string | null; name: string; project_group?: TaskProjectGroup | string | null; color?: string | null; icon?: string | null; sort_order?: number; is_pinned?: boolean; revenue_stream?: "main" | "side" | string | null };
export const TASK_PROJECT_GROUPS: Array<{ key: TaskProjectGroup; label: string; dot: string }> = [
  { key: "cho-thue", label: "Cho thuê", dot: "bg-emerald-500" },
  { key: "lam-do", label: "Làm đồ", dot: "bg-amber-500" },
  { key: "chup-anh", label: "Chụp ảnh", dot: "bg-purple-500" },
  { key: "order", label: "Order", dot: "bg-orange-500" },
  { key: "luu-tru", label: "Lưu trữ", dot: "bg-slate-500" },
];

/** Project mặc định do backend PHP seed (db.php). Frontend KHÔNG seed để tránh dupe. */


/** Phân loại sâu hơn project_group để form/list biết render khác nhau. */
export type GroupKind = "cho-thue" | "lam-do" | "chup-anh" | "order" | "luu-tru";
export function deriveGroupKind(project: { name?: string; project_group?: string | null } | null | undefined): GroupKind {
  if (!project) return "cho-thue";
  const g = project.project_group;
  if (g === "lam-do" || g === "order" || g === "luu-tru" || g === "cho-thue" || g === "chup-anh") return g as GroupKind;
  const n = (project.name || "").toLowerCase();
  if (n.includes("chụp ảnh") || n.includes("chup anh") || n.includes("photo")) return "chup-anh";
  if (n.includes("làm đồ") || n.includes("lam do")) return "lam-do";
  if (n.includes("order")) return "order";
  if (n.includes("lưu trữ") || n.includes("luu tru")) return "luu-tru";
  return "cho-thue";
}

/** K9 Phase 3 — Sections + Custom Fields cho Task (đồng bộ schema cũ admin.tasks). */
export type FieldType = "text" | "number" | "date" | "select" | "checkbox";
export type BoardColumnGroup = "todo" | "doing" | "done" | "notes";
export type BoardColumn = { key: string; label: string; group: BoardColumnGroup };
export type TaskSection = {
  id: string; project_id: string; key?: string | null;
  name: string; sort_order: number;
  board_columns?: BoardColumn[] | null;
};
export type CustomField = {
  id: string; project_id: string; key?: string | null;
  name: string; type: FieldType;
  options?: string[] | null;
  section_keys?: string[] | null;
  sort_order: number;
};

export type Task = {
  id: string;
  project_id: string;
  section_id?: string | null;
  title: string;
  description?: string | null;
  status?: string | null;
  priority?: string | null;
  customer_id?: string | null;
  start_date?: string | null;
  due_date?: string | null;
  completed_at?: string | null;
  piwigo_album_id?: number | null;
  piwigo_album_url?: string | null;
  album_status?: string | null;
  tags?: string[] | null;
  custom_values?: Record<string, unknown> | null;
  sort_order?: number;
  /** K9 Phase 2: liên thông Task ↔ Product/Inventory. */
  product_ids?: string[] | null;
  inventory_item_ids?: string[] | null;
  reminder_days_before?: number | null;
};

export type FinanceEntry = {
  id: string;
  kind: "income" | "expense" | string;
  amount: number;
  currency?: string | null;
  category?: string | null;
  occurred_on?: string | null;
  note?: string | null;
  item_name?: string | null;
  quantity?: number | null;
  unit_price?: number | null;
  task_id?: string | null;
  customer_id?: string | null;
  project_id?: string | null;
  auto?: boolean;
  revenue_stream?: "main" | "side" | string;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};


export type Page =
  | "home" | "collection"
  | "login" | "dashboard" | "customers" | "tasks" | "finance" | "backup"
  | "photo-checklist" | "inventory" | "taxonomy"
  | "products" | "reports" | "media" | "timeline" | "users" | "piwigo"
  | "history"
  | "gallery" | "partners" | "site-text";

type InventoryCategory = "trang-phuc" | "phu-kien-cosplay" | "mo-hinh-sung";
const INV_CATEGORY_LABEL: Record<InventoryCategory, string> = {
  "trang-phuc": "Trang phục",
  "phu-kien-cosplay": "Phụ kiện cosplay",
  "mo-hinh-sung": "Mô hình súng",
};
export type InventoryParent = {
  id: string; name: string; slug: string;
  category: string | null; mode: string;
  revenue_stream: "main" | "side";
  is_public: boolean;
  cover_image: string | null;
  first_image: string | null;
};
export type InventoryItem = {
  id: string; name: string; category: InventoryCategory; variant?: string | null;
  note?: string | null; image_url?: string | null; group_name?: string | null;
  available: boolean; sort_order?: number;
  updated_at?: string | null;
  product_id?: string | null;
  parent?: InventoryParent | null;
};
type AttrType = { id: string; key: string; label: string; sort_order: number };
type AttrValue = { id: string; type: string; value: string; label: string; sort_order: number };

export type ChecklistItem = { id: string; name: string; checked: boolean; image_url?: string | null };
export type PhotoChecklistDefault = { id: string; name: string; sort_order: number };
export const CHECKLIST_KEY = "_photo_checklist";

function makeChecklistFromDefaults(defs: PhotoChecklistDefault[]): ChecklistItem[] {
  return defs.map((d) => ({ id: (globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)), name: d.name, checked: false }));
}


/* ============================================================ Query client (safe defaults) */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: 0,
    },
    mutations: { retry: 0 },
  },
});

/* ============================================================ Helpers */

const PAGE_PATHS: Record<Exclude<Page, "home" | "collection" | "login" | "dashboard">, string> = {
  customers: "customers", tasks: "tasks", finance: "finance", backup: "backup",
  "photo-checklist": "photo-checklist", inventory: "inventory", taxonomy: "taxonomy",
  products: "products", reports: "reports", media: "media",
  timeline: "timeline", users: "users", piwigo: "piwigo",
  history: "history",
  gallery: "gallery",
  partners: "partners",
  "site-text": "site-text",
};

const PUBLIC_PAGES: Page[] = ["home", "collection"];
function isPublicPage(p: Page) { return PUBLIC_PAGES.includes(p); }

/** Đọc cả pathname + search → giá trị mode cho trang collection. */
function getCollectionMode(): string {
  if (typeof window === "undefined") return "all";
  const m = window.location.pathname.match(/^\/bo-suu-tap\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : "all";
}

function getPageFromLocation(): Page {
  if (typeof window === "undefined") return "home";
  const p = window.location.pathname.replace(/\/+$/, "") || "/";
  if (p === "/" || p === "") return "home";
  if (p.startsWith("/bo-suu-tap/")) return "collection";
  if (p === "/login") return "login";
  for (const [page, slug] of Object.entries(PAGE_PATHS)) {
    if (p === `/${slug}` || p === `/admin/${slug}`) return page as Page;
  }
  if (p === "/admin") return "dashboard";
  return "home";
}

function setPathForPage(page: Page) {
  const path =
    page === "home" ? "/"
    : page === "collection" ? "/bo-suu-tap/all"
    : page === "login" ? "/login"
    : page === "dashboard" ? "/admin"
    : `/admin/${PAGE_PATHS[page as Exclude<Page, "home" | "collection" | "login" | "dashboard">]}`;
  window.history.pushState({ page }, "", path);
}

async function fetchMe(): Promise<PhpUser | null> {
  try {
    const res = await api.get<{ user: PhpUser | null }>("/auth/me");
    return res.user ?? null;
  } catch {
    return null;
  }
}

export function formatMoney(value: number) {
  return new Intl.NumberFormat("vi-VN").format(Math.round(value || 0)) + "₫";
}

export function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

/* ============================================================ Chống trùng khách hàng
 * Chuẩn hoá định danh (SĐT / FB / IG) + parse 409 từ POST /customers.
 * Backend trả: { error: 'duplicate_customer', message, candidates: Customer[] }
 */
function normPhoneVN(s: string | null | undefined): string {
  if (!s) return "";
  let d = String(s).replace(/\D+/g, "");
  if (d.length > 9 && d.startsWith("84")) d = "0" + d.slice(2);
  return d;
}
function normHandle(s: string | null | undefined): string {
  if (!s) return "";
  let x = String(s).trim().toLowerCase();
  x = x.replace(/^https?:\/\/(www\.|m\.)?(facebook\.com|fb\.com|fb\.me|instagram\.com)\//, "");
  x = x.replace(/^[@/]+/, "");
  x = x.split(/[?#/]/)[0];
  return x;
}
export function extractConflictCandidates(err: unknown): Customer[] | null {
  if (err instanceof ApiError && err.status === 409) {
    const body = err.body as any;
    if (body && Array.isArray(body.candidates) && body.candidates.length > 0) return body.candidates as Customer[];
  }
  return null;
}
function describeMatchReasons(c: Customer): string {
  const r = c._match_reasons ?? [];
  if (!r.length) return "";
  return r.map((s) => {
    if (s.startsWith("phone:")) return "SĐT " + s.slice(6);
    if (s.startsWith("facebook:")) return "Facebook " + s.slice(9);
    if (s.startsWith("instagram:")) return "Instagram " + s.slice(10);
    return s;
  }).join(" · ");
}

/** Modal hỏi user khi backend báo 409: gộp vào khách cũ hay vẫn tạo mới. */
function CustomerConflictDialog({
  candidates,
  draftLabel,
  onMerge,
  onForceCreate,
  onCancel,
  busy,
}: {
  candidates: Customer[];
  draftLabel: string;
  onMerge: (target: Customer) => void;
  onForceCreate: () => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  return (
    <Modal title="Phát hiện khách trùng" onClose={onCancel}>
      <div className="space-y-3 text-sm">
        <p>
          Khách mới <b>{draftLabel}</b> có cùng định danh với{" "}
          {candidates.length === 1 ? "khách hiện có" : `${candidates.length} khách hiện có`}.
          Bạn muốn <b>gộp</b> hay <b>tạo mới riêng</b>?
        </p>
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {candidates.map((c) => (
            <div key={c.id} className="rounded border border-border bg-muted/30 p-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {describeMatchReasons(c)}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {(c.phones ?? [c.phone].filter(Boolean) as string[]).join(", ") || "—"}
                  </div>
                </div>
                <PrimaryBtn type="button" onClick={() => onMerge(c)} disabled={busy}>
                  Gộp vào khách này
                </PrimaryBtn>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-1 border-t border-border">
          <GhostBtn type="button" onClick={onCancel} disabled={busy}>Huỷ</GhostBtn>
          <GhostBtn type="button" onClick={onForceCreate} disabled={busy}>Vẫn tạo khách mới</GhostBtn>
        </div>
      </div>
    </Modal>
  );
}

/** Gộp draft vào khách hiện có: thêm phone/fb/ig/bank/tags mới vào mảng, giữ tên cũ.
 *  V4: union bank_transfer_names + tags; chỉ set real_name/vip_score nếu target chưa có. */
async function mergeDraftIntoCustomer(target: Customer, draft: {
  phone?: string | null; facebook?: string | null; instagram?: string | null;
  address?: string | null; note?: string | null;
  real_name?: string | null;
  bank_transfer_names?: string[] | null;
  tags?: string[] | null;
  vip_score?: number | null;
}): Promise<Customer> {
  const merge = (arr: string[] | undefined, primary: string | null | undefined, add: string | null | undefined) => {
    const out = [...(arr ?? [])];
    if (primary && !out.includes(primary)) out.unshift(primary);
    if (add && add.trim() && !out.includes(add.trim())) out.push(add.trim());
    return out;
  };
  const unionArr = (a: string[] | null | undefined, b: string[] | null | undefined) => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const v of [...(a ?? []), ...(b ?? [])]) {
      const s = (v ?? "").toString().trim();
      if (!s) continue;
      const k = s.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k); out.push(s);
    }
    return out;
  };
  const body: Record<string, unknown> = {
    phones:     merge(target.phones,     target.phone,     draft.phone),
    facebooks:  merge(target.facebooks,  target.facebook,  draft.facebook),
    instagrams: merge(target.instagrams, target.instagram, draft.instagram),
  };
  if (draft.address && !target.address) body.address = draft.address;
  if (draft.note) body.note = target.note ? `${target.note}\n— ${draft.note}` : draft.note;
  // V4 — real_name: chỉ set nếu target chưa có.
  if (draft.real_name && draft.real_name.trim() && !target.real_name) {
    body.real_name = draft.real_name.trim();
  }
  // V4 — bank_transfer_names: union array, không ghi đè.
  if (draft.bank_transfer_names && draft.bank_transfer_names.length > 0) {
    body.bank_transfer_names = unionArr(target.bank_transfer_names ?? [], draft.bank_transfer_names);
  }
  // V4 — tags: union array, không ghi đè.
  if (draft.tags && draft.tags.length > 0) {
    body.tags = unionArr(target.tags ?? [], draft.tags);
  }
  // V4 — vip_score: chỉ set nếu target chưa có hoặc =0.
  if (typeof draft.vip_score === "number" && draft.vip_score > 0 && !(Number(target.vip_score ?? 0) > 0)) {
    body.vip_score = draft.vip_score;
  }
  return await api.put<Customer>(`/customers/${encodeURIComponent(target.id)}`, body);
}

export function todayISO() {
  return todayVN();
}

/* ============================================================ Stream lock (Dòng chính / Dòng phụ)
 * Một state duy nhất cho cả app, persist vào localStorage.
 * Quy tắc: Order + Mô hình súng = "side"; còn lại = "main".
 */
export type Stream = "main" | "side";
const STREAM_KEY = "nana.currentStream";
function readStream(): Stream {
  if (typeof window === "undefined") return "main";
  const v = window.localStorage.getItem(STREAM_KEY);
  return v === "side" ? "side" : "main";
}
const streamListeners = new Set<(s: Stream) => void>();
function setCurrentStream(s: Stream) {
  try { window.localStorage.setItem(STREAM_KEY, s); } catch {}
  streamListeners.forEach((fn) => fn(s));
}
export function useCurrentStream(): [Stream, (s: Stream) => void] {
  const [s, setS] = useState<Stream>(() => readStream());
  useEffect(() => {
    const fn = (next: Stream) => setS(next);
    streamListeners.add(fn);
    return () => { streamListeners.delete(fn); };
  }, []);
  return [s, setCurrentStream];
}
export function streamLabel(s: Stream): string {
  return s === "main" ? "Dòng chính" : "Dòng phụ";
}
/** Suy stream của project theo key/project_group. Dùng làm fallback nếu BE chưa set revenue_stream. */
export function getProjectStream(p: Pick<Project, "key" | "project_group" | "revenue_stream"> | null | undefined): Stream {
  if (!p) return "main";
  if (p.revenue_stream === "side" || p.revenue_stream === "main") return p.revenue_stream;
  if (p.key === "order" || p.key === "rental-gun") return "side";
  if (p.project_group === "order") return "side";
  return "main";
}
/** Item kho thuộc dòng nào: mô hình súng = phụ, còn lại = chính. */
function getInventoryStream(category: string | InventoryCategory): Stream {
  return category === "mo-hinh-sung" ? "side" : "main";
}

/* ============================================================ Root */

export default function AppStatic() {
  const [page, setPage] = useState<Page>(() => getPageFromLocation());
  const [user, setUser] = useState<PhpUser | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchMe().then((u) => {
      if (!alive) return;
      setUser(u);
      setAuthChecked(true);
      const cur = getPageFromLocation();
      // Trang công khai (/, /bo-suu-tap/...) KHÔNG ép login.
      if (isPublicPage(cur)) return;
      if (!u && cur !== "login") {
        // Chưa login → quay về trang chủ công khai (không ép tới /login).
        window.history.replaceState({ page: "home" }, "", "/");
        setPage("home");
      }
      if (u && cur === "login") {
        window.history.replaceState({ page: "dashboard" }, "", "/admin");
        setPage("dashboard");
      }
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onPop = () => setPage(getPageFromLocation());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((next: Page) => {
    setPathForPage(next);
    setPage(next);
  }, []);

  const handleLogin = useCallback((u: PhpUser) => {
    setUser(u);
    queryClient.clear();
    window.history.replaceState({ page: "dashboard" }, "", "/admin");
    setPage("dashboard");
  }, []);

  const handleLogout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(null);
    queryClient.clear();
    // Logout → về trang chủ công khai.
    window.history.replaceState({ page: "home" }, "", "/");
    setPage("home");
  }, []);

  if (!authChecked) return <Shell><StatusText>Đang kiểm tra đăng nhập…</StatusText></Shell>;

  // === Trang công khai (không cần login) ===
  if (isPublicPage(page)) {
    return (
      <QueryClientProvider client={queryClient}>
        <EditModeProvider isAdmin={(user?.role ?? "") === "admin"}>
          {page === "home" ? (
            <PublicHomePage
              isAdmin={(user?.role ?? "") === "admin"}
              onGoAdmin={() => navigate("dashboard")}
              onGoLogin={() => navigate("login")}
            />
          ) : (
            <PublicCollectionPage mode={getCollectionMode()} />
          )}
        </EditModeProvider>
      </QueryClientProvider>
    );
  }

  if (!user || page === "login") {
    return (
      <QueryClientProvider client={queryClient}>
        <LoginPage onLogin={handleLogin} />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <EditModeProvider isAdmin={(user.role ?? "") === "admin"}>
      <Shell user={user} page={page} onNavigate={navigate} onLogout={handleLogout}>
        {page === "customers" ? <CustomersPage /> :
         page === "tasks" ? <TasksPage onNavigate={navigate} /> :
         page === "finance" ? <FinancePage /> :
         page === "backup" ? <BackupPage user={user} /> :
         page === "photo-checklist" ? <PhotoChecklistDefaultsPage /> :
         page === "inventory" ? <InventoryPage /> :
         page === "taxonomy" ? <TaxonomyPage /> :
         page === "reports" ? <ReportsPage /> :
         page === "timeline" ? <TimelinePage user={user} /> :
         page === "users" ? <UsersPage user={user} /> :
         page === "media" ? <MediaPage /> :
         page === "products" ? <ProductsPage /> :
         page === "piwigo" ? <PiwigoAdminPage user={user} /> :
         page === "history" ? <HistoryPage /> :
         page === "gallery" ? <AdminGalleryPage user={user} /> :
         page === "partners" ? <PartnersPage /> :
         page === "site-text" ? <SiteTextPage /> :
         <DashboardPage user={user} onNavigate={navigate} />}
      </Shell>
      </EditModeProvider>
      <ConfirmHost />
    </QueryClientProvider>
  );
}

/* ============================================================ Shell — sidebar layout */

type NavItem = { page: Page; label: string; icon: typeof Package };
const NAV: NavItem[] = [
  { page: "products",        label: "Sản phẩm",              icon: Package },
  { page: "gallery",         label: "Bộ sưu tập",            icon: LayoutGrid },
  { page: "tasks",           label: "Quản lý task",          icon: KanbanSquare },
  { page: "customers",       label: "Khách hàng (CRM)",      icon: UserCircle2 },
  { page: "partners",        label: "Đối tác & Đối thủ",     icon: Handshake },
  { page: "reports",         label: "Báo cáo",               icon: BarChart3 },
  { page: "finance",         label: "Tài chính",             icon: Wallet },
  { page: "inventory",       label: "Kho cho thuê",          icon: Boxes },
  { page: "media",           label: "Thư viện ảnh",          icon: ImageIcon },
  { page: "taxonomy",        label: "Danh mục & thuộc tính", icon: Tag },
  { page: "timeline",        label: "Timeline",              icon: Clock },
  { page: "photo-checklist", label: "Checklist phụ kiện",    icon: Camera },
  { page: "users",           label: "Người dùng",            icon: Users },
  { page: "site-text",       label: "Văn bản trang",         icon: Type },
  { page: "piwigo",          label: "Piwigo (ảnh)",          icon: Server },
  { page: "history",         label: "Lịch sử & hoàn tác",    icon: Clock },
  { page: "backup",          label: "Sao lưu",               icon: Download },
];

function NavList({ page, onNavigate }: { page?: Page; onNavigate: (p: Page) => void }) {
  return (
    <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
      {NAV.map((it) => {
        const active = page === it.page;
        const Icon = it.icon;
        return (
          <button
            key={it.page}
            type="button"
            onClick={() => onNavigate(it.page)}
            className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors ${
              active ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-accent hover:text-foreground"
            }`}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="truncate text-left">{it.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function Shell({ children, user, page, onNavigate, onLogout }: {
  children: ReactNode; user?: PhpUser | null; page?: Page;
  onNavigate?: (p: Page) => void; onLogout?: () => void;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { theme: adminTheme, setTheme: setAdminTheme } = useAdminTheme(user?.id);
  const adminThemeStyle = useMemo(() => buildAdminThemeStyle(adminTheme), [adminTheme]);

  // Unauthenticated layout for login/loading
  if (!user || !onNavigate || !onLogout) {
    return (
      <main className="min-h-screen bg-background px-4 py-6 text-foreground">
        <div className="mx-auto w-full max-w-6xl">{children}</div>
      </main>
    );
  }

  const navigate = (p: Page) => { onNavigate(p); setDrawerOpen(false); };

  const Brand = (
    <div className="flex items-center gap-2 border-b border-border/60 px-4 py-4">
      <div className="grid h-8 w-8 place-items-center rounded-md bg-primary/15 text-primary">
        <Sparkles className="h-4 w-4" />
      </div>
      <div>
        <div className="text-sm font-bold leading-tight">Nana Admin</div>
        <div className="text-[10px] text-muted-foreground">Bảng điều khiển</div>
      </div>
    </div>
  );

  const Footer = (
    <div className="space-y-1.5 border-t border-border/60 p-3">
      <button type="button" onClick={() => navigate("dashboard")}
        className="flex h-10 w-full items-center gap-2 rounded-md px-3 text-sm text-foreground/80 hover:bg-accent">
        <Home className="h-3.5 w-3.5" /> Dashboard
      </button>
      <button type="button" onClick={() => navigate("home")}
        className="flex h-10 w-full items-center gap-2 rounded-md px-3 text-sm text-foreground/80 hover:bg-accent">
        <ExternalLink className="h-3.5 w-3.5" /> Xem trang công khai
      </button>
      <button type="button" onClick={onLogout}
        className="flex h-10 w-full items-center gap-2 rounded-md px-3 text-sm text-destructive hover:bg-destructive/10">
        <LogOut className="h-3.5 w-3.5" /> Đăng xuất
      </button>
    </div>
  );

  const currentLabel =
    page === "dashboard" ? "Dashboard"
    : NAV.find((n) => n.page === page)?.label ?? "Trang";

  return (
    <div
      className="admin-shell flex min-h-screen bg-background text-foreground"
      data-admin-appearance={adminTheme.appearance}
      data-admin-preset={adminTheme.preset || "default"}
      style={adminThemeStyle}
    >
      <aside className="hidden w-60 shrink-0 flex-col border-r border-border/60 bg-card/40 md:flex">
        {Brand}
        <NavList page={page} onNavigate={navigate} />
        {Footer}
      </aside>

      {drawerOpen ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawerOpen(false)} />
          <aside className="relative flex h-full w-[82vw] max-w-[320px] flex-col border-r border-border/60 bg-card">
            {Brand}
            <NavList page={page} onNavigate={navigate} />
            {Footer}
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 border-b border-border/60 bg-background/85 backdrop-blur-xl">
          <div className="flex items-center justify-between gap-2 px-3 py-2.5 md:px-6 md:py-3">
            <div className="flex min-w-0 items-center gap-2">
              <button type="button" onClick={() => setDrawerOpen(true)} aria-label="Mở menu"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-md hover:bg-accent md:hidden">
                <Menu className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <h1 className="truncate text-sm font-bold tracking-tight md:text-lg">{currentLabel}</h1>
                <p className="hidden truncate text-[10px] text-muted-foreground md:block">{user.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <StreamSwitcher />
              <PushToggle isAdmin={(user.role ?? "") === "admin"} />
              <AdminAppearanceToggle theme={adminTheme} onChange={setAdminTheme} />
              <AdminThemeSwitcher theme={adminTheme} onChange={setAdminTheme} />
              <EditModeToggle />
            </div>
          </div>
          </div>
        </header>
        <main className="flex-1 px-3 py-4 md:px-6 md:py-6">{children}</main>
      </div>
      <BuildVersionBadge />
    </div>
  );
}

function StatusText({ children }: { children: ReactNode }) {
  return <p className="rounded-md border border-border bg-card p-4 text-sm text-muted-foreground">{children}</p>;
}

function BuildVersionBadge() {
  const v = (import.meta.env.VITE_BUILD_ID as string | undefined) ?? "dev";
  return (
    <div
      className="fixed bottom-2 right-2 z-50 select-text rounded-md border border-border bg-card/90 px-2 py-1 text-[10px] font-mono text-muted-foreground shadow-sm backdrop-blur"
      title="Build version (để kiểm tra bản deploy)"
    >
      build: {v}
    </div>
  );
}

/* ------------------ Stream switcher (2 nút, không có "Tất cả") ------------------ */
function StreamSwitcher() {
  const [stream, setStream] = useCurrentStream();
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-border" role="tablist" aria-label="Chọn dòng tiền">
      {([
        ["main", "Chính"],
        ["side", "Phụ"],
      ] as const).map(([v, lb]) => (
        <button
          key={v}
          type="button"
          role="tab"
          aria-selected={stream === v}
          onClick={() => setStream(v)}
          className={`h-8 px-3 text-xs font-medium transition-colors ${
            stream === v ? "bg-primary text-primary-foreground" : "bg-card text-foreground/70 hover:bg-muted"
          }`}
          title={v === "main" ? "Cosplay · Props · Photography · Lưu trữ" : "Order · Mô hình súng"}
        >Dòng {lb}</button>
      ))}
    </div>
  );
}



/**
 * DateInputVN — ô nhập ngày hiển thị dd/MM/yyyy đồng nhất mọi máy/locale.
 * - value (in/out): chuỗi ISO "YYYY-MM-DD" hoặc rỗng — giữ nguyên hợp đồng với <Input type="date">.
 * - Hiển thị: text "dd/MM/yyyy"; có nút icon mở native date picker để chọn nhanh.
 */

const ALBUM_STATUS_LABEL: Record<string, string> = {
  none: "Chưa có album",
  manual: "Link thủ công",
  created: "Album đã tạo",
  uploaded: "Đã upload ảnh",
  error: "Lỗi",
};
const ALBUM_STATUS_CLASS: Record<string, string> = {
  none: "bg-muted text-muted-foreground",
  manual: "bg-blue-500/15 text-blue-500",
  created: "bg-indigo-500/15 text-indigo-500",
  uploaded: "bg-green-500/15 text-green-600",
  error: "bg-red-500/15 text-red-500",
};
export function AlbumCell({ url, status }: { url?: string | null; status?: string | null }) {
  const key = status && ALBUM_STATUS_LABEL[status] ? status : (url ? "manual" : "none");
  const label = ALBUM_STATUS_LABEL[key];
  return (
    <div className="flex flex-col gap-1">
      <span className={`inline-flex w-fit rounded px-2 py-0.5 text-xs ${ALBUM_STATUS_CLASS[key]}`}>{label}</span>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className="text-xs text-primary underline">Mở album</a>
      ) : (
        <span className="text-xs text-muted-foreground">Chưa có link</span>
      )}
    </div>
  );
}
export function DangerBtn(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button {...props} className={`rounded-md border border-destructive/50 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 disabled:opacity-60 ${props.className ?? ""}`} />;
}

/* ============================================================ InlineConfirm
 * Nút xác nhận trực tiếp (không dùng window.confirm) — 10 giây tự huỷ.
 * Dùng cho mọi hành động cần xác nhận: xoá, ghi đè, ...
 */

/* Modal-based confirm cho các handler không dùng JSX-button (ví dụ trong onClick xa). */
type ConfirmReq = { id: number; message: string; resolve: (v: boolean) => void };
let confirmReqId = 0;
const confirmListeners = new Set<(reqs: ConfirmReq[]) => void>();
let confirmReqs: ConfirmReq[] = [];
function pushConfirm(req: ConfirmReq) {
  confirmReqs = [...confirmReqs, req];
  confirmListeners.forEach((fn) => fn(confirmReqs));
}
function dropConfirm(id: number) {
  confirmReqs = confirmReqs.filter((r) => r.id !== id);
  confirmListeners.forEach((fn) => fn(confirmReqs));
}
export function confirmAsync(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    pushConfirm({ id: ++confirmReqId, message, resolve });
  });
}
function ConfirmHost() {
  const [reqs, setReqs] = useState<ConfirmReq[]>(confirmReqs);
  useEffect(() => {
    const fn = (r: ConfirmReq[]) => setReqs(r);
    confirmListeners.add(fn);
    return () => { confirmListeners.delete(fn); };
  }, []);
  const req = reqs[0];
  const [left, setLeft] = useState(10);
  useEffect(() => {
    if (!req) return;
    setLeft(10);
    const i = window.setInterval(() => setLeft((n) => (n > 0 ? n - 1 : 0)), 1000);
    const t = window.setTimeout(() => { req.resolve(false); dropConfirm(req.id); }, 10000);
    return () => { window.clearInterval(i); window.clearTimeout(t); };
  }, [req]);
  if (!req) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-md border border-border bg-card p-4 shadow-xl">
        <div className="text-sm whitespace-pre-line">{req.message}</div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button"
            onClick={() => { req.resolve(false); dropConfirm(req.id); }}
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted">Huỷ</button>
          <button type="button"
            onClick={() => { req.resolve(true); dropConfirm(req.id); }}
            className="rounded-md bg-destructive px-3 py-1.5 text-sm text-destructive-foreground hover:opacity-90">
            Xác nhận ({left}s)
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================ Phase D — Pickers & Suggestions */

export const RENTAL_ITEMS_KEY = "__rental_items";

function taskInventoryIds(t: Task): string[] {
  if (Array.isArray(t.inventory_item_ids) && t.inventory_item_ids.length) return t.inventory_item_ids as string[];
  const legacy = (t.custom_values as any)?.[RENTAL_ITEMS_KEY];
  return Array.isArray(legacy) ? legacy as string[] : [];
}

function taskDateConflict(t: Task, tasks: Task[]): boolean {
  const ids = taskInventoryIds(t);
  if (!ids.length || (!t.start_date && !t.due_date)) return false;
  const a1 = t.start_date || "0000-01-01";
  const a2 = t.due_date || t.start_date || "9999-12-31";
  return tasks.some((o) => {
    if (o.id === t.id || o.status === "done" || o.status === "cancelled") return false;
    if (!taskInventoryIds(o).some((id) => ids.includes(id))) return false;
    const b1 = o.start_date || "0000-01-01";
    const b2 = o.due_date || o.start_date || "9999-12-31";
    return a1 <= b2 && b1 <= a2;
  });
}

function normVi(s: string) {
  return (s ?? "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/đ/g, "d");
}

/** History-based autocomplete input. Suggestions filtered by fuzzy/substring match. */
export function SuggestInput({
  value, onChange, suggestions, placeholder, type = "text", className, autoFocus, required,
}: {
  value: string;
  onChange: (v: string) => void;
  suggestions: string[];
  placeholder?: string;
  type?: string;
  className?: string;
  autoFocus?: boolean;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);

  const matches = useMemo(() => {
    const q = normVi(value);
    const uniq = Array.from(new Set(suggestions.filter(Boolean).map(String)));
    if (!q) return uniq.slice(0, 8);
    const scored = uniq
      .map((s) => {
        const n = normVi(s);
        if (n === q) return null;
        if (n.startsWith(q)) return { s, score: 0 };
        const idx = n.indexOf(q);
        if (idx >= 0) return { s, score: 1 + idx * 0.01 };
        return null;
      })
      .filter((x): x is { s: string; score: number } => !!x)
      .sort((a, b) => a.score - b.score)
      .map((x) => x.s);
    return scored.slice(0, 8);
  }, [value, suggestions]);

  useEffect(() => { setHi(0); }, [value]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={wrap} className="relative">
      <Input
        type={type}
        autoFocus={autoFocus}
        required={required}
        className={className}
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open || matches.length === 0) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, matches.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === "Enter") {
            if (matches[hi]) { e.preventDefault(); onChange(matches[hi]); setOpen(false); }
          } else if (e.key === "Escape") setOpen(false);
        }}
      />
      {open && matches.length > 0 ? (
        <div className="absolute z-50 left-0 right-0 mt-1 rounded-md border border-border bg-card shadow-lg max-h-[180px] overflow-y-auto">
          {matches.map((s, i) => (
            <button
              key={s + i}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(s); setOpen(false); }}
              onMouseEnter={() => setHi(i)}
              className={`w-full text-left px-2.5 py-1.5 text-sm truncate ${i === hi ? "bg-muted" : "hover:bg-muted/60"}`}
            >
              {s}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** localStorage-backed history for free-text fields. Up to 50 entries each. */
const FIELD_HISTORY_PREFIX = "nana.fieldHist:";
export function getFieldHistory(key: string): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(FIELD_HISTORY_PREFIX + key);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : [];
  } catch { return []; }
}
export function pushFieldHistory(key: string, value: string) {
  if (typeof window === "undefined") return;
  const v = String(value ?? "").trim();
  if (!v) return;
  try {
    const cur = getFieldHistory(key);
    const next = [v, ...cur.filter((x) => x !== v)].slice(0, 50);
    window.localStorage.setItem(FIELD_HISTORY_PREFIX + key, JSON.stringify(next));
  } catch { /* ignore */ }
}
function useFieldHistory(key: string): string[] {
  const [v, setV] = useState<string[]>(() => getFieldHistory(key));
  useEffect(() => { setV(getFieldHistory(key)); }, [key]);
  return v;
}

/** Searchable customer picker w/ inline "create new". */
export function CustomerSearchPicker({
  value, onChange,
}: {
  value: string | null;
  onChange: (id: string | null, customer: Customer | null) => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  // V4: thêm field optional real_name / bank_transfer_names (CSV) / tags (CSV) / vip_score.
  const [draft, setDraft] = useState({
    name: "", phone: "", address: "", facebook: "", instagram: "",
    real_name: "", bank_transfer_names: "", tags: "", vip_score: "",
  });
  const emptyDraft = {
    name: "", phone: "", address: "", facebook: "", instagram: "",
    real_name: "", bank_transfer_names: "", tags: "", vip_score: "",
  };
  const parseCsv = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);
  const wrap = useRef<HTMLDivElement>(null);
  const [conflict, setConflict] = useState<{ candidates: Customer[] } | null>(null);

  const list = useQuery({
    queryKey: ["customers", q],
    queryFn: () => api.get<Customer[]>("/customers", q ? { q } : undefined),
  });
  const selected = useQuery({
    queryKey: ["customer", value],
    enabled: !!value,
    queryFn: () => api.get<Customer>(`/customers/${value!}`),
  });

  const createMut = useMutation({
    mutationFn: (opts?: { force?: boolean }) => {
      const tagArr = parseCsv(draft.tags);
      const bankArr = parseCsv(draft.bank_transfer_names);
      const vip = Number(draft.vip_score);
      const body: Record<string, unknown> = {
        name: draft.name.trim(),
        phone: draft.phone.trim() || null,
        address: draft.address.trim() || null,
        facebook: draft.facebook.trim() || null,
        instagram: draft.instagram.trim() || null,
        // V4 — optional fields, không bắt nhập.
        real_name: draft.real_name.trim() || null,
        bank_transfer_names: bankArr,
        tags: tagArr,
        force_create: !!opts?.force,
      };
      if (!Number.isNaN(vip) && vip > 0) body.vip_score = vip;
      return api.post<Customer>("/customers", body);
    },
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      onChange(c.id, c);
      setCreating(false);
      setOpen(false);
      setQ("");
      setDraft(emptyDraft);
      setConflict(null);
    },
    onError: (err) => {
      const cands = extractConflictCandidates(err);
      if (cands) setConflict({ candidates: cands });
    },
  });

  const mergeMut = useMutation({
    mutationFn: (target: Customer) => {
      const vip = Number(draft.vip_score);
      return mergeDraftIntoCustomer(target, {
        phone: draft.phone, facebook: draft.facebook, instagram: draft.instagram, address: draft.address,
        real_name: draft.real_name || null,
        bank_transfer_names: parseCsv(draft.bank_transfer_names),
        tags: parseCsv(draft.tags),
        vip_score: !Number.isNaN(vip) && vip > 0 ? vip : null,
      });
    },
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      onChange(c.id, c);
      setCreating(false); setOpen(false); setQ(""); setConflict(null);
      setDraft(emptyDraft);
    },
  });

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) { setOpen(false); setCreating(false); }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const cur = selected.data;

  return (
    <div ref={wrap} className="relative space-y-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm text-left"
      >
        {cur ? (
          <span className="truncate">
            <span className="font-medium">{cur.name}</span>
            {cur.phone ? <span className="text-muted-foreground"> · {cur.phone}</span> : null}
            {cur.blacklist ? <span className="ml-2 rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold text-destructive">BL</span> : null}
          </span>
        ) : (
          <span className="text-muted-foreground">— Chọn khách hàng —</span>
        )}
        <span className="text-muted-foreground text-xs">▾</span>
      </button>

      {open ? (
        <div className="absolute z-50 left-0 right-0 mt-1 rounded-md border border-border bg-card shadow-lg max-h-[80vh] overflow-hidden flex flex-col">
          {creating ? (
            <div className="flex max-h-[80vh] flex-col">
              <div className="overflow-y-auto p-3 space-y-2">
              <div className="text-xs font-semibold">Khách hàng mới</div>
              <Input placeholder="Tên khách *" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} autoFocus />
              <Input placeholder="Số điện thoại" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
              <Input placeholder="Facebook (username hoặc URL)" value={draft.facebook} onChange={(e) => setDraft({ ...draft, facebook: e.target.value })} />
              <Input placeholder="Instagram (@user hoặc URL)" value={draft.instagram} onChange={(e) => setDraft({ ...draft, instagram: e.target.value })} />
              <Input placeholder="Địa chỉ" value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
              {/* V4 — optional: Tên thật / Tên CK / Tags / VIP. Không bắt nhập. */}
              <Input placeholder="Tên thật (optional)" value={draft.real_name} onChange={(e) => setDraft({ ...draft, real_name: e.target.value })} />
              <Input placeholder="Tên chuyển khoản (optional, CSV: A, B)" value={draft.bank_transfer_names} onChange={(e) => setDraft({ ...draft, bank_transfer_names: e.target.value })} />
              <Input placeholder="Tags (optional, CSV)" value={draft.tags} onChange={(e) => setDraft({ ...draft, tags: e.target.value })} />
              <div className="flex flex-wrap gap-1">
                {SUGGESTED_CUSTOMER_TAGS.map((t) => {
                  const cur = parseCsv(draft.tags);
                  const active = cur.some((x) => x.toLowerCase() === t.toLowerCase());
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        const next = active
                          ? cur.filter((x) => x.toLowerCase() !== t.toLowerCase())
                          : [...cur, t];
                        setDraft({ ...draft, tags: next.join(", ") });
                      }}
                      className={`text-[10px] px-1.5 py-0.5 rounded-full border transition-colors ${
                        active
                          ? "bg-primary/15 text-primary border-primary/30"
                          : "bg-muted text-muted-foreground border-border hover:bg-accent"
                      }`}
                    >
                      {active ? "✓ " : "+ "}{t}
                    </button>
                  );
                })}
              </div>
              <Input type="number" min={0} max={100} placeholder="VIP score (0–100, optional)" value={draft.vip_score} onChange={(e) => setDraft({ ...draft, vip_score: e.target.value })} />
              <div className="text-[10px] text-muted-foreground">
                ⓘ Nên có Facebook/Instagram nếu khách liên hệ qua MXH; không bắt buộc (có thể chỉ dùng SĐT/Zalo).
              </div>
              {createMut.error ? <ErrorBox message={getErrorMessage(createMut.error, "Lưu thất bại")} /> : null}
              </div>
              <div className="shrink-0 border-t border-border bg-card p-2 flex justify-end gap-2">
                <GhostBtn type="button" onClick={() => setCreating(false)}>Hủy</GhostBtn>
                <PrimaryBtn
                  type="button"
                  disabled={!draft.name.trim() || createMut.isPending}
                  onClick={() => createMut.mutate(undefined)}
                >
                  {createMut.isPending ? "…" : "Tạo"}
                </PrimaryBtn>
              </div>
            </div>
          ) : (
            <>
              <div className="p-2 border-b border-border">
                <Input autoFocus placeholder="Tìm tên / SĐT / địa chỉ…" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <div className="overflow-y-auto flex-1">
                {value ? (
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); onChange(null, null); setOpen(false); }}
                    className="w-full text-left px-3 py-1.5 text-xs italic text-muted-foreground hover:bg-muted"
                  >
                    Bỏ chọn khách hàng
                  </button>
                ) : null}
                {(list.data ?? []).length === 0 && !list.isFetching ? (
                  <div className="px-3 py-2 text-xs text-muted-foreground">Không có kết quả.</div>
                ) : null}
                {(list.data ?? []).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); onChange(c.id, c); setOpen(false); setQ(""); }}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-muted ${value === c.id ? "bg-muted/60" : ""}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium truncate flex-1">{c.name}</span>
                      {c.blacklist ? <span className="text-[10px] font-semibold text-destructive">⚠ BL</span> : null}
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {[c.phone, c.address].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => { setCreating(true); setDraft({ ...emptyDraft, name: q }); }}
                className="border-t border-border px-3 py-2 text-left text-xs hover:bg-muted"
              >
                + Tạo khách mới{q ? `: "${q}"` : ""}
              </button>
            </>
          )}
        </div>
      ) : null}

      {cur && !open ? (
        <div className="rounded border border-border/50 bg-muted/30 px-2 py-1.5 text-[11px] space-y-0.5">
          {/* V4 — VIP + tags + tên thật + tên CK (optional, ẩn nếu rỗng). */}
          {(() => {
            const vip = Number(cur.vip_score ?? 0);
            const tags = (cur.tags ?? []).filter(Boolean);
            const hasHead = vip > 0 || tags.length > 0;
            if (!hasHead) return null;
            return (
              <div className="flex flex-wrap items-center gap-1">
                {vip > 0 ? (
                  <span className={`text-[10px] px-1.5 py-0 rounded-full font-medium ${vip >= 80 ? "bg-amber-500/15 text-amber-600" : vip >= 50 ? "bg-blue-500/15 text-blue-600" : "bg-muted text-muted-foreground"}`}>VIP {vip}</span>
                ) : null}
                <CustomerTagPills tags={tags} max={5} />
              </div>
            );
          })()}
          {cur.real_name ? (
            <div className="italic text-muted-foreground/80">Tên thật: {cur.real_name}</div>
          ) : null}
          {(cur.bank_transfer_names ?? []).length > 0 ? (
            <div><BankNamePills names={cur.bank_transfer_names ?? null} max={4} /></div>
          ) : null}
          <CustomerContactIcons cust={cur} />
          {cur.phone ? (
            <div className="text-muted-foreground">
              📞 <a href={`tel:${cur.phone}`} className="hover:underline">{cur.phone}</a>
            </div>
          ) : null}
          {cur.address ? (
            <div className="text-muted-foreground">📍 {cur.address}</div>
          ) : null}
          {cur.blacklist ? <div className="text-destructive font-medium">⚠ Khách trong blacklist</div> : null}
        </div>
      ) : null}

      {conflict ? (
        <CustomerConflictDialog
          candidates={conflict.candidates}
          draftLabel={draft.name || draft.phone || draft.facebook || draft.instagram || "—"}
          onCancel={() => setConflict(null)}
          onForceCreate={() => createMut.mutate({ force: true })}
          onMerge={(t) => mergeMut.mutate(t)}
          busy={createMut.isPending || mergeMut.isPending}
        />
      ) : null}
    </div>
  );
}

/** Multi-select inventory picker, grouped by category + group_name. */
export function InventoryMultiPicker({
  value, onChange,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrap = useRef<HTMLDivElement>(null);
  const items = useQuery({ queryKey: ["inventory"], queryFn: () => api.get<InventoryItem[]>("/inventory") });

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const all = items.data ?? [];
  const selected = all.filter((i) => value.includes(i.id));
  const filtered = useMemo(() => {
    const qq = normVi(q);
    if (!qq) return all;
    return all.filter((i) => normVi(`${i.name} ${i.variant ?? ""}`).includes(qq));
  }, [all, q]);

  const groups = useMemo(() => {
    const m = new Map<string, { label: string; items: InventoryItem[] }>();
    for (const i of filtered) {
      const cat = INV_CATEGORY_LABEL[i.category];
      const grp = (i.group_name ?? "").trim() || "Chưa phân nhóm";
      const key = `${i.category}::${grp}`;
      if (!m.has(key)) m.set(key, { label: `${cat} · ${grp}`, items: [] });
      m.get(key)!.items.push(i);
    }
    return Array.from(m.values());
  }, [filtered]);

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  }

  return (
    <div ref={wrap} className="relative">
      <div className="flex flex-wrap items-center gap-1.5">
        {selected.length === 0 ? (
          <span className="text-xs italic text-muted-foreground">Chưa chọn món hàng nào</span>
        ) : selected.map((i) => (
          <span key={i.id} className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs">
            {i.name}{i.variant ? ` · ${i.variant}` : ""}
            <button type="button" onClick={() => toggle(i.id)} className="text-muted-foreground hover:text-destructive">×</button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="rounded border border-border bg-card px-2 py-0.5 text-xs hover:bg-muted"
        >
          + Chọn từ kho
        </button>
      </div>

      {open ? (
        <div className="absolute z-50 left-0 mt-1 w-[340px] rounded-md border border-border bg-card shadow-lg max-h-[320px] overflow-hidden flex flex-col">
          <div className="p-2 border-b border-border">
            <Input autoFocus placeholder="Tìm món hàng…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="overflow-y-auto flex-1 p-1">
            {all.length === 0 ? (
              <div className="px-3 py-2 text-xs italic text-muted-foreground">Kho rỗng. Vào trang Kho hàng để thêm.</div>
            ) : groups.length === 0 ? (
              <div className="px-3 py-2 text-xs italic text-muted-foreground">Không khớp.</div>
            ) : groups.map((g) => (
              <div key={g.label} className="mb-1">
                <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/40 rounded">
                  {g.label}
                </div>
                {g.items.map((i) => (
                  <label key={i.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                    <input type="checkbox" checked={value.includes(i.id)} onChange={() => toggle(i.id)} />
                    <span className="flex-1 truncate">
                      {i.name}{i.variant ? ` · ${i.variant}` : ""}
                      {!i.available ? <span className="ml-1 text-[10px] text-orange-500">(đang cho thuê)</span> : null}
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ============================================================ ProductMultiPicker
 * K9 Phase 2: chọn nhiều sản phẩm (catalog) gắn vào task — để biết task đang
 * làm/giao những item nào trong catalog "Đặt làm / Cho thuê / Order".
 */
export function ProductMultiPicker({ value, onChange }: {
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrap = useRef<HTMLDivElement>(null);
  const products = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await api.get<any[]>("/products")).map(normalizeProductRow),
  });

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const all = products.data ?? [];
  const selected = all.filter((p) => value.includes(p.id));
  const filtered = useMemo(() => {
    const qq = normVi(q);
    if (!qq) return all;
    return all.filter((p) => normVi(`${p.name} ${p.fandom ?? ""} ${p.category ?? ""}`).includes(qq));
  }, [all, q]);
  const groups = useMemo(() => {
    const m = new Map<string, ProductRow[]>();
    for (const p of filtered) {
      const k = `${PRODUCT_MODE_FALLBACK[p.mode] ?? p.mode} · ${p.category ?? "Khác"}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(p);
    }
    return Array.from(m.entries());
  }, [filtered]);

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  }

  return (
    <div ref={wrap} className="relative">
      <div className="flex flex-wrap items-center gap-1.5">
        {selected.length === 0 ? (
          <span className="text-xs italic text-muted-foreground">Chưa gắn sản phẩm catalog nào</span>
        ) : selected.map((p) => (
          <span key={p.id} className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs">
            {p.name}
            <button type="button" onClick={() => toggle(p.id)} className="text-muted-foreground hover:text-destructive">×</button>
          </span>
        ))}
        <button type="button" onClick={() => setOpen((o) => !o)}
          className="rounded border border-border bg-card px-2 py-0.5 text-xs hover:bg-muted">
          + Chọn từ catalog
        </button>
      </div>

      {open ? (
        <div className="absolute z-50 left-0 mt-1 w-[340px] rounded-md border border-border bg-card shadow-lg max-h-[320px] overflow-hidden flex flex-col">
          <div className="p-2 border-b border-border">
            <Input autoFocus placeholder="Tìm sản phẩm…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="overflow-y-auto flex-1 p-1">
            {all.length === 0 ? (
              <div className="px-3 py-2 text-xs italic text-muted-foreground">Catalog rỗng. Vào trang Sản phẩm để thêm.</div>
            ) : groups.length === 0 ? (
              <div className="px-3 py-2 text-xs italic text-muted-foreground">Không khớp.</div>
            ) : groups.map(([label, items]) => (
              <div key={label} className="mb-1">
                <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/40 rounded">
                  {label}
                </div>
                {items.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                    <input type="checkbox" checked={value.includes(p.id)} onChange={() => toggle(p.id)} />
                    {(p.cover_image || p.images[0]) && (
                      <img src={p.cover_image ?? p.images[0]} alt="" loading="lazy" className="h-6 w-6 rounded object-cover" />
                    )}
                    <span className="flex-1 truncate">
                      {p.name}
                      {p.fandom ? <span className="ml-1 text-[10px] text-muted-foreground">· {p.fandom}</span> : null}
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}


/* ============================================================ Login */

function LoginPage({ onLogin }: { onLogin: (u: PhpUser) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (loading) return;
    setLoading(true); setError(null);
    try {
      const res = await api.post<{ user: PhpUser }>("/auth/login", { email, password });
      onLogin(res.user);
    } catch (err) {
      setError(getErrorMessage(err, "Sai email hoặc mật khẩu"));
    } finally { setLoading(false); }
  }

  return (
    <Shell>
      <section className="mx-auto mt-16 w-full max-w-sm rounded-md border border-border bg-card p-6">
        <h1 className="text-xl font-bold">Đăng nhập</h1>
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <label className="block text-sm font-medium">Email
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required className="mt-1" />
          </label>
          <label className="block text-sm font-medium">Mật khẩu
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required className="mt-1" />
          </label>
          {error ? <ErrorBox message={error} /> : null}
          <PrimaryBtn type="submit" disabled={loading} className="w-full">{loading ? "Đang đăng nhập…" : "Đăng nhập"}</PrimaryBtn>
        </form>
      </section>
    </Shell>
  );
}

/* ============================================================ Dashboard */

function DashboardPage({ user, onNavigate }: { user: PhpUser; onNavigate: (p: Page) => void }) {
  const customers = useQuery({ queryKey: ["customers"], queryFn: () => api.get<Customer[]>("/customers") });
  const projects = useQuery({ queryKey: ["task_projects"], queryFn: () => api.get<Project[]>("/tasks/projects") });
  const firstPid = projects.data?.[0]?.id;
  const tasks = useQuery({
    queryKey: ["tasks", firstPid ?? "none"],
    enabled: !!firstPid,
    queryFn: () => api.get<Task[]>("/tasks", { project_id: firstPid! }),
  });
  const range = useMemo(() => {
    const now = nowVNDate();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    return { from: `${y}-${m}-01`, to: todayVN() };
  }, []);
  const [dashStream] = useCurrentStream();
  const finance = useQuery({
    queryKey: ["finance", range.from, range.to, dashStream],
    queryFn: () => api.get<FinanceEntry[]>("/finance", { ...range, stream: dashStream }),
  });

  const totals = useMemo(() => {
    return (finance.data ?? []).filter((r) => (r.revenue_stream ?? "side") === dashStream).reduce((acc, r) => {
      if (r.kind === "income") acc.income += Number(r.amount || 0);
      else acc.expense += Number(r.amount || 0);
      return acc;
    }, { income: 0, expense: 0 });
  }, [finance.data, dashStream]);

  return (
    <div className="relative space-y-6">
      <StickerCanvas pageKey="dashboard" />
      <section className="grid gap-4 md:grid-cols-3">
        <Stat title="Khách hàng" value={customers.isFetching ? "…" : String(customers.data?.length ?? 0)} onClick={() => onNavigate("customers")} />
        <Stat title={`Công việc${projects.data?.[0]?.name ? ` · ${projects.data[0].name}` : ""}`} value={tasks.isFetching || projects.isFetching ? "…" : String(tasks.data?.length ?? 0)} onClick={() => onNavigate("tasks")} />
        <Stat title="Số dư tháng này" value={finance.isFetching ? "…" : formatMoney(totals.income - totals.expense)} onClick={() => onNavigate("finance")} />
      </section>
      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-md border border-border bg-card p-4">
          <h3 className="font-semibold">Thông tin phiên</h3>
          <p className="mt-2 text-sm text-muted-foreground">Email: {user.email}</p>
          <p className="text-sm text-muted-foreground">Vai trò: {user.role || "admin"}</p>
          <p className="text-sm text-muted-foreground">Chế độ: Safe self-host (PHP API)</p>
        </div>
        <div className="rounded-md border border-border bg-card p-4">
          <h3 className="font-semibold">Thu/Chi tháng này</h3>
          <p className="text-[11px] text-muted-foreground">Đang xem: <b>{streamLabel(dashStream)}</b></p>
          <p className="mt-2 text-sm">Thu: <span className="font-medium text-emerald-600">{formatMoney(totals.income)}</span></p>
          <p className="text-sm">Chi: <span className="font-medium text-destructive">{formatMoney(totals.expense)}</span></p>
        </div>
      </section>
    </div>
  );
}

function Stat({ title, value, onClick }: { title: string; value: string; onClick?: () => void }) {
  return (
    <button type="button" onClick={onClick} className="rounded-md border border-border bg-card p-4 text-left hover:bg-muted">
      <p className="text-sm text-muted-foreground">{title}</p>
      <p className="mt-2 break-words text-2xl font-semibold">{value}</p>
    </button>
  );
}

/* ============================================================ Customers */

/** V3/V4 — gợi ý tag chuẩn cho xưởng nhỏ. Optional: user vẫn có thể nhập tag tự do.
 *  V4: bổ sung "khách cũ" theo yêu cầu audit. */
export const SUGGESTED_CUSTOMER_TAGS = [
  "khách mới",
  "khách cũ",
  "khách quen",
  "cọc cao",
  "khách gấp",
  "ưu tiên",
  "vip",
] as const;

/** V3 — chip nhỏ hiển thị tag khách, dùng style sẵn có (bg-muted text-muted-foreground). */
export function CustomerTagPills({ tags, max = 3 }: { tags?: string[] | null; max?: number }) {
  const list = (tags ?? []).filter(Boolean);
  if (list.length === 0) return null;
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {shown.map((t) => (
        <span key={t} className="text-[10px] px-1.5 py-0 rounded-full bg-muted text-muted-foreground">{t}</span>
      ))}
      {extra > 0 ? <span className="text-[10px] text-muted-foreground/70">+{extra}</span> : null}
    </span>
  );
}

/** V3 — pill nhỏ hiển thị tên chuyển khoản, giữ style mỏng nhẹ. */
export function BankNamePills({ names, max = 2 }: { names?: string[] | null; max?: number }) {
  const list = (names ?? []).map((s) => (s ?? "").trim()).filter(Boolean);
  if (list.length === 0) return null;
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {shown.map((t) => (
        <span key={t} className="text-[10px] px-1.5 py-0 rounded bg-amber-500/10 text-amber-600 border border-amber-500/20" title={`Tên CK: ${t}`}>
          ₫ {t}
        </span>
      ))}
      {extra > 0 ? <span className="text-[10px] text-muted-foreground/70">+{extra}</span> : null}
    </span>
  );
}

function CustomersPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Customer | "new" | null>(null);
  const [viewing, setViewing] = useState<Customer | null>(null);
  const [showDupes, setShowDupes] = useState(false);
  const list = useQuery({
    queryKey: ["customers", search],
    queryFn: () => api.get<Customer[]>("/customers", search ? { q: search } : undefined),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.del(`/customers/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Tìm theo tên / SĐT / địa chỉ…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" />
        <PrimaryBtn onClick={() => setEditing("new")}>+ Thêm khách</PrimaryBtn>
        <GhostBtn onClick={() => setShowDupes(true)}>🔍 Tìm khách trùng</GhostBtn>
      </div>
      {list.error ? <ErrorBox message={getErrorMessage(list.error, "Không tải được khách hàng")} /> : null}
      <div className="rounded-md border border-border bg-card">
        <SectionHeader title={`Khách hàng (${list.data?.length ?? 0})`} loading={list.isFetching} />
        {(list.data?.length ?? 0) === 0 && !list.isFetching ? (
          <p className="p-4 text-sm text-muted-foreground">Chưa có khách hàng.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Tên</th>
                  <th className="px-3 py-2 font-medium">VIP</th>
                  <th className="px-3 py-2 font-medium">SĐT</th>
                  <th className="px-3 py-2 font-medium">Địa chỉ</th>
                  <th className="px-3 py-2 font-medium">Ghi chú</th>
                  <th className="px-3 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {(list.data ?? []).map((c) => (
                  <tr key={c.id} className="border-t border-border">
                    <td className="px-3 py-2 align-top">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{c.name}</span>
                        {c.blacklist ? <span className="rounded bg-destructive/10 px-1.5 py-0.5 text-xs text-destructive">BL</span> : null}
                        <CustomerTagPills tags={c.tags ?? null} />
                      </div>
                      {c.real_name ? (
                        <div className="text-[11px] italic text-muted-foreground/80">Tên thật: {c.real_name}</div>
                      ) : null}
                      {(c.bank_transfer_names ?? []).length > 0 ? (
                        <div className="mt-0.5"><BankNamePills names={c.bank_transfer_names ?? null} max={3} /></div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 align-top"><VipBadge score={c.vip_score ?? 0} /></td>
                    <td className="px-3 py-2 align-top">{c.phone || "—"}</td>
                    <td className="px-3 py-2 align-top">{c.address || "—"}</td>
                    <td className="px-3 py-2 align-top">{c.note || "—"}</td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-wrap gap-2">
                        <GhostBtn onClick={() => setViewing(c)}>Chi tiết</GhostBtn>
                        <GhostBtn onClick={() => setEditing(c)}>Sửa</GhostBtn>
                        <ConfirmBtn message={`Xóa khách "${c.name}"?`} onConfirm={() => del.mutate(c.id)}>Xóa</ConfirmBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing ? (
        <CustomerForm
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["customers"] }); }}
        />
      ) : null}
      {viewing ? (
        <CustomerDetailModal customer={viewing} onClose={() => setViewing(null)} />
      ) : null}
      {showDupes ? (
        <DuplicateCustomersModal onClose={() => setShowDupes(false)} onMerged={() => qc.invalidateQueries({ queryKey: ["customers"] })} />
      ) : null}
    </section>
  );
}

/* ============================================================ Tìm & gộp khách trùng */
function DuplicateCustomersModal({ onClose, onMerged }: { onClose: () => void; onMerged: () => void }) {
  const qc = useQueryClient();
  const dupes = useQuery({
    queryKey: ["customers", "duplicates"],
    queryFn: () => api.get<{ groups: Array<{ customers: Customer[] }> }>("/customers/duplicates"),
  });
  const [primaryByGroup, setPrimaryByGroup] = useState<Record<number, string>>({});
  const mergeMut = useMutation({
    mutationFn: ({ primaryId, fromIds }: { primaryId: string; fromIds: string[] }) =>
      api.post(`/customers/${encodeURIComponent(primaryId)}/merge`, { from_ids: fromIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["customers", "duplicates"] });
      onMerged();
    },
  });

  return (
    <Modal title="Khách hàng trùng định danh" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Các nhóm dưới đây có chung SĐT / Facebook / Instagram. Chọn 1 khách <b>chính</b> trong nhóm rồi bấm <b>Gộp</b> — toàn bộ task, tài chính và mọi định danh sẽ dồn về khách chính.
        </p>
        {dupes.isFetching ? <p className="text-sm">Đang quét…</p> : null}
        {dupes.data && dupes.data.groups.length === 0 ? (
          <p className="text-sm text-emerald-600">✓ Không phát hiện khách trùng.</p>
        ) : null}
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {(dupes.data?.groups ?? []).map((g, gi) => {
            const chosen = primaryByGroup[gi] ?? g.customers[0]?.id;
            const fromIds = g.customers.filter((c) => c.id !== chosen).map((c) => c.id);
            return (
              <div key={gi} className="rounded border border-border p-2 space-y-1.5">
                {g.customers.map((c) => (
                  <label key={c.id} className="flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name={`grp-${gi}`}
                      checked={chosen === c.id}
                      onChange={() => setPrimaryByGroup({ ...primaryByGroup, [gi]: c.id })}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{c.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        SĐT: {(c.phones ?? []).join(", ") || c.phone || "—"}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        FB: {(c.facebooks ?? []).join(", ") || c.facebook || "—"} · IG: {(c.instagrams ?? []).join(", ") || c.instagram || "—"}
                      </div>
                    </div>
                  </label>
                ))}
                <div className="flex justify-end">
                  <PrimaryBtn
                    type="button"
                    disabled={!chosen || fromIds.length === 0 || mergeMut.isPending}
                    onClick={() => mergeMut.mutate({ primaryId: chosen!, fromIds })}
                  >
                    Gộp {fromIds.length} khách vào khách chính
                  </PrimaryBtn>
                </div>
              </div>
            );
          })}
        </div>
        {mergeMut.error ? <ErrorBox message={getErrorMessage(mergeMut.error, "Gộp thất bại")} /> : null}
        <div className="flex justify-end">
          <GhostBtn type="button" onClick={onClose}>Đóng</GhostBtn>
        </div>
      </div>
    </Modal>
  );
}

function VipBadge({ score }: { score: number }) {
  const s = Number(score) || 0;
  if (s <= 0) return <span className="text-xs text-muted-foreground">—</span>;
  const cls = s >= 80 ? "bg-amber-500/15 text-amber-600" : s >= 50 ? "bg-blue-500/15 text-blue-600" : "bg-muted text-muted-foreground";
  return <span className={`inline-flex w-fit rounded px-2 py-0.5 text-xs font-medium ${cls}`}>VIP {s}</span>;
}


function CustomerForm({ initial, onClose, onSaved }: { initial: Customer | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(initial?.name ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [address, setAddress] = useState(initial?.address ?? "");
  const [facebook, setFacebook] = useState(initial?.facebook ?? "");
  const [instagram, setInstagram] = useState(initial?.instagram ?? "");
  const [tags, setTags] = useState((initial?.tags ?? []).join(", "));
  // V3 sync — tên thật & danh sách tên chuyển khoản (cả hai optional).
  const [realName, setRealName] = useState(initial?.real_name ?? "");
  const [bankNames, setBankNames] = useState((initial?.bank_transfer_names ?? []).join(", "));
  const [note, setNote] = useState(initial?.note ?? "");
  const [blacklist, setBlacklist] = useState(!!initial?.blacklist);
  const [vipScore, setVipScore] = useState<string>(initial?.vip_score != null ? String(initial.vip_score) : "0");
  const [region, setRegion] = useState<"hcm" | "tinh">(initial?.region === "tinh" ? "tinh" : "hcm");
  const [err, setErr] = useState<string | null>(null);
  const qcForm = useQueryClient();
  const [conflict, setConflict] = useState<{ candidates: Customer[] } | null>(null);

  const save = useMutation({
    mutationFn: async (opts?: { force?: boolean }) => {
      const tagArr = tags.split(",").map((t) => t.trim()).filter(Boolean);
      const bankArr = bankNames.split(",").map((t) => t.trim()).filter(Boolean);
      const body: Record<string, unknown> = {
        name,
        phone: phone || null,
        address: address || null,
        facebook: facebook || null,
        instagram: instagram || null,
        tags: tagArr,
        note: note || null,
        blacklist,
        vip_score: Number(vipScore) || 0,
        region,
        real_name: realName.trim() || null,
        bank_transfer_names: bankArr,
      };
      if (initial) return api.put(`/customers/${initial.id}`, body);
      if (opts?.force) body.force_create = true;
      return api.post("/customers", body);
    },
    onSuccess: onSaved,
    onError: (e) => {
      const cands = extractConflictCandidates(e);
      if (cands && !initial) { setConflict({ candidates: cands }); return; }
      setErr(getErrorMessage(e, "Lưu thất bại"));
    },
  });

  const merge = useMutation({
    mutationFn: (target: Customer) => mergeDraftIntoCustomer(target, { phone, facebook, instagram, address, note }),
    onSuccess: () => { qcForm.invalidateQueries({ queryKey: ["customers"] }); setConflict(null); onSaved(); },
    onError: (e) => setErr(getErrorMessage(e, "Gộp thất bại")),
  });


  return (
    <Modal title={initial ? "Sửa khách hàng" : "Thêm khách hàng"} onClose={onClose}>
      <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); if (!name.trim()) { setErr("Tên là bắt buộc"); return; } setErr(null); save.mutate(undefined); }}>
        <label className="block text-sm">Tên *<Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1" /></label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">SĐT<Input value={phone ?? ""} onChange={(e) => setPhone(e.target.value)} className="mt-1" /></label>
          <label className="block text-sm">Địa chỉ<Input value={address ?? ""} onChange={(e) => setAddress(e.target.value)} className="mt-1" /></label>
          <label className="block text-sm">Facebook<Input value={facebook ?? ""} onChange={(e) => setFacebook(e.target.value)} className="mt-1" /></label>
          <label className="block text-sm">Instagram<Input value={instagram ?? ""} onChange={(e) => setInstagram(e.target.value)} className="mt-1" /></label>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">Vùng (tính ngày giữ đồ cho thuê)
            <Select value={region} onChange={(e) => setRegion(e.target.value as "hcm" | "tinh")} className="mt-1">
              <option value="hcm">TP.HCM — giữ 3 ngày</option>
              <option value="tinh">Tỉnh — giữ 7 ngày (gồm vận chuyển)</option>
            </Select>
          </label>
        </div>
        <label className="block text-sm">
          Tên thật (optional)
          <Input value={realName ?? ""} onChange={(e) => setRealName(e.target.value)} placeholder="vd: Nguyễn Văn A" className="mt-1" />
        </label>
        <label className="block text-sm">
          Tên chuyển khoản (optional, phân cách dấu phẩy)
          <Input
            value={bankNames}
            onChange={(e) => setBankNames(e.target.value)}
            placeholder="vd: NGUYEN VAN A, NV ANH"
            className="mt-1"
          />
          <span className="mt-0.5 block text-[11px] text-muted-foreground/80">
            Để match khách khi sao kê ngân hàng hiện tên khác với nickname.
          </span>
        </label>
        <div className="block text-sm">
          Tags (phân cách dấu phẩy)
          <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="vd: VIP, khách quen" className="mt-1" />
          <div className="mt-1 flex flex-wrap gap-1">
            {SUGGESTED_CUSTOMER_TAGS.map((t) => {
              const cur = tags.split(",").map((s) => s.trim()).filter(Boolean);
              const active = cur.some((x) => x.toLowerCase() === t.toLowerCase());
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    const next = active
                      ? cur.filter((x) => x.toLowerCase() !== t.toLowerCase())
                      : [...cur, t];
                    setTags(next.join(", "));
                  }}
                  className={`text-[10px] px-1.5 py-0.5 rounded-full border transition-colors ${
                    active
                      ? "bg-primary/15 text-primary border-primary/30"
                      : "bg-muted text-muted-foreground border-border hover:bg-accent"
                  }`}
                >
                  {active ? "✓ " : "+ "}{t}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">VIP score (0–100)<Input type="number" min={0} max={100} value={vipScore} onChange={(e) => setVipScore(e.target.value)} className="mt-1" /></label>
          <label className="flex items-end gap-2 pb-1 text-sm"><input type="checkbox" checked={blacklist} onChange={(e) => setBlacklist(e.target.checked)} /> Blacklist</label>
        </div>
        <label className="block text-sm">Ghi chú<Textarea rows={3} value={note ?? ""} onChange={(e) => setNote(e.target.value)} className="mt-1" /></label>
        {err ? <ErrorBox message={err} /> : null}
        <div className="flex justify-end gap-2">
          <GhostBtn type="button" onClick={onClose}>Hủy</GhostBtn>
          <PrimaryBtn type="submit" disabled={save.isPending}>{save.isPending ? "Đang lưu…" : "Lưu"}</PrimaryBtn>
        </div>
      </form>
      {conflict ? (
        <CustomerConflictDialog
          candidates={conflict.candidates}
          draftLabel={name || phone || facebook || instagram || "—"}
          onCancel={() => setConflict(null)}
          onForceCreate={() => save.mutate({ force: true })}
          onMerge={(t) => merge.mutate(t)}
          busy={save.isPending || merge.isPending}
        />
      ) : null}
    </Modal>
  );
}

/* Tasks UI moved to src/features/tasks/TasksPage.tsx */

/* ============================================================ Finance — helpers */

export const FIN_CATEGORY_LABEL: Record<string, string> = {
  "lam-do": "Làm đồ",
  "thue-cosplay": "Cho thuê cosplay",
  "order": "Order đặt hộ",
  "vat-lieu": "Vật liệu",
  "ship": "Vận chuyển",
  "marketing": "Marketing",
};
const FIN_CATEGORY_OPTIONS = Object.entries(FIN_CATEGORY_LABEL).map(([value, label]) => ({ value, label }));

export type FinancePeriod = "today" | "week" | "month" | "quarter" | "year" | "all";
export function finComputeRange(p: FinancePeriod): { from: string; to: string } {
  const today = nowVNDate();
  const to = todayVN();
  const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
  const pad = (n: number) => String(n).padStart(2, "0");
  const iso = (yy: number, mm: number, dd: number) => `${yy}-${pad(mm + 1)}-${pad(dd)}`;
  let from: string;
  switch (p) {
    case "today": from = iso(y, m, d); break;
    case "week": { const dow = (today.getDay() + 6) % 7; from = addDaysISO(iso(y, m, d), -dow); break; }
    case "month": from = iso(y, m, 1); break;
    case "quarter": from = iso(y, Math.floor(m / 3) * 3, 1); break;
    case "year": from = iso(y, 0, 1); break;
    case "all": from = "2000-01-01"; break;
  }
  return { from, to };
}

/** Parse "1.500.000" / "1,500,000" / "1500000" / "1.5tr" / "500k" → number */
export function parseAmount(s: string): number {
  if (!s) return 0;
  let t = s.toString().trim().toLowerCase().replace(/[\s₫đ]/g, "");
  let mul = 1;
  const sm = t.match(/(k|tr|m|b)$/);
  if (sm) {
    mul = sm[1] === "k" ? 1_000 : sm[1] === "b" ? 1_000_000_000 : 1_000_000;
    t = t.slice(0, -sm[1].length);
  }
  // Một dấu . hoặc , với 1-2 chữ số phía sau ⇒ phần thập phân (vd: 1.5tr, 2,75tr)
  // Nhiều dấu hoặc >2 chữ số phía sau ⇒ dấu phân tách hàng nghìn (vd: 1.500.000)
  const dec = t.match(/^(\d+)[.,](\d{1,2})$/);
  const n = dec ? Number(dec[1] + "." + dec[2]) : Number(t.replace(/[.,]/g, "")) || 0;
  return n * mul;
}

/* Finance CSV export/import đã chuyển sang src/shared/csv/csvExport.ts — re-export ở đầu file. */

export const FIN_PRESETS: { value: FinancePeriod; label: string }[] = [
  { value: "today", label: "Hôm nay" },
  { value: "week", label: "Tuần" },
  { value: "month", label: "Tháng" },
  { value: "quarter", label: "Quý" },
  { value: "year", label: "Năm" },
];

/* ------------------ P/L (Profit & Loss) helpers ------------------ */

type PnLMonthRow = {
  month: string;       // YYYY-MM
  income: number;
  expense: number;
  profit: number;
  byCatIncome: Record<string, number>;
  byCatExpense: Record<string, number>;
  byStreamIncome: Record<string, number>;
  byStreamExpense: Record<string, number>;
};

export function buildMonthlyPnL(rows: FinanceEntry[], from: string, to: string): PnLMonthRow[] {
  // Seed months between from..to so empty months also show up
  const months = new Map<string, PnLMonthRow>();
  const start = new Date(from + "T00:00:00");
  const end = new Date(to + "T00:00:00");
  if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const stop = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cur <= stop) {
      const key = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}`;
      months.set(key, {
        month: key, income: 0, expense: 0, profit: 0,
        byCatIncome: {}, byCatExpense: {},
        byStreamIncome: {}, byStreamExpense: {},
      });
      cur.setMonth(cur.getMonth() + 1);
    }
  }
  for (const r of rows) {
    const date = r.occurred_on || "";
    const key = date.slice(0, 7);
    if (!key) continue;
    if (!months.has(key)) {
      months.set(key, {
        month: key, income: 0, expense: 0, profit: 0,
        byCatIncome: {}, byCatExpense: {},
        byStreamIncome: {}, byStreamExpense: {},
      });
    }
    const m = months.get(key)!;
    const a = Number(r.amount || 0);
    const cat = r.category || "khac";
    const stream = r.revenue_stream === "side" ? "side" : "main";
    if (r.kind === "income") {
      m.income += a;
      m.byCatIncome[cat] = (m.byCatIncome[cat] || 0) + a;
      m.byStreamIncome[stream] = (m.byStreamIncome[stream] || 0) + a;
    } else {
      m.expense += a;
      m.byCatExpense[cat] = (m.byCatExpense[cat] || 0) + a;
      m.byStreamExpense[stream] = (m.byStreamExpense[stream] || 0) + a;
    }
    m.profit = m.income - m.expense;
  }
  return Array.from(months.values()).sort((a, b) => a.month.localeCompare(b.month));
}

export function exportPnLXlsx(rows: FinanceEntry[], from: string, to: string, suffix: string = "") {
  const months = buildMonthlyPnL(rows, from, to);
  const cats = Array.from(new Set([
    ...Object.keys(FIN_CATEGORY_LABEL),
    ...rows.map((r) => r.category || "khac"),
  ]));
  const catLabel = (c: string) => FIN_CATEGORY_LABEL[c] ?? c;

  // Sheet 1: Tổng quan P/L theo tháng
  const overview: (string | number)[][] = [
    ["Tháng", "Doanh thu", "Chi phí", "Lợi nhuận", "Biên LN %", "Thu dòng chính", "Thu dòng phụ", "Chi dòng chính", "Chi dòng phụ"],
  ];
  let totIn = 0, totEx = 0;
  for (const m of months) {
    totIn += m.income; totEx += m.expense;
    overview.push([
      m.month, m.income, m.expense, m.profit,
      m.income > 0 ? Number((m.profit / m.income * 100).toFixed(2)) : 0,
      m.byStreamIncome.main || 0, m.byStreamIncome.side || 0,
      m.byStreamExpense.main || 0, m.byStreamExpense.side || 0,
    ]);
  }
  overview.push([
    "TỔNG", totIn, totEx, totIn - totEx,
    totIn > 0 ? Number(((totIn - totEx) / totIn * 100).toFixed(2)) : 0,
    months.reduce((s, m) => s + (m.byStreamIncome.main || 0), 0),
    months.reduce((s, m) => s + (m.byStreamIncome.side || 0), 0),
    months.reduce((s, m) => s + (m.byStreamExpense.main || 0), 0),
    months.reduce((s, m) => s + (m.byStreamExpense.side || 0), 0),
  ]);

  // Sheet 2: Chi tiết theo danh mục
  const detail: (string | number)[][] = [
    ["Tháng", "Loại", ...cats.map(catLabel), "Tổng"],
  ];
  for (const m of months) {
    const inRow: (string | number)[] = [m.month, "Thu"];
    let sIn = 0;
    for (const c of cats) { const v = m.byCatIncome[c] || 0; inRow.push(v); sIn += v; }
    inRow.push(sIn);
    detail.push(inRow);
    const exRow: (string | number)[] = [m.month, "Chi"];
    let sEx = 0;
    for (const c of cats) { const v = m.byCatExpense[c] || 0; exRow.push(v); sEx += v; }
    exRow.push(sEx);
    detail.push(exRow);
  }

  // Sheet 3: Giao dịch
  const tx: (string | number)[][] = [
    ["Ngày", "Loại", "Dòng", "Danh mục", "Tên món", "SL", "Đơn giá", "Số tiền", "Ghi chú", "Tự động"],
  ];
  for (const r of rows) {
    tx.push([
      r.occurred_on || "",
      r.kind === "income" ? "Thu" : "Chi",
      r.revenue_stream === "side" ? "Phụ" : "Chính",
      catLabel(r.category || "khac"),
      r.item_name || "",
      r.quantity ?? "",
      r.unit_price ?? "",
      Number(r.amount || 0),
      r.note || "",
      r.auto ? "1" : "",
    ]);
  }

  const wb = XLSX.utils.book_new();
  const ws1 = XLSX.utils.aoa_to_sheet(overview);
  const ws2 = XLSX.utils.aoa_to_sheet(detail);
  const ws3 = XLSX.utils.aoa_to_sheet(tx);
  ws1["!cols"] = [{ wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];
  ws2["!cols"] = [{ wch: 10 }, { wch: 6 }, ...cats.map(() => ({ wch: 14 })), { wch: 14 }];
  ws3["!cols"] = [{ wch: 12 }, { wch: 6 }, { wch: 8 }, { wch: 18 }, { wch: 28 }, { wch: 6 }, { wch: 12 }, { wch: 14 }, { wch: 30 }, { wch: 8 }];
  XLSX.utils.book_append_sheet(wb, ws1, "P&L Tổng quan");
  XLSX.utils.book_append_sheet(wb, ws2, "P&L Chi tiết");
  XLSX.utils.book_append_sheet(wb, ws3, "Giao dịch");
  XLSX.writeFile(wb, `bao-cao-pnl${suffix ? "_" + suffix : ""}_${from}_${to}.xlsx`);
}

/* ============================================================ Finance */

/** Combobox danh mục: gõ tự do, có gợi ý + tìm kiếm. Enter để tạo mới. */
export function CategoryCombobox({
  value, onChange, suggestions, placeholder, className, allowAll, allLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  suggestions: string[];
  placeholder?: string;
  className?: string;
  allowAll?: boolean;
  allLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const label = (v: string) => FIN_CATEGORY_LABEL[v] ?? v;
  const display = !value ? "" : value === "__all__" ? (allLabel ?? "Tất cả") : label(value);

  const list = useMemo(() => {
    const merged = Array.from(new Set([
      ...Object.keys(FIN_CATEGORY_LABEL),
      ...suggestions.filter(Boolean),
    ]));
    const q = query.trim().toLowerCase();
    if (!q) return merged;
    return merged.filter((c) => label(c).toLowerCase().includes(q) || c.toLowerCase().includes(q));
  }, [suggestions, query]);

  function pick(v: string) {
    onChange(v);
    setOpen(false);
    setQuery("");
  }

  return (
    <div ref={wrapRef} className={`relative ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-8 w-full items-center justify-between rounded-md border border-border bg-card px-2 text-left text-xs hover:bg-muted"
      >
        <span className={display ? "" : "text-muted-foreground"}>{display || (placeholder ?? "Chọn danh mục…")}</span>
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      {open ? (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-72 w-56 overflow-hidden rounded-md border border-border bg-popover shadow-lg">
          <div className="border-b border-border p-1.5">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const t = query.trim();
                  if (t) pick(t);
                }
              }}
              placeholder="Tìm hoặc gõ danh mục mới…"
              className="h-7 w-full rounded border border-border bg-background px-2 text-xs outline-none focus:border-primary"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {allowAll ? (
              <button type="button" onClick={() => pick("__all__")}
                className={`flex w-full items-center px-2 py-1.5 text-xs hover:bg-muted ${value === "__all__" || value === "" ? "bg-muted/60" : ""}`}>
                {allLabel ?? "Tất cả danh mục"}
              </button>
            ) : null}
            {list.map((c) => (
              <button key={c} type="button" onClick={() => pick(c)}
                className={`flex w-full items-center px-2 py-1.5 text-xs hover:bg-muted ${value === c ? "bg-muted/60" : ""}`}>
                {label(c)}
              </button>
            ))}
            {query.trim() && !list.some((c) => label(c).toLowerCase() === query.trim().toLowerCase() || c.toLowerCase() === query.trim().toLowerCase()) ? (
              <button type="button" onClick={() => pick(query.trim())}
                className="flex w-full items-center gap-1 border-t border-border px-2 py-1.5 text-xs text-primary hover:bg-muted">
                <Plus className="h-3 w-3" /> Tạo mới "{query.trim()}"
              </button>
            ) : null}
            {list.length === 0 && !query.trim() ? (
              <p className="px-2 py-2 text-xs text-muted-foreground">Chưa có danh mục.</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Hook lấy danh sách danh mục đã dùng trong DB. */
export function useFinanceCategories(stream: "main" | "side") {
  return useQuery({
    queryKey: ["finance_categories", stream],
    queryFn: () => api.get<string[]>("/finance/categories", { stream }),
  });
}


/* ============================================================ Shared bits */


/* ============================================================ Backup (Phase 0) */

type ImportRowLog = {
  line: number;
  action: "created" | "updated" | "merged" | "skipped" | "error";
  reason: string;
  matched_by?: string;
  id?: string;
};
type ImportReport = {
  ok?: boolean;
  table?: string;
  dry_run?: boolean;
  total_rows?: number;
  headers?: { recognized: string[]; unknown: string[]; aliases_supported?: string[] };
  created: number;
  updated: number;
  skipped: number;
  customers_created?: number;
  rows?: ImportRowLog[];
  rows_truncated?: boolean;
  errors?: string[];
  _sourcePath?: string;
  _fileName?: string;
};

function BackupPage({ user }: { user: PhpUser }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [dryRun, setDryRun] = useState<boolean>(false);
  const [importReport, setImportReport] = useState<ImportReport | null>(null);
  const [reportFilter, setReportFilter] = useState<"all" | "issues">("all");
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ["backup-list"],
    queryFn: () => api.get<{
      config: { primary_dir: string; mirror_dir: string };
      entries: Array<{ kind: string; name: string; disk: string; path: string; mtime: number; size: number; manifest: any }>;
    }>("/backup/list"),
    enabled: (user.role ?? "") === "admin",
  });

  const runMut = useMutation({
    mutationFn: (kind: "daily" | "monthly") => api.post<{ targets: Record<string, { ok: boolean; path?: string; reason?: string }> }>("/backup/run", { kind }),
    onSuccess: (r) => {
      const okP = r.targets?.primary?.ok; const okM = r.targets?.mirror?.ok;
      setInfo(`Backup xong — ổ chính: ${okP ? "✓" : "✗"}, ổ sao chép: ${okM ? "✓" : "—"}`);
      qc.invalidateQueries({ queryKey: ["backup-list"] });
    },
    onError: (e) => setError(getErrorMessage(e, "Chạy backup thất bại")),
  });

  if ((user.role ?? "") !== "admin") {
    return <ErrorBox message="Chỉ tài khoản admin mới được tải sao lưu." />;
  }

  async function download(path: string, fallbackName: string) {
    if (busy) return;
    setBusy(path); setError(null); setInfo(null);
    try {
      const res = await fetch(`${API_BASE}${path}`, { credentials: "include" });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const ct = res.headers.get("content-type") || "";
          if (ct.includes("application/json")) {
            const j = await res.json();
            msg = j?.message || j?.error || msg;
          }
        } catch { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const disp = res.headers.get("content-disposition") || "";
      const m = disp.match(/filename="?([^"]+)"?/i);
      const name = m?.[1] || fallbackName;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setInfo(`Đã tải ${name} (${Math.round(blob.size / 1024)} KB)`);
    } catch (e) {
      setError(getErrorMessage(e, "Tải file thất bại"));
    } finally {
      setBusy(null);
    }
  }

  async function importCsv(path: string, file: File) {
    if (busy) return;
    setBusy("import:" + path); setError(null); setInfo(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = dryRun ? path + (path.includes("?") ? "&" : "?") + "dry_run=1" : path;
      const r = await api.upload<ImportReport>(url, fd);
      setImportReport({ ...r, _sourcePath: path, _fileName: file.name });
      setReportFilter("all");
      const parts: string[] = [];
      if (r.created)  parts.push(`+${r.created} mới`);
      if (r.updated)  parts.push(`✎ ${r.updated} cập nhật`);
      if (r.skipped)  parts.push(`bỏ qua ${r.skipped}`);
      if (r.customers_created) parts.push(`+${r.customers_created} khách mới`);
      const prefix = r.dry_run ? "Thử trước (chưa ghi DB)" : "Nhập CSV xong";
      const msg = `${prefix}: ${parts.join(", ") || "không có thay đổi"}. Xem chi tiết trong popup.`;
      setInfo(msg);
      if (!r.dry_run) qc.invalidateQueries();
    } catch (e) {
      setError(getErrorMessage(e, "Nhập CSV thất bại"));
    } finally {
      setBusy(null);
    }
  }

  async function runDedupe() {
    if (busy) return;
    if (!confirm("Quét toàn bộ DB và hợp nhất các khách hàng / task trùng (gom về bản cũ nhất). Tiếp tục?")) return;
    setBusy("dedupe"); setError(null); setInfo(null);
    try {
      const r = await api.post<{ customers_merged: number; tasks_merged: number; finance_relinked: number }>("/backup/dedupe", {});
      setInfo(`Đã hợp nhất: ${r.customers_merged} khách, ${r.tasks_merged} task, relink ${r.finance_relinked} dòng tài chính.`);
      qc.invalidateQueries();
    } catch (e) {
      setError(getErrorMessage(e, "Hợp nhất thất bại"));
    } finally {
      setBusy(null);
    }
  }

  const items: Array<{ label: string; path: string; file: string; desc: string }> = [
    { label: "Tải file SQLite", path: "/backup/sqlite",        file: "nana.sqlite",   desc: "Bản sao đầy đủ toàn bộ database (.sqlite). Dùng để khôi phục tay khi cần." },
    { label: "Export Customers (CSV)", path: "/backup/csv/customers", file: "customers.csv", desc: "Danh sách khách hàng." },
    { label: "Export Tasks (CSV)",     path: "/backup/csv/tasks",     file: "tasks.csv",     desc: "Toàn bộ task / job." },
    { label: "Export Finance (CSV)",   path: "/backup/csv/finance",   file: "finance.csv",   desc: "Toàn bộ phiếu thu/chi." },
    { label: "Export Inventory (CSV)", path: "/backup/csv/inventory", file: "inventory.csv", desc: "Kho cho thuê — toàn bộ vật phẩm." },
    { label: "Export Products (CSV)",  path: "/backup/csv/products",  file: "products.csv",  desc: "Catalog sản phẩm (Đặt làm / Cho thuê / Sample…)." },
    { label: "Export Partners (CSV)",  path: "/backup/csv/partners",  file: "partners.csv",  desc: "Đối tác & đối thủ." },
    { label: "Export Projects (CSV)",  path: "/backup/csv/projects",  file: "projects.csv",  desc: "Cấu trúc project / nhóm task." },
    { label: "Export Media (CSV)",     path: "/backup/csv/media",     file: "media.csv",     desc: "Thư viện ảnh đã upload." },
  ];

  return (
    <section className="space-y-4">
      <div className="rounded-md border border-border bg-card p-4">
        <h2 className="text-lg font-semibold">Sao lưu &amp; Xuất dữ liệu</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Bản sao lưu chỉ dành cho admin. File được tải về máy bạn, KHÔNG public ra internet.
          Nên tải SQLite trước khi triển khai tính năng mới.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          CSV xuất ra và nhập vào dùng <b>chung cột</b> — bạn có thể tải xuống, sửa Excel, rồi nhập lại.
          Riêng <b>Tasks</b>: có cột <code>sample_shot / shared / payment_received / archived_at</code>;
          khi đủ 3 cờ ✓ (hoặc có <code>archived_at</code>) task sẽ tự vào <b>Lưu trữ</b> và sinh dòng tài chính.
          Khách hàng có <b>tên là link Facebook</b> sẽ tự được tách sang cột <code>facebook</code> và KHÔNG đếm trùng.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          Nhập <b>Tasks CSV</b> có thể kèm cột <code>customer_name / customer_phone / customer_facebook / customer_instagram / customer_address</code>{" "}
          — hệ thống sẽ <b>tự tìm hoặc tạo khách</b> rồi gán vào task. Khi tải xuống lại, các cột này đã có sẵn.
          Khách trùng FB/IG/SĐT (hoặc cùng id) sẽ được <b>gộp vào bản đang có</b>, không tạo trùng.
        </p>
        <div className="mt-3">
          <GhostBtn type="button" disabled={busy !== null} onClick={runDedupe}>
            {busy === "dedupe" ? "Đang hợp nhất…" : "🧹 Hợp nhất trùng lặp trong DB"}
          </GhostBtn>
          <label className="ml-3 inline-flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
            Thử trước khi nhập CSV (không ghi DB)
          </label>
        </div>
      </div>

      {error ? <ErrorBox message={error} /> : null}
      {info ? <p className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700">{info}</p> : null}

      <div className="grid gap-3 md:grid-cols-2">
        {items.map((it) => (
          <div key={it.path} className="flex flex-col gap-2 rounded-md border border-border bg-card p-4">
            <div>
              <h3 className="font-medium">{it.label}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{it.desc}</p>
            </div>
            <div>
              <PrimaryBtn
                type="button"
                disabled={busy !== null}
                onClick={() => download(it.path, it.file)}
              >
                {busy === it.path ? "Đang tải…" : "Tải xuống"}
              </PrimaryBtn>
              {it.path !== "/backup/sqlite" ? (
                <label className="ml-2 inline-flex items-center gap-1 cursor-pointer rounded border border-border bg-background px-3 py-1.5 text-sm hover:bg-accent">
                  {busy === "import:" + it.path ? "Đang nhập…" : "↑ Nhập CSV"}
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    disabled={busy !== null}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) importCsv(it.path, f);
                    }}
                  />
                </label>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      <BackupOriginalsDirSetting />

      <BackupDriveLinksSetting />

      <BackupRotationSection
        primaryDir={list.data?.config?.primary_dir ?? ""}
        mirrorDir={list.data?.config?.mirror_dir ?? ""}
        entries={list.data?.entries ?? []}
        running={runMut.isPending}
        onRun={(k) => { setError(null); setInfo(null); runMut.mutate(k); }}
        onReload={() => qc.invalidateQueries({ queryKey: ["backup-list"] })}
      />

      <p className="text-xs text-muted-foreground">
        Phase 0 — chỉ tải xuống, chưa có khôi phục tự động. Để khôi phục, thay file{" "}
        <code>data/nana.sqlite</code> trên server bằng file SQLite đã tải.
      </p>

      {importReport ? (
        <ImportReportModal
          report={importReport}
          filter={reportFilter}
          onFilterChange={setReportFilter}
          onClose={() => setImportReport(null)}
        />
      ) : null}
    </section>
  );
}

/** Popup chi tiết kết quả nhập CSV: header, từng dòng, lý do skip. */
function ImportReportModal({
  report, filter, onFilterChange, onClose,
}: {
  report: ImportReport;
  filter: "all" | "issues";
  onFilterChange: (f: "all" | "issues") => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const rows = report.rows ?? [];
  const shown = filter === "issues" ? rows.filter((r) => r.action === "skipped" || r.action === "error") : rows;
  const recognized = report.headers?.recognized ?? [];
  const unknown = report.headers?.unknown ?? [];
  const aliases = report.headers?.aliases_supported ?? [];

  function actionBadge(a: ImportRowLog["action"]) {
    const map: Record<ImportRowLog["action"], string> = {
      created: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
      updated: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30",
      merged:  "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30",
      skipped: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
      error:   "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
    };
    return <span className={`inline-block rounded border px-1.5 py-0.5 text-[10px] uppercase font-semibold ${map[a]}`}>{a}</span>;
  }

  function downloadJson() {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `csv-import-report-${report.table ?? "x"}-${Date.now()}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" onClick={onClose}>
      <div className="mt-6 w-full max-w-4xl rounded-md border border-border bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold">
              Kết quả nhập CSV — <code>{report.table}</code>
              {report.dry_run ? <span className="ml-2 inline-block rounded border border-amber-500/40 bg-amber-500/15 px-2 py-0.5 text-xs font-bold uppercase text-amber-700 dark:text-amber-400">Dry-run</span> : null}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              File: <code>{report._fileName}</code> · {report.total_rows ?? rows.length} dòng dữ liệu
            </p>
          </div>
          <div className="flex gap-2">
            <GhostBtn type="button" onClick={downloadJson}>Tải báo cáo JSON</GhostBtn>
            <GhostBtn type="button" onClick={onClose}>Đóng</GhostBtn>
          </div>
        </div>

        {/* Tổng quan */}
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5 text-sm">
          <ImportStat label="Tạo mới" value={report.created} cls="text-emerald-600 dark:text-emerald-400" />
          <ImportStat label="Cập nhật" value={report.updated} cls="text-blue-600 dark:text-blue-400" />
          <ImportStat label="Gộp (merge)" value={rows.filter((r) => r.action === "merged").length} cls="text-violet-600 dark:text-violet-400" />
          <ImportStat label="Bỏ qua" value={report.skipped} cls="text-amber-600 dark:text-amber-400" />
          <ImportStat label="Khách mới" value={report.customers_created ?? 0} cls="text-emerald-600 dark:text-emerald-400" />
        </div>

        {/* Header analysis */}
        <div className="mb-4 rounded-md border border-border bg-background p-3">
          <p className="text-xs font-semibold mb-2">Phân tích cột tiêu đề</p>
          <div className="grid gap-2 sm:grid-cols-2 text-xs">
            <div>
              <p className="text-emerald-700 dark:text-emerald-400 font-semibold mb-1">✓ Cột được nhận ({recognized.length})</p>
              <div className="flex flex-wrap gap-1">
                {recognized.length === 0 ? <span className="text-muted-foreground">— không có —</span>
                  : recognized.map((h) => <code key={h} className="rounded bg-emerald-500/10 px-1.5 py-0.5">{h}</code>)}
              </div>
            </div>
            <div>
              <p className="text-amber-700 dark:text-amber-400 font-semibold mb-1">⚠ Cột bị bỏ qua ({unknown.length})</p>
              <div className="flex flex-wrap gap-1">
                {unknown.length === 0 ? <span className="text-muted-foreground">— không có —</span>
                  : unknown.map((h) => <code key={h} className="rounded bg-amber-500/10 px-1.5 py-0.5">{h}</code>)}
              </div>
              {unknown.length > 0 ? (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Các cột này không khớp với cột bảng <code>{report.table}</code>{aliases.length ? <> hay alias hợp lệ (<code>{aliases.join(", ")}</code>)</> : null} → dữ liệu trong cột bị bỏ qua.
                </p>
              ) : null}
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="mb-2 flex items-center gap-3 text-xs">
          <span className="font-semibold">Chi tiết dòng ({shown.length}/{rows.length}):</span>
          <label className="inline-flex items-center gap-1 cursor-pointer">
            <input type="radio" checked={filter === "all"} onChange={() => onFilterChange("all")} /> Tất cả
          </label>
          <label className="inline-flex items-center gap-1 cursor-pointer">
            <input type="radio" checked={filter === "issues"} onChange={() => onFilterChange("issues")} /> Chỉ hiện bỏ qua / lỗi
          </label>
          {report.rows_truncated ? <span className="ml-auto text-amber-600 dark:text-amber-400">⚠ Đã cắt tại 500 dòng đầu</span> : null}
        </div>

        {/* Bảng chi tiết */}
        <div className="max-h-[420px] overflow-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur">
              <tr>
                <th className="px-2 py-1.5 text-left w-14">Dòng</th>
                <th className="px-2 py-1.5 text-left w-24">Hành động</th>
                <th className="px-2 py-1.5 text-left w-48">ID / khớp qua</th>
                <th className="px-2 py-1.5 text-left">Lý do</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr><td colSpan={4} className="px-2 py-3 text-center text-muted-foreground">Không có dòng nào.</td></tr>
              ) : shown.map((r, i) => (
                <tr key={i} className="border-t border-border/60">
                  <td className="px-2 py-1 font-mono">{r.line}</td>
                  <td className="px-2 py-1">{actionBadge(r.action)}</td>
                  <td className="px-2 py-1 font-mono text-[11px] text-muted-foreground">
                    {r.id ? <span title={r.id}>{r.id.slice(0, 8)}…</span> : "—"}
                    {r.matched_by ? <span className="ml-1 opacity-70">[{r.matched_by}]</span> : null}
                  </td>
                  <td className="px-2 py-1">{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {report.errors && report.errors.length > 0 ? (
          <details className="mt-3 text-xs">
            <summary className="cursor-pointer text-red-700 dark:text-red-400 font-semibold">Có {report.errors.length} thông điệp lỗi gộp</summary>
            <ul className="mt-1 list-disc pl-5 max-h-32 overflow-auto">
              {report.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </details>
        ) : null}
      </div>
    </div>
  );
}

function ImportStat({ label, value, cls }: { label: string; value: number; cls?: string }) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold ${cls ?? ""}`}>{value}</div>
    </div>
  );
}

/** Phase B1 — cấu hình 2 ổ + danh sách bản backup xoay vòng. */
function BackupRotationSection({ primaryDir, mirrorDir, entries, running, onRun, onReload }: {
  primaryDir: string; mirrorDir: string;
  entries: Array<{ kind: string; name: string; disk: string; path: string; mtime: number; size: number; manifest: any }>;
  running: boolean; onRun: (k: "daily" | "monthly") => void; onReload: () => void;
}) {
  const qc = useQueryClient();
  // Placeholder mẫu — KHÔNG hard-code bắt buộc. Admin có thể đổi sang bất kỳ path nào.
  const DEFAULT_PRIMARY = "";
  const DEFAULT_MIRROR  = "";
  const [primary, setPrimary] = useState(primaryDir || DEFAULT_PRIMARY);
  const [mirror, setMirror] = useState(mirrorDir || DEFAULT_MIRROR);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { setPrimary(primaryDir || DEFAULT_PRIMARY); }, [primaryDir]);
  useEffect(() => { setMirror(mirrorDir || DEFAULT_MIRROR); }, [mirrorDir]);

  // Lấy các setting đường dẫn mở rộng (portable backup) trực tiếp từ site-texts.
  const { data: stRows = [] } = useQuery({
    queryKey: ["site-texts"],
    queryFn: () => api.get<Array<{ key: string; value: string }>>("/site-texts"),
  });
  const curExtra   = stRows.find(r => r.key === "app.backup_extra_roots")?.value ?? "";
  const curPiwigo  = stRows.find(r => r.key === "app.piwigo_root")?.value ?? "";
  const curUploads = stRows.find(r => r.key === "app.uploads_root")?.value ?? "";
  const [extra, setExtra]     = useState(curExtra);
  const [piwigo, setPiwigo]   = useState(curPiwigo);
  const [uploads, setUploads] = useState(curUploads);
  useEffect(() => { setExtra(curExtra); },     [curExtra]);
  useEffect(() => { setPiwigo(curPiwigo); },   [curPiwigo]);
  useEffect(() => { setUploads(curUploads); }, [curUploads]);

  const saveDirs = useMutation({
    mutationFn: async () => {
      await api.put("/site-texts/" + encodeURIComponent("app.backup_primary_dir"), { value: primary.trim(), upsert: true });
      await api.put("/site-texts/" + encodeURIComponent("app.backup_mirror_dir"),  { value: mirror.trim(),  upsert: true });
      await api.put("/site-texts/" + encodeURIComponent("app.backup_extra_roots"), { value: extra.trim(),   upsert: true });
      await api.put("/site-texts/" + encodeURIComponent("app.piwigo_root"),        { value: piwigo.trim(),  upsert: true });
      await api.put("/site-texts/" + encodeURIComponent("app.uploads_root"),       { value: uploads.trim(), upsert: true });
    },
    onSuccess: () => {
      setMsg("Đã lưu cấu hình đường dẫn backup/restore.");
      qc.invalidateQueries({ queryKey: ["backup-list"] });
      qc.invalidateQueries({ queryKey: ["site-texts"] });
      onReload();
    },
    onError: (e) => setMsg(getErrorMessage(e, "Lưu thất bại")),
  });

  return (
    <div className="rounded-md border border-border bg-card p-4 space-y-3">
      <div>
        <h3 className="font-medium">Cấu hình đường dẫn backup (portable giữa các server)</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Daily giữ 10 bản gần nhất, monthly giữ 12. Không hard-code path: tất cả đường dẫn dưới đây
          lưu trong DB (app_settings/site_texts) và scan theo cấu trúc <code>daily|weekly|monthly|manual/&lt;stamp&gt;/</code>.
          Khi chuyển server (Pi, ổ khác…), chỉ cần đổi path ở đây — code không đổi.
          Cron: <code>5 2 * * * php backend-php/cli/run-backup.php daily</code>{" · "}
          <code>10 2 1 * * php backend-php/cli/run-backup.php monthly</code>{" · "}
          override path: <code>--root=/your/Backup-Data</code>.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">Backup root chính (backup_root_primary)
          <Input value={primary} onChange={(e) => setPrimary(e.target.value)} placeholder="ví dụ /mnt/disk1/Web-backup/Backup-Data" className="mt-1" />
        </label>
        <label className="block text-sm">Backup root phụ (backup_root_secondary)
          <Input value={mirror} onChange={(e) => setMirror(e.target.value)} placeholder="ví dụ /mnt/disk2/Web-backup/Backup-Data" className="mt-1" />
        </label>
      </div>
      <label className="block text-sm">Backup roots bổ sung (mỗi dòng 1 path — quét cùng cấu trúc)
        <textarea value={extra} onChange={(e) => setExtra(e.target.value)}
          placeholder={"ví dụ:\n/home/nana/Web-backup/Backup-Data\n/mnt/usb/Backup-Data"}
          className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm font-mono"
          rows={3} />
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">Uploads root (image_backup_root / uploads runtime)
          <Input value={uploads} onChange={(e) => setUploads(e.target.value)}
            placeholder="ví dụ /var/www/nana-deploy/uploads (mặc định từ config app.php)" className="mt-1" />
        </label>
        <label className="block text-sm">Piwigo root (gốc ảnh Piwigo)
          <Input value={piwigo} onChange={(e) => setPiwigo(e.target.value)}
            placeholder="ví dụ /var/www/piwigo" className="mt-1" />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <PrimaryBtn type="button" disabled={saveDirs.isPending} onClick={() => saveDirs.mutate()}>
          {saveDirs.isPending ? "Đang lưu…" : "Lưu cấu hình đường dẫn"}
        </PrimaryBtn>
        <GhostBtn type="button" disabled={running} onClick={() => onRun("daily")}>
          {running ? "Đang chạy…" : "Chạy backup daily ngay"}
        </GhostBtn>
        <GhostBtn type="button" disabled={running} onClick={() => onRun("monthly")}>Chạy backup monthly</GhostBtn>
        <GhostBtn type="button" onClick={onReload}>Tải lại danh sách</GhostBtn>
      </div>
      {msg ? <p className="text-xs text-muted-foreground">{msg}</p> : null}

      <RestoreFromFolderBlock entries={entries} />

      <div className="mt-2">
        <h4 className="text-sm font-medium mb-2">Bản sao lưu hiện có ({entries.length})</h4>
        {entries.length === 0 ? (
          <p className="text-xs text-muted-foreground">Chưa có bản nào (chưa chạy CLI hoặc chưa cấu hình ổ).</p>
        ) : (
          <div className="overflow-x-auto rounded border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-2 py-1 text-left">Tên</th>
                  <th className="px-2 py-1 text-left">Loại</th>
                  <th className="px-2 py-1 text-left">Root</th>
                  <th className="px-2 py-1 text-center">SQLite</th>
                  <th className="px-2 py-1 text-center">Manifest</th>
                  <th className="px-2 py-1 text-center">Uploads</th>
                  <th className="px-2 py-1 text-center">Piwigo</th>
                  <th className="px-2 py-1 text-right">SQLite</th>
                  <th className="px-2 py-1 text-left">Tạo lúc</th>
                  <th className="px-2 py-1 text-left">Đường dẫn</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, i) => (
                  <tr key={i} className="border-t border-border/60">
                    <td className="px-2 py-1 font-mono">{e.name}</td>
                    <td className="px-2 py-1">{e.kind}</td>
                    <td className="px-2 py-1">{e.disk}</td>
                    <td className="px-2 py-1 text-center">{(e as any).has_sqlite === false ? "❌" : "✓"}</td>
                    <td className="px-2 py-1 text-center">{(e as any).manifest_missing ? "⚠" : "✓"}</td>
                    <td className="px-2 py-1 text-center">{(e as any).has_uploads ? "✓" : "—"}</td>
                    <td className="px-2 py-1 text-center">{(e as any).has_piwigo ? "✓" : "—"}</td>
                    <td className="px-2 py-1 text-right font-mono">{(e.size / 1024).toFixed(1)} KB</td>
                    <td className="px-2 py-1">{fmtDateTime(new Date(e.mtime * 1000))}</td>
                    <td className="px-2 py-1 font-mono text-[10px] text-muted-foreground">{e.path}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function BackupOriginalsDirSetting() {
  return BackupOriginalsDirSettingImpl();
}

/** Khôi phục từ thư mục backup — input đường dẫn + Preview + Confirm gõ RESTORE. */
function RestoreFromFolderBlock({ entries }: { entries: Array<{ path: string; name: string; kind: string }> }) {
  const latestEntry = entries && entries.length > 0 ? entries[0].path : "";
  const [folder, setFolder] = useState(latestEntry || "");
  const [includeUploads, setIncludeUploads] = useState(true);
  const [preview, setPreview] = useState<any>(null);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [piwigoBusy, setPiwigoBusy] = useState(false);
  const [piwigoResult, setPiwigoResult] = useState<any>(null);

  async function doPreview() {
    setError(null); setPreview(null); setBusy(true);
    try {
      const r = await api.get<any>("/backup/inspect", { folder });
      setPreview(r);
    } catch (e) { setError(getErrorMessage(e, "Xem trước thất bại")); }
    finally { setBusy(false); }
  }

  async function doRestore() {
    setError(null); setResult(null); setBusy(true);
    try {
      const r = await api.post<any>("/backup/restore", {
        folder, include_uploads: includeUploads, confirm,
      });
      setResult(r);
      setTimeout(() => { try { window.location.reload(); } catch {} }, 2500);
    } catch (e) { setError(getErrorMessage(e, "Restore thất bại")); }
    finally { setBusy(false); }
  }

  async function doRestorePiwigo() {
    if (!window.confirm("Khôi phục ảnh gốc Piwigo từ bản backup này vào piwigo_root? Hành động không thể undo.")) return;
    setError(null); setPiwigoResult(null); setPiwigoBusy(true);
    try {
      const r = await api.post<any>("/backup/restore-piwigo", { folder, confirm: "RESTORE_PIWIGO" });
      setPiwigoResult(r);
    } catch (e) { setError(getErrorMessage(e, "Restore Piwigo thất bại")); }
    finally { setPiwigoBusy(false); }
  }

  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2 mt-2">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-medium">Khôi phục từ thư mục backup</h4>
        <span className="text-[10px] text-amber-700/80 dark:text-amber-300/80">⚠ Ghi đè DB hiện tại</span>
      </div>
      <p className="text-xs text-muted-foreground">
        Dán đường dẫn tuyệt đối tới thư mục backup (bất kỳ root nào đã cấu hình ở trên,
        ví dụ <code className="ml-1">&lt;backup_root&gt;/daily/2026-05-28_021707</code>).
        Hệ thống sẽ tự sao lưu DB hiện tại thành <code>nana.sqlite.pre-restore-…</code> trước khi ghi đè.
      </p>
      <div className="flex flex-wrap gap-2">
        <Input value={folder} onChange={(e) => setFolder(e.target.value)}
               placeholder="<backup_root>/daily/YYYY-MM-DD_HHMMSS" className="flex-1 min-w-[280px]" />
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" checked={includeUploads} onChange={(e) => setIncludeUploads(e.target.checked)} />
          Khôi phục ảnh local (uploads.tar.gz)
        </label>
        <GhostBtn type="button" disabled={!folder.trim() || busy} onClick={doPreview}>
          {busy ? "Đang đọc…" : "Xem trước"}
        </GhostBtn>
        <button type="button"
          disabled={!folder.trim() || busy}
          onClick={() => { setRestoreOpen(true); setConfirm(""); setResult(null); setError(null); }}
          className="rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50">
          Khôi phục…
        </button>
        {preview?.has_piwigo ? (
          <button type="button"
            disabled={!folder.trim() || piwigoBusy}
            onClick={doRestorePiwigo}
            className="rounded-md bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50">
            {piwigoBusy ? "Đang khôi phục Piwigo…" : "Khôi phục ảnh gốc Piwigo"}
          </button>
        ) : null}
      </div>
      {entries.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">Hoặc chọn nhanh từ {entries.length} bản đã có</summary>
          <div className="mt-1 max-h-32 overflow-auto space-y-0.5">
            {entries.slice(0, 30).map((e, i) => (
              <button key={i} type="button" onClick={() => setFolder(e.path)}
                className="block w-full text-left font-mono text-[10px] hover:bg-muted/40 px-1 py-0.5 rounded">
                [{e.kind}] {e.name} — {e.path}
              </button>
            ))}
          </div>
        </details>
      )}
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
      {preview ? (
        <div className="rounded border border-border bg-background/60 p-2 text-xs space-y-1">
          <div><b>Folder:</b> <code>{preview.folder}</code></div>
          <div>
            SQLite: {preview.has_sqlite ? `${(preview.sqlite_bytes/1024).toFixed(1)} KB ✓` : "❌ thiếu"}{" · "}
            Uploads: {preview.has_uploads ? `${(preview.uploads_bytes/1024/1024).toFixed(2)} MB ✓` : "(không có uploads.tar.gz)"}
            {" · "}Piwigo: {preview.has_piwigo
              ? `${preview.piwigo_artifact}${preview.piwigo_is_dir ? " (thư mục)" : ` ${(preview.piwigo_bytes/1024/1024).toFixed(2)} MB`} ✓`
              : "(không có)"}
          </div>
          {preview.manifest_missing ? (
            <div className="text-amber-700 dark:text-amber-300">⚠ Backup thiếu manifest.json — vẫn cho restore nhưng không có metadata.</div>
          ) : null}
          {preview.manifest ? (
            <div>
              <b>Manifest:</b> {preview.manifest.kind} · {preview.manifest.created_at}
              {preview.manifest.row_counts ? (
                <pre className="text-[10px] mt-1 max-h-32 overflow-auto">{JSON.stringify(preview.manifest.row_counts, null, 2)}</pre>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {piwigoResult ? (
        <div className="rounded border border-green-500/40 bg-green-500/10 p-2 text-xs">
          <div className="font-medium text-green-700 dark:text-green-300">✓ Khôi phục Piwigo xong</div>
          <pre className="text-[10px] max-h-40 overflow-auto">{JSON.stringify(piwigoResult, null, 2)}</pre>
        </div>
      ) : null}

      {restoreOpen && (
        <Modal title="Xác nhận khôi phục" onClose={() => !busy && setRestoreOpen(false)}>
          <div className="space-y-3 text-sm">
            <p className="text-red-600">
              Hành động này sẽ <b>ghi đè toàn bộ database hiện tại</b> bằng bản backup tại:
            </p>
            <code className="block text-xs break-all bg-muted/40 p-2 rounded">{folder}</code>
            <p className="text-xs text-muted-foreground">
              DB hiện tại sẽ được tự động lưu thành <code>nana.sqlite.pre-restore-&lt;timestamp&gt;</code>.
              {includeUploads ? " Ảnh local cũng sẽ được giải nén đè." : " (Bỏ qua ảnh local.)"}
            </p>
            <label className="block text-xs">
              Gõ <code className="text-red-600">RESTORE</code> để xác nhận:
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" autoFocus />
            </label>
            {error ? <p className="text-xs text-red-600 whitespace-pre-wrap">{error}</p> : null}
            {result ? (
              <div className="text-xs space-y-1 rounded border border-green-500/40 bg-green-500/10 p-2">
                <div className="font-medium text-green-700 dark:text-green-300">✓ Khôi phục xong — tự reload sau 2.5s</div>
                <pre className="text-[10px] max-h-40 overflow-auto">{JSON.stringify(result, null, 2)}</pre>
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <GhostBtn type="button" disabled={busy} onClick={() => setRestoreOpen(false)}>Huỷ</GhostBtn>
              <button type="button"
                disabled={busy || confirm !== "RESTORE" || !!result}
                onClick={doRestore}
                className="rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50">
                {busy ? "Đang khôi phục…" : "Khôi phục ngay"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function BackupDriveLinksSetting() {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({
    queryKey: ["site-texts"],
    queryFn: () => api.get<Array<{ key: string; value: string }>>("/site-texts"),
  });
  const curPrimary = rows.find((r) => r.key === "app.backup_primary_url")?.value ?? "";
  const curMirror  = rows.find((r) => r.key === "app.backup_mirror_url")?.value ?? "";
  const [primary, setPrimary] = useState(curPrimary);
  const [mirror, setMirror] = useState(curMirror);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { setPrimary(curPrimary); }, [curPrimary]);
  useEffect(() => { setMirror(curMirror); }, [curMirror]);

  const save = useMutation({
    mutationFn: async () => {
      await api.put("/site-texts/" + encodeURIComponent("app.backup_primary_url"), { value: primary.trim(), upsert: true });
      await api.put("/site-texts/" + encodeURIComponent("app.backup_mirror_url"),  { value: mirror.trim(),  upsert: true });
    },
    onSuccess: () => {
      setMsg("Đã lưu link 2 ổ sao lưu.");
      qc.invalidateQueries({ queryKey: ["site-texts"] });
    },
    onError: (e) => setMsg(getErrorMessage(e, "Lưu thất bại")),
  });

  const linkBox = (label: string, url: string) => (
    <div className="rounded-md border border-border bg-muted/30 p-3">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer"
           className="mt-1 block break-all text-sm text-primary underline underline-offset-2 hover:opacity-80">
          {url}
        </a>
      ) : (
        <div className="mt-1 text-sm text-muted-foreground italic">Chưa cấu hình</div>
      )}
    </div>
  );

  return (
    <div className="rounded-md border border-border bg-card p-4 space-y-3">
      <div>
        <h3 className="font-medium">Link truy cập 2 thư mục sao lưu (Drive / NAS / URL)</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Dán URL có thể mở được từ trình duyệt (Google Drive, OneDrive, NAS web UI, file server…) trỏ tới thư mục
          backup trên 2 ổ. Hai link này hiển thị ở đầu trang để mở nhanh khi cần xem/khôi phục.
        </p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {linkBox("Ổ chính (Primary)", curPrimary)}
        {linkBox("Ổ sao chép (Mirror)", curMirror)}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">Link ổ chính
          <Input value={primary} onChange={(e) => setPrimary(e.target.value)}
            placeholder="https://drive.google.com/drive/folders/..." className="mt-1" />
        </label>
        <label className="block text-sm">Link ổ sao chép
          <Input value={mirror} onChange={(e) => setMirror(e.target.value)}
            placeholder="https://drive.google.com/drive/folders/..." className="mt-1" />
        </label>
      </div>
      <div>
        <PrimaryBtn type="button" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Đang lưu…" : "Lưu link"}
        </PrimaryBtn>
      </div>
      {msg ? <p className="text-xs text-muted-foreground">{msg}</p> : null}
    </div>
  );
}

function BackupOriginalsDirSettingImpl() {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({
    queryKey: ["site-texts"],
    queryFn: () => api.get<Array<{ key: string; value: string }>>("/site-texts"),
  });
  const current = rows.find((r) => r.key === "app.backup_originals_dir")?.value ?? "";
  const DEFAULT_ORIGINALS = "/mnt/media/Web-backup/Backup-Picture";
  const [val, setVal] = useState(current || DEFAULT_ORIGINALS);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { setVal(current || DEFAULT_ORIGINALS); }, [current]);

  const save = useMutation({
    mutationFn: () => api.put("/site-texts/" + encodeURIComponent("app.backup_originals_dir"), {
      value: val.trim(), upsert: true,
    }),
    onSuccess: () => {
      setMsg("Đã lưu đường dẫn sao lưu ảnh gốc.");
      qc.invalidateQueries({ queryKey: ["site-texts"] });
    },
    onError: (e) => setMsg(getErrorMessage(e, "Lưu thất bại")),
  });

  return (
    <div className="rounded-md border border-border bg-card p-4">
      <h3 className="font-medium">Thư mục sao lưu ảnh gốc</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Đường dẫn tuyệt đối trên server tới ổ cứng dùng để lưu file ảnh gốc trước khi convert webp + upload Piwigo.
        Để trống = dùng mặc định <code>backend-php/public/uploads/_originals</code>. Thư mục phải tồn tại và writable cho web server (www-data).
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="/mnt/backup-disk/nana-originals"
          className="flex-1 min-w-[260px]"
        />
        <PrimaryBtn type="button" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Đang lưu…" : "Lưu đường dẫn"}
        </PrimaryBtn>
      </div>
      {msg ? <p className="mt-2 text-xs text-muted-foreground">{msg}</p> : null}
    </div>
  );
}

/* ============================================================ Piwigo (Phase I) */

type PiwigoTestResp = {
  ok: boolean; message?: string;
  base_url?: string | null; username?: string | null;
  parent_id?: number | null; privacy?: number | null;
};

function PiwigoAdminPage({ user }: { user: PhpUser }) {
  if ((user.role ?? "") !== "admin") {
    return <ErrorBox message="Chỉ admin mới quản lý Piwigo." />;
  }
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PiwigoTestResp | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function test() {
    if (busy) return;
    setBusy(true); setErr(null); setResult(null);
    try {
      const r = await api.get<PiwigoTestResp>("/piwigo/test");
      setResult(r);
    } catch (e) {
      setErr(getErrorMessage(e, "Không gọi được /piwigo/test"));
    } finally { setBusy(false); }
  }

  return (
    <section className="space-y-4">
      <div className="rounded-md border border-border bg-card p-4">
        <h2 className="text-lg font-semibold">Piwigo — Tự động hoá ảnh</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Tạo album và upload ảnh từ task vào Piwigo. Trang này chỉ kiểm tra kết nối.
          Các thao tác tạo album / upload nằm trong từng task (mở task → khối &quot;Album Piwigo&quot;).
        </p>
      </div>

      <div className="rounded-md border border-border bg-card p-4 space-y-3">
        <h3 className="font-medium">Bước I1 — Kiểm tra kết nối</h3>
        <p className="text-xs text-muted-foreground">
          File config: <code>backend-php/api/config/piwigo.php</code> (copy từ <code>piwigo.example.php</code>).
          Endpoint kiểm tra: <code>GET /api/piwigo/test</code> — server sẽ login vào Piwigo bằng tài khoản đã cấu hình.
        </p>
        <div>
          <PrimaryBtn type="button" disabled={busy} onClick={test}>
            {busy ? "Đang kiểm tra…" : "Kiểm tra kết nối"}
          </PrimaryBtn>
        </div>
        {err ? <ErrorBox message={err} /> : null}
        {result ? (
          result.ok ? (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 space-y-0.5">
              <div className="font-medium">✅ {result.message ?? "OK"}</div>
              {result.base_url ? <div className="text-xs">Base URL: <code>{result.base_url}</code></div> : null}
              {result.username ? <div className="text-xs">User: <code>{result.username}</code></div> : null}
              {result.parent_id != null ? <div className="text-xs">Parent album mặc định: #{result.parent_id}</div> : null}
              {result.privacy != null ? <div className="text-xs">Privacy level mặc định: {result.privacy}</div> : null}
            </div>
          ) : (
            <ErrorBox message={`Piwigo trả lỗi: ${result.message ?? "unknown"}`} />
          )
        ) : null}
      </div>

      <div className="rounded-md border border-border bg-card p-4 space-y-2 text-sm text-muted-foreground">
        <h3 className="font-medium text-foreground">Hướng dẫn tiếp theo</h3>
        <ul className="list-disc space-y-1 pl-5 text-xs">
          <li>I2 — Mở 1 task → khối &quot;Album Piwigo&quot; → bấm <strong>Tạo album rỗng</strong>. Album ID/URL được lưu vào task.</li>
          <li>I3 — Album ID/URL được tự động ghi vào trường <code>piwigo_album_id</code>/<code>piwigo_album_url</code> của task.</li>
          <li>I4/I5 — Chọn ảnh (JPG/PNG/WebP) → bấm <strong>Upload vào album</strong>. Thử 1 ảnh trước, ổn rồi mới chọn nhiều.</li>
          <li>I6 — Khi cần, ép định dạng WebP từ client trước khi upload (sẽ thêm sau khi I4/I5 chạy ổn).</li>
        </ul>
      </div>
    </section>
  );
}

/* Khối tạo album / upload ảnh — dùng trong TaskFormModal khi task đã có id. */
export function TaskPiwigoActions({
  taskId, taskTitle, albumId, albumUrl, albumStatus,
  onChange,
}: {
  taskId: string; taskTitle: string;
  albumId: string; albumUrl: string; albumStatus: string;
  onChange: (next: { albumId: string; albumUrl: string; albumStatus: string }) => void;
}) {
  const [name, setName] = useState(taskTitle || "");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState<"" | "create" | "upload">("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [convertWebp, setConvertWebp] = useState<boolean>(() => {
    try { return localStorage.getItem("nana.piwigoWebp") !== "0"; } catch { return true; }
  });
  const [keepOriginal, setKeepOriginal] = useState<boolean>(() => {
    try { return localStorage.getItem("nana.piwigoKeepOriginal") === "1"; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem("nana.piwigoWebp", convertWebp ? "1" : "0"); } catch {} }, [convertWebp]);
  useEffect(() => { try { localStorage.setItem("nana.piwigoKeepOriginal", keepOriginal ? "1" : "0"); } catch {} }, [keepOriginal]);

  async function toWebp(file: File, quality = 0.85): Promise<File> {
    if (file.type === "image/webp") return file;
    const bitmap = await createImageBitmap(file).catch(() => null);
    if (!bitmap) return file;
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width; canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0);
    const blob: Blob | null = await new Promise((res) => canvas.toBlob(res, "image/webp", quality));
    if (!blob) return file;
    const newName = file.name.replace(/\.(jpe?g|png|gif|bmp)$/i, "") + ".webp";
    return new File([blob], newName, { type: "image/webp" });
  }

  function pick(list: FileList | null) {
    if (!list) return;
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const next: File[] = [];
    const bad: string[] = [];
    for (const f of Array.from(list)) {
      if (!allowed.includes(f.type)) { bad.push(`${f.name} (định dạng không hỗ trợ)`); continue; }
      if (f.size > 20 * 1024 * 1024) { bad.push(`${f.name} (>20MB)`); continue; }
      next.push(f);
    }
    setFiles(next);
    if (bad.length > 0) setErr("Bỏ qua: " + bad.join(", ")); else setErr(null);
  }

  async function createAlbum() {
    if (busy) return;
    setBusy("create"); setErr(null); setMsg(null);
    try {
      const fd = new FormData();
      fd.append("data", JSON.stringify({ task_id: taskId, name: name || taskTitle || `task-${taskId.slice(0, 8)}` }));
      const r = await api.upload<{ albumId: number; albumUrl: string; albumStatus: string }>(
        "/piwigo/create-with-upload", fd,
      );
      onChange({ albumId: String(r.albumId), albumUrl: r.albumUrl, albumStatus: r.albumStatus });
      setMsg(`Đã tạo album #${r.albumId}`);
    } catch (e) {
      setErr(getErrorMessage(e, "Tạo album thất bại"));
    } finally { setBusy(""); }
  }

  async function uploadFiles() {
    if (busy) return;
    if (!albumId) { setErr("Chưa có album — bấm 'Tạo album' trước."); return; }
    if (files.length === 0) { setErr("Chưa chọn ảnh"); return; }
    setBusy("upload"); setErr(null); setMsg(null);
    try {
      const fd = new FormData();
      fd.append("album_id", albumId);
      const toSend: File[] = [];
      for (const f of files) {
        if (convertWebp && f.type !== "image/webp") {
          const webp = await toWebp(f);
          toSend.push(webp);
          if (keepOriginal) toSend.push(f);
        } else {
          toSend.push(f);
        }
      }
      for (const f of toSend) fd.append("images[]", f, f.name);
      const r = await api.upload<{ uploaded: number; uploadedFiles: any[]; failedFiles: { name: string; reason: string }[] }>(
        "/piwigo/upload", fd,
      );
      const okN = r.uploaded ?? r.uploadedFiles?.length ?? 0;
      const failN = r.failedFiles?.length ?? 0;
      if (failN === 0) {
        setMsg(`✅ Upload xong ${okN} ảnh`);
        onChange({ albumId, albumUrl, albumStatus: "uploaded" });
      } else {
        setMsg(`⚠ Upload ${okN}/${okN + failN} — lỗi: ${r.failedFiles.slice(0, 3).map((f) => `${f.name}: ${f.reason}`).join("; ")}`);
        onChange({ albumId, albumUrl, albumStatus: okN > 0 ? "upload_partial" : "upload_failed" });
      }
      setFiles([]);
      setFileKey((k) => k + 1);
    } catch (e) {
      setErr(getErrorMessage(e, "Upload thất bại"));
    } finally { setBusy(""); }
  }

  return (
    <div className="rounded-md border border-border/60 bg-muted/30 p-3 space-y-2.5 sm:col-span-2">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        <Server className="h-3 w-3" /> Album Piwigo (tự động)
      </div>

      {albumId || albumUrl ? (
        <div className="text-xs space-y-0.5">
          {albumId ? <div>Album ID: <span className="font-mono">#{albumId}</span> · trạng thái: <span className="font-medium">{albumStatus || "—"}</span></div> : null}
          {albumUrl ? (
            <a href={albumUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline break-all">
              <ExternalLink className="h-3 w-3" /> {albumUrl}
            </a>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={taskTitle || "Tên album"}
          disabled={busy !== ""}
        />
        <GhostBtn type="button" onClick={createAlbum} disabled={busy !== ""}>
          {busy === "create" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FolderPlus className="h-3.5 w-3.5" />}
          <span className="ml-1">Tạo album rỗng</span>
        </GhostBtn>
      </div>

      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <Input
          key={fileKey}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          multiple
          onChange={(e) => pick(e.target.files)}
          disabled={busy !== "" || !albumId}
        />
        <GhostBtn type="button" onClick={uploadFiles} disabled={busy !== "" || !albumId || files.length === 0}>
          {busy === "upload" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
          <span className="ml-1">Upload {files.length > 0 ? `(${files.length})` : ""}</span>
        </GhostBtn>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={convertWebp} onChange={(e) => setConvertWebp(e.target.checked)} disabled={busy !== ""} />
          <span>Nén WebP trước khi upload (giảm dung lượng)</span>
        </label>
        <label className={`inline-flex items-center gap-1.5 ${convertWebp ? "cursor-pointer" : "opacity-50"}`}>
          <input type="checkbox" checked={keepOriginal} onChange={(e) => setKeepOriginal(e.target.checked)} disabled={busy !== "" || !convertWebp} />
          <span>Giữ cả ảnh gốc</span>
        </label>
      </div>

      {!albumId ? (
        <p className="text-[11px] text-muted-foreground">Tạo album trước khi upload. Định dạng cho phép: JPG/PNG/WebP ≤ 20MB.</p>
      ) : null}
      {msg ? <p className="text-xs text-emerald-700">{msg}</p> : null}
      {err ? <p className="text-xs text-destructive">{err}</p> : null}
    </div>
  );
}

/* ============================================================ Customer detail modal */

export const TASK_STATUS_LABEL: Record<string, string> = {
  todo: "Cần làm", in_progress: "Đang làm", done: "Đã xong", blocked: "Tạm dừng",
};

export function CustomerDetailModal({ customer, onClose }: { customer: Customer; onClose: () => void }) {
  const hist = useQuery({
    queryKey: ["customers", "history", customer.id],
    queryFn: () => api.get<CustomerHistory>(`/customers/${customer.id}/history`),
  });

  return (
    <Modal title={`Khách: ${customer.name}`} onClose={onClose}>
      <div className="space-y-4 text-sm">
        <div className="grid gap-2 sm:grid-cols-2">
          <div><span className="text-muted-foreground">SĐT:</span> {customer.phone || "—"}</div>
          <div><span className="text-muted-foreground">VIP:</span> <VipBadge score={customer.vip_score ?? 0} /></div>
          <div className="sm:col-span-2"><span className="text-muted-foreground">Địa chỉ:</span> {customer.address || "—"}</div>
          {customer.real_name ? (
            <div className="sm:col-span-2"><span className="text-muted-foreground">Tên thật:</span> <span className="italic">{customer.real_name}</span></div>
          ) : null}
          {(customer.bank_transfer_names ?? []).length > 0 ? (
            <div className="sm:col-span-2">
              <span className="text-muted-foreground">Tên CK:</span> <BankNamePills names={customer.bank_transfer_names ?? null} max={6} />
            </div>
          ) : null}
          {(customer.tags ?? []).length > 0 ? (
            <div className="sm:col-span-2">
              <span className="text-muted-foreground">Tags:</span> <CustomerTagPills tags={customer.tags ?? null} max={20} />
            </div>
          ) : null}
          {customer.note ? <div className="sm:col-span-2"><span className="text-muted-foreground">Ghi chú:</span> {customer.note}</div> : null}
        </div>

        {hist.error ? <ErrorBox message={getErrorMessage(hist.error, "Không tải được lịch sử")} /> : null}

        {hist.data ? (
          <>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-md border border-border bg-card p-3">
                <p className="text-xs text-muted-foreground">Tổng thu</p>
                <p className="font-semibold text-emerald-600">{formatMoney(hist.data.totalIncome)}</p>
              </div>
              <div className="rounded-md border border-border bg-card p-3">
                <p className="text-xs text-muted-foreground">Tổng chi</p>
                <p className="font-semibold text-destructive">{formatMoney(hist.data.totalExpense)}</p>
              </div>
              <div className="rounded-md border border-border bg-card p-3">
                <p className="text-xs text-muted-foreground">Số dư</p>
                <p className="font-semibold">{formatMoney(hist.data.netSpend)}</p>
              </div>
            </div>

            <div>
              <h3 className="mb-2 font-semibold">Công việc ({hist.data.tasks.length})</h3>
              {hist.data.tasks.length === 0 ? (
                <p className="text-xs text-muted-foreground">Chưa có công việc.</p>
              ) : (
                <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-border">
                  {hist.data.tasks.map((t) => (
                    <li key={t.id} className="flex items-center justify-between gap-2 border-b border-border px-3 py-2 text-xs last:border-b-0">
                      <span className="truncate">{t.title}</span>
                      <span className="shrink-0 text-muted-foreground">
                        {TASK_STATUS_LABEL[t.status ?? ""] ?? t.status ?? "—"}{t.due_date ? ` · ${fmtDate(t.due_date)}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <h3 className="mb-2 font-semibold">Thu/Chi ({hist.data.finance.length})</h3>
              {hist.data.finance.length === 0 ? (
                <p className="text-xs text-muted-foreground">Chưa có giao dịch.</p>
              ) : (
                <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-border">
                  {hist.data.finance.map((f) => (
                    <li key={f.id} className="flex items-center justify-between gap-2 border-b border-border px-3 py-2 text-xs last:border-b-0">
                      <span className="truncate">{f.occurred_on ? fmtDate(f.occurred_on) : "—"} · {f.note || f.item_name || f.category || "—"}</span>
                      <span className={`shrink-0 font-medium ${f.kind === "income" ? "text-emerald-600" : "text-destructive"}`}>
                        {f.kind === "income" ? "+" : "−"}{formatMoney(Number(f.amount || 0))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : (
          <StatusText>Đang tải lịch sử…</StatusText>
        )}

        <div className="flex justify-end">
          <GhostBtn type="button" onClick={onClose}>Đóng</GhostBtn>
        </div>
      </div>
    </Modal>
  );
}

/* ============================================================ Photo checklist (inline trong TaskForm) */

export function ChecklistEditor({ items, onChange, defaults }: {
  items: ChecklistItem[]; onChange: (next: ChecklistItem[]) => void; defaults: PhotoChecklistDefault[];
}) {
  const [newName, setNewName] = useState("");
  const done = items.filter((i) => i.checked).length;

  const loadDefaults = () => {
    const fresh = makeChecklistFromDefaults(defaults);
    const have = new Set(items.map((i) => i.name.toLowerCase()));
    onChange([...items, ...fresh.filter((d) => !have.has(d.name.toLowerCase()))]);
  };
  const add = () => {
    const n = newName.trim();
    if (!n) return;
    onChange([...items, { id: globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2), name: n, checked: false }]);
    setNewName("");
  };
  const toggle = (id: string) => onChange(items.map((i) => i.id === id ? { ...i, checked: !i.checked } : i));
  const remove = (id: string) => onChange(items.filter((i) => i.id !== id));

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Checklist phụ kiện {items.length > 0 ? <span className="ml-1 font-normal normal-case tracking-normal">({done}/{items.length})</span> : null}
        </div>
        <GhostBtn type="button" onClick={loadDefaults} disabled={defaults.length === 0} className="!py-1 text-xs">Nạp mặc định</GhostBtn>
      </div>
      {items.length === 0 ? (
        <div className="rounded-md border border-dashed p-3 text-xs italic text-muted-foreground">
          Chưa có món nào. Bấm "Nạp mặc định" hoặc thêm thủ công bên dưới.
        </div>
      ) : (
        <div className="max-h-64 space-y-1 overflow-auto rounded-md border border-border bg-background p-2">
          {items.map((it) => (
            <div key={it.id} className="rounded px-1.5 py-1 hover:bg-muted">
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={it.checked} onChange={() => toggle(it.id)} />
                {it.image_url ? (
                  <img src={it.image_url} alt="" className="w-8 h-8 object-cover rounded border border-border" />
                ) : null}
                <span className={`flex-1 text-sm ${it.checked ? "text-muted-foreground line-through" : ""}`}>{it.name}</span>
                <Input
                  placeholder="URL ảnh (tuỳ chọn)"
                  value={it.image_url ?? ""}
                  onChange={(e) => onChange(items.map((x) => x.id === it.id ? { ...x, image_url: e.target.value || null } : x))}
                  className="!py-0.5 text-[11px] w-40"
                />
                <button type="button" onClick={() => remove(it.id)} className="text-xs text-destructive hover:underline">Xóa</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input placeholder="Thêm phụ kiện…" value={newName} onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} className="!py-1 text-sm" />
        <GhostBtn type="button" onClick={add} disabled={!newName.trim()} className="!py-1 text-sm">+ Thêm</GhostBtn>
      </div>
    </div>
  );
}

/* ============================================================ Photo checklist defaults page */

function PhotoChecklistDefaultsPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["photo_checklist_defaults"],
    queryFn: () => api.get<PhotoChecklistDefault[]>("/photo-checklist-defaults"),
  });
  const [newName, setNewName] = useState("");

  const create = useMutation({
    mutationFn: (name: string) => {
      const nextOrder = ((list.data ?? [])[(list.data ?? []).length - 1]?.sort_order ?? 0) + 10;
      return api.post("/photo-checklist-defaults", { name, sort_order: nextOrder });
    },
    onSuccess: () => { setNewName(""); qc.invalidateQueries({ queryKey: ["photo_checklist_defaults"] }); },
  });
  const update = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.put(`/photo-checklist-defaults/${id}`, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["photo_checklist_defaults"] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/photo-checklist-defaults/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["photo_checklist_defaults"] }),
  });

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Checklist phụ kiện chụp ảnh</h2>
        <p className="text-sm text-muted-foreground">
          Danh sách mặc định sẽ được nạp vào mỗi task chụp ảnh mới. Bạn vẫn có thể thêm/bớt riêng cho từng buổi chụp.
        </p>
      </div>

      <div className="flex gap-2">
        <Input placeholder="Tên phụ kiện mới…" value={newName} onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (newName.trim()) create.mutate(newName.trim()); } }} />
        <PrimaryBtn type="button" onClick={() => newName.trim() && create.mutate(newName.trim())} disabled={!newName.trim() || create.isPending}>
          + Thêm
        </PrimaryBtn>
      </div>

      {list.error ? <ErrorBox message={getErrorMessage(list.error, "Không tải được danh sách")} /> : null}
      {create.error ? <ErrorBox message={getErrorMessage(create.error, "Thêm thất bại")} /> : null}

      {list.isFetching && !list.data ? (
        <StatusText>Đang tải…</StatusText>
      ) : (list.data?.length ?? 0) === 0 ? (
        <p className="text-sm italic text-muted-foreground">Chưa có phụ kiện mặc định nào.</p>
      ) : (
        <div className="space-y-2">
          {(list.data ?? []).map((it) => (
            <DefaultRow key={it.id} item={it}
              onSave={(name) => update.mutate({ id: it.id, name })}
              onDelete={async () => { if (await confirmAsync(`Xoá "${it.name}"?`)) del.mutate(it.id); }} />
          ))}
        </div>
      )}
    </section>
  );
}

function DefaultRow({ item, onSave, onDelete }: {
  item: PhotoChecklistDefault; onSave: (name: string) => void; onDelete: () => void;
}) {
  const [name, setName] = useState(item.name);
  useEffect(() => { setName(item.name); }, [item.name]);
  const dirty = name.trim() !== item.name && name.trim().length > 0;
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-card p-2">
      <Input value={name} onChange={(e) => setName(e.target.value)} className="flex-1" />
      {dirty ? <GhostBtn type="button" onClick={() => onSave(name.trim())}>Lưu</GhostBtn> : null}
      <DangerBtn type="button" onClick={onDelete}>Xoá</DangerBtn>
    </div>
  );
}

/* ============================================================ Coming soon */

function ComingSoonPage({ page }: { page: Page }) {
  const label = NAV.find((n) => n.page === page)?.label ?? page;
  return (
    <section className="rounded-md border border-dashed border-border bg-card p-10 text-center">
      <h2 className="text-lg font-semibold">{label}</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Tính năng này sẽ được bù vào trong các phase sau. UI đã được khôi phục theo bản gốc.
      </p>
    </section>
  );
}

/* ============================================================ Inventory (Kho cho thuê) */

const INV_UNGROUPED = "__ungrouped__";
const INV_UNGROUPED_LABEL = "Chưa phân nhóm";

function InventoryPage() {
  const qc = useQueryClient();
  const [stream] = useCurrentStream();
  // Khoá cứng theo stream: chính = cosplay & phụ kiện; phụ = mô hình súng.
  const tab: "cosplay" | "sung" = stream === "side" ? "sung" : "cosplay";
  const [edit, setEdit] = useState<Partial<InventoryItem> | null>(null);
  const list = useQuery({
    queryKey: ["inventory"],
    queryFn: () => api.get<InventoryItem[]>("/inventory"),
  });
  const items = list.data ?? [];
  // K9 Phase 2: liên thông Task ↔ Inventory. Map id → Task[] dùng cho tooltip/badge.
  const links = useTaskCrossLinks();
  void links;

  const grouped = useMemo(() => {
    const m = new Map<InventoryCategory, Map<string, InventoryItem[]>>();
    (["trang-phuc", "phu-kien-cosplay", "mo-hinh-sung"] as InventoryCategory[]).forEach((c) =>
      m.set(c, new Map([[INV_UNGROUPED, []]])));
    for (const i of items) {
      const cat = m.get(i.category); if (!cat) continue;
      const key = i.group_name?.trim() || INV_UNGROUPED;
      if (!cat.has(key)) cat.set(key, []);
      cat.get(key)!.push(i);
    }
    return m;
  }, [items]);

  const groupsByCat = useMemo(() => {
    const m = new Map<InventoryCategory, string[]>();
    for (const [cat, gmap] of grouped) m.set(cat, Array.from(gmap.keys()).filter((k) => k !== INV_UNGROUPED));
    return m;
  }, [grouped]);

  function addGroup(category: InventoryCategory) {
    const name = window.prompt("Tên nhóm mới (vd: Genshin Impact, FPS, ...):")?.trim();
    if (!name) return;
    if (name === INV_UNGROUPED_LABEL) { alert("Tên này được hệ thống dùng riêng"); return; }
    if ((groupsByCat.get(category) ?? []).includes(name)) { alert("Nhóm đã tồn tại"); return; }
    setEdit({ category, group_name: name, available: true });
  }

  function renderCategory(category: InventoryCategory, title: string) {
    const gmap = grouped.get(category) ?? new Map<string, InventoryItem[]>();
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold uppercase tracking-wider opacity-70">{title}</h3>
          <GhostBtn onClick={() => addGroup(category)} className="!py-1 text-xs">+ Thêm nhóm</GhostBtn>
        </div>
        {Array.from(gmap.entries()).map(([gkey, gitems]) => (
          <InventorySection key={gkey}
            title={gkey === INV_UNGROUPED ? INV_UNGROUPED_LABEL : gkey}
            items={gitems} loading={list.isFetching} ungrouped={gkey === INV_UNGROUPED}
            onEdit={setEdit}
            onAdd={() => setEdit({ category, group_name: gkey === INV_UNGROUPED ? null : gkey, available: true })}
            onDelete={async (id) => {
              try { await api.del(`/inventory/${id}`); qc.invalidateQueries({ queryKey: ["inventory"] }); }
              catch (e) { alert(getErrorMessage(e, "Xoá thất bại")); }
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="rounded-md border border-border bg-card px-3 py-1.5 text-sm">
          {stream === "side"
            ? "Kho Dòng phụ — Mô hình súng"
            : "Kho Dòng chính — Trang phục & phụ kiện cosplay"}
        </div>
        <PrimaryBtn onClick={() => setEdit({
          category: stream === "side" ? "mo-hinh-sung" : "trang-phuc",
          available: true,
        })}>+ Thêm món hàng</PrimaryBtn>
      </div>

      {list.error ? <ErrorBox message={getErrorMessage(list.error, "Không tải được kho")} /> : null}

      {tab === "cosplay" ? (
        <div className="space-y-8">
          {renderCategory("trang-phuc", "Trang phục")}
          {renderCategory("phu-kien-cosplay", "Phụ kiện cosplay")}
        </div>
      ) : (
        renderCategory("mo-hinh-sung", "Mô hình súng")
      )}

      {edit ? (
        <InventoryEditModal item={edit}
          existingGroups={groupsByCat.get((edit.category ?? "trang-phuc") as InventoryCategory) ?? []}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); qc.invalidateQueries({ queryKey: ["inventory"] }); }} />
      ) : null}
    </section>
  );
}

function InventorySection({ title, items, loading, ungrouped, onEdit, onAdd, onDelete }: {
  title: string; items: InventoryItem[]; loading: boolean; ungrouped?: boolean;
  onEdit: (i: InventoryItem) => void; onAdd: () => void; onDelete: (id: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border/60">
      <div className="flex items-center justify-between bg-muted/30 px-4 py-2">
        <div className="text-sm font-bold">
          {title} <span className="text-xs opacity-60">({items.length})</span>
          {ungrouped ? <span className="ml-2 text-[10px] uppercase opacity-50">mặc định</span> : null}
        </div>
        <GhostBtn onClick={onAdd} className="!py-0.5 text-xs">+ Thêm vào nhóm</GhostBtn>
      </div>
      {loading && items.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">Đang tải…</div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center text-sm italic text-muted-foreground">Chưa có món nào</div>
      ) : (
        <div className="divide-y divide-border/40">
          {items.map((i) => {
            const thumb = i.parent?.cover_image || i.parent?.first_image || i.image_url || "";
            const pStream = i.parent?.revenue_stream;
            return (
            <div key={i.id} className="group flex items-center gap-3 px-4 py-2.5 hover:bg-accent/30">
              {thumb ? (
                <img src={thumb} alt={i.name} className="h-10 w-10 rounded object-cover" />
              ) : (
                <div className="grid h-10 w-10 place-items-center rounded bg-muted text-muted-foreground"><Package className="h-4 w-4 opacity-50" /></div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {i.name}{i.variant ? ` · ${i.variant}` : ""}
                  {i.parent ? (
                    <span className="ml-2 text-[10px] text-primary/80">↗ {i.parent.name}</span>
                  ) : (
                    <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-700">Chưa liên kết sản phẩm</span>
                  )}
                </div>
                {i.note ? <div className="truncate text-xs text-muted-foreground">{i.note}</div> : null}
              </div>
              {pStream ? (
                <span className={`rounded px-1.5 py-0.5 text-[10px] ${pStream === "side" ? "bg-purple-500/15 text-purple-700" : "bg-sky-500/15 text-sky-700"}`}>
                  {pStream === "side" ? "Phụ" : "Chính"}
                </span>
              ) : null}
              <span className={`rounded px-2 py-0.5 text-[10px] ${i.available ? "bg-emerald-500/15 text-emerald-600" : "bg-muted text-muted-foreground"}`}>
                {i.available ? "Có sẵn" : "Đang cho thuê"}
              </span>
              <button type="button" onClick={() => onEdit(i)} className="text-xs text-primary hover:underline">Sửa</button>
              <button type="button" onClick={async () => { if (await confirmAsync(`Xoá "${i.name}"?`)) onDelete(i.id); }}
                className="text-xs text-destructive hover:underline">Xoá</button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InventoryEditModal({ item, existingGroups, onClose, onSaved }: {
  item: Partial<InventoryItem>; existingGroups: string[]; onClose: () => void; onSaved: () => void;
}) {
  const [stream] = useCurrentStream();
  const [form, setForm] = useState({
    name: item.name ?? "",
    category: (item.category ?? "trang-phuc") as InventoryCategory,
    variant: item.variant ?? "",
    note: item.note ?? "",
    image_url: item.image_url ?? "",
    group_name: item.group_name ?? "",
    available: item.available ?? true,
    product_id: item.product_id ?? null as string | null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const productsQ = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await api.get<any[]>("/products") ?? []).map(normalizeProductRow),
    staleTime: 60_000,
  });
  const linkedProduct = (productsQ.data ?? []).find((p) => p.id === form.product_id) || item.parent || null;

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!form.name.trim()) { setError("Cần tên món"); return; }
    setSaving(true); setError(null);
    const body = {
      name: form.name.trim(),
      category: form.category,
      variant: form.variant.trim() || null,
      note: form.note.trim() || null,
      image_url: form.image_url.trim() || null,
      group_name: form.group_name.trim() || null,
      available: form.available,
      product_id: form.product_id || null,
    };
    try {
      if (item.id) await api.put(`/inventory/${item.id}`, body);
      else await api.post("/inventory", body);
      onSaved();
    } catch (e) { setError(getErrorMessage(e, "Lưu thất bại")); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={item.id ? "Sửa món hàng" : "Thêm món hàng"} onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <div className="rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-xs">
          Đang ghi vào: <span className="font-semibold">{streamLabel(stream)}</span>
        </div>
        <label className="block text-sm">Tên *
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus className="mt-1" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">Danh mục
            <Select value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value as InventoryCategory })}
              className="mt-1">
              {(Object.keys(INV_CATEGORY_LABEL) as InventoryCategory[])
                .filter((c) => getInventoryStream(c) === stream)
                .map((c) => <option key={c} value={c}>{INV_CATEGORY_LABEL[c]}</option>)}
            </Select>
          </label>
          <label className="block text-sm">Size / Phiên bản
            <Input value={form.variant} onChange={(e) => setForm({ ...form, variant: e.target.value })} placeholder="vd: M, đen, v2..." className="mt-1" />
          </label>
        </div>
        <label className="block text-sm">Nhóm con
          <Input value={form.group_name} onChange={(e) => setForm({ ...form, group_name: e.target.value })}
            placeholder="Để trống = Chưa phân nhóm" list="inv-group-suggest" className="mt-1" />
          <datalist id="inv-group-suggest">
            {existingGroups.map((g) => <option key={g} value={g} />)}
          </datalist>
        </label>
        <label className="block text-sm">Ghi chú
          <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className="mt-1" />
        </label>
        <div className="text-sm">
          <ImageInput
            label="Ảnh"
            multiple={false}
            value={form.image_url ? [form.image_url] : []}
            onChange={(urls) => setForm({ ...form, image_url: urls[0] ?? "" })}
            folder={`kho/${form.category}`}
          />
        </div>
        <div className="rounded-md border border-border p-2.5 text-sm">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-xs font-medium uppercase tracking-wider opacity-70">Sản phẩm cha (parent product)</span>
            <div className="flex gap-2">
              <GhostBtn type="button" className="!py-0.5 text-xs" onClick={() => setPickerOpen((v) => !v)}>
                {pickerOpen ? "Đóng" : (form.product_id ? "Đổi" : "Liên kết")}
              </GhostBtn>
              {form.product_id ? (
                <GhostBtn type="button" className="!py-0.5 text-xs" onClick={() => setForm({ ...form, product_id: null })}>Gỡ liên kết</GhostBtn>
              ) : null}
            </div>
          </div>
          {linkedProduct ? (
            <div className="flex items-center gap-2">
              {(linkedProduct as any).cover_image || (linkedProduct as any).images?.[0] ? (
                <img src={(linkedProduct as any).cover_image || (linkedProduct as any).images?.[0]}
                     alt="" className="h-8 w-8 rounded object-cover" />
              ) : null}
              <span className="text-sm">{(linkedProduct as any).name}</span>
              <span className="text-[10px] opacity-60">stream: {(linkedProduct as any).revenue_stream ?? "main"}</span>
            </div>
          ) : (
            <div className="text-xs italic text-amber-700">Chưa liên kết — sẽ hiển thị "Chưa liên kết sản phẩm".</div>
          )}
          {pickerOpen ? (
            <ProductPickerInline
              products={productsQ.data ?? []}
              onPick={(id) => { setForm({ ...form, product_id: id }); setPickerOpen(false); }}
            />
          ) : null}
        </div>
        <label className="flex items-center gap-2 pt-2 text-sm">
          <input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} />
          Còn sẵn
        </label>
        {error ? <ErrorBox message={error} /> : null}
        <div className="flex justify-end gap-2 pt-2">
          <GhostBtn type="button" onClick={onClose}>Huỷ</GhostBtn>
          <PrimaryBtn type="submit" disabled={saving}>{saving ? "Đang lưu…" : "Lưu"}</PrimaryBtn>
        </div>
      </form>
    </Modal>
  );
}

function ProductPickerInline({ products, onPick }: {
  products: ProductRow[];
  onPick: (id: string) => void;
}) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    const rentalish = products.filter((p) => p.mode === "rental" || p.mode === "both" || p.mode === "sample");
    if (!n) return rentalish.slice(0, 30);
    return rentalish.filter((p) =>
      p.name.toLowerCase().includes(n) ||
      (p.slug ?? "").toLowerCase().includes(n) ||
      (p.category ?? "").toLowerCase().includes(n),
    ).slice(0, 50);
  }, [products, q]);
  return (
    <div className="mt-2 rounded border border-border/60 bg-muted/20 p-2">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm sản phẩm theo tên / slug / loại…" />
      <div className="mt-2 max-h-64 space-y-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-2 py-3 text-xs italic text-muted-foreground">Không có sản phẩm phù hợp.</div>
        ) : filtered.map((p) => (
          <button key={p.id} type="button" onClick={() => onPick(p.id)}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent">
            {p.cover_image || p.images?.[0] ? (
              <img src={p.cover_image || p.images?.[0]} alt="" className="h-7 w-7 rounded object-cover" />
            ) : <div className="h-7 w-7 rounded bg-muted" />}
            <span className="flex-1 truncate">{p.name}</span>
            <span className="text-[10px] opacity-60">{p.revenue_stream}</span>
          </button>
        ))}
      </div>
    </div>
  );
}



/* ============================================================ Taxonomy (Danh mục & thuộc tính) */

function TaxonomyPage() {
  const qc = useQueryClient();
  const types = useQuery({ queryKey: ["attribute_types"], queryFn: () => api.get<AttrType[]>("/attribute-types") });
  const values = useQuery({ queryKey: ["product_attributes"], queryFn: () => api.get<AttrValue[]>("/product-attributes") });
  const [active, setActive] = useState<string>("");
  const [showNewType, setShowNewType] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newValue, setNewValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const typeList = types.data ?? [];
  const valueList = values.data ?? [];
  const effectiveActive = active || typeList[0]?.key || "";
  const itemsOfType = useMemo(() => valueList.filter((v) => v.type === effectiveActive), [valueList, effectiveActive]);
  const currentType = typeList.find((t) => t.key === effectiveActive);

  function slugify(s: string) {
    return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  }

  async function addType() {
    const key = newKey.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    const label = newLabel.trim();
    if (!key || !label) { setError("Nhập đủ mã và tên hiển thị"); return; }
    setError(null);
    try {
      const order = (typeList.at(-1)?.sort_order ?? 0) + 1;
      await api.post("/attribute-types", { key, label, sort_order: order });
      setNewKey(""); setNewLabel(""); setShowNewType(false); setActive(key);
      qc.invalidateQueries({ queryKey: ["attribute_types"] });
    } catch (e) { setError(getErrorMessage(e, "Tạo thất bại")); }
  }
  async function renameType(t: AttrType) {
    const v = prompt("Đổi tên loại:", t.label);
    if (!v || v === t.label) return;
    await api.put(`/attribute-types/${t.id}`, { label: v.trim() });
    qc.invalidateQueries({ queryKey: ["attribute_types"] });
  }
  async function deleteType(t: AttrType) {
    const used = valueList.filter((v) => v.type === t.key).length;
    if (!(await confirmAsync(`Xoá loại "${t.label}"?${used ? `\n${used} giá trị bên trong cũng sẽ mất.` : ""}`))) return;
    await api.del(`/attribute-types/${t.id}`);
    qc.invalidateQueries({ queryKey: ["attribute_types"] });
    qc.invalidateQueries({ queryKey: ["product_attributes"] });
    if (effectiveActive === t.key) setActive("");
  }
  async function addValue() {
    if (!effectiveActive || !newValue.trim()) return;
    const label = newValue.trim();
    const value = slugify(label) + "-" + Math.random().toString(36).slice(2, 5);
    const order = (itemsOfType.at(-1)?.sort_order ?? 0) + 1;
    try {
      await api.post("/product-attributes", { type: effectiveActive, value, label, sort_order: order });
      setNewValue("");
      qc.invalidateQueries({ queryKey: ["product_attributes"] });
    } catch (e) { setError(getErrorMessage(e, "Tạo thất bại")); }
  }
  async function renameValue(v: AttrValue) {
    const lab = prompt("Đổi tên giá trị:", v.label);
    if (!lab || lab === v.label) return;
    await api.put(`/product-attributes/${v.id}`, { label: lab.trim() });
    qc.invalidateQueries({ queryKey: ["product_attributes"] });
  }
  async function deleteValue(v: AttrValue) {
    if (!(await confirmAsync(`Xoá giá trị "${v.label}"?`))) return;
    await api.del(`/product-attributes/${v.id}`);
    qc.invalidateQueries({ queryKey: ["product_attributes"] });
  }

  return (
    <section className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Quản lý danh sách gợi ý dùng trong sản phẩm. Có thể tự tạo loại mới (vd: <em>sự kiện</em>, <em>kích thước</em>…) và thêm giá trị.
      </p>

      {error ? <ErrorBox message={error} /> : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        {/* Left: types */}
        <div className="rounded-lg border border-border/60 bg-card">
          <div className="flex items-center justify-between border-b border-border/60 px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Loại thuộc tính</span>
            <GhostBtn onClick={() => setShowNewType(true)} className="!py-0.5 text-xs">+ Thêm</GhostBtn>
          </div>
          <ul className="p-1">
            {typeList.map((t) => {
              const count = valueList.filter((v) => v.type === t.key).length;
              const isActive = effectiveActive === t.key;
              return (
                <li key={t.id} className={`group flex items-center gap-1 rounded-md px-2 py-1.5 ${isActive ? "bg-primary/15" : "hover:bg-accent"}`}>
                  <button onClick={() => setActive(t.key)} className="flex flex-1 items-center gap-2 text-left text-sm">
                    <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className={isActive ? "font-semibold" : ""}>{t.label}</span>
                    <span className="text-[10px] text-muted-foreground">({count})</span>
                  </button>
                  <button type="button" onClick={() => renameType(t)} className="text-xs text-muted-foreground opacity-0 hover:text-foreground group-hover:opacity-100">Đổi</button>
                  <button type="button" onClick={() => deleteType(t)} className="text-xs text-destructive opacity-0 hover:underline group-hover:opacity-100">Xoá</button>
                </li>
              );
            })}
            {typeList.length === 0 && !types.isFetching ? (
              <li className="p-4 text-center text-xs text-muted-foreground">Chưa có loại nào</li>
            ) : null}
          </ul>
        </div>

        {/* Right: values */}
        <div className="rounded-lg border border-border/60 bg-card p-4">
          {!currentType ? (
            <div className="grid place-items-center py-16 text-sm text-muted-foreground">Chọn một loại bên trái</div>
          ) : (
            <>
              <div className="mb-3 flex items-baseline justify-between">
                <div>
                  <h2 className="text-base font-bold">{currentType.label}</h2>
                  <p className="text-[11px] text-muted-foreground">Mã: <code>{currentType.key}</code></p>
                </div>
              </div>
              <div className="flex gap-2">
                <Input value={newValue} onChange={(e) => setNewValue(e.target.value)}
                  placeholder={`Thêm giá trị mới cho "${currentType.label}"…`}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addValue(); } }} />
                <PrimaryBtn onClick={addValue} disabled={!newValue.trim()}>+ Thêm</PrimaryBtn>
              </div>
              {itemsOfType.length === 0 ? (
                <div className="mt-4 rounded-md border border-dashed border-border/60 p-8 text-center text-sm text-muted-foreground">
                  Chưa có giá trị. Thêm ở trên.
                </div>
              ) : (
                <ul className="mt-3 divide-y divide-border/40 overflow-hidden rounded-md border border-border/60">
                  {itemsOfType.map((v) => (
                    <li key={v.id} className="flex items-center gap-2 bg-card px-3 py-2 hover:bg-muted/30">
                      <span className="flex-1 text-sm">{v.label}</span>
                      <code className="text-[10px] text-muted-foreground">{v.value}</code>
                      <button type="button" onClick={() => renameValue(v)} className="text-xs text-primary hover:underline">Sửa</button>
                      <button type="button" onClick={() => deleteValue(v)} className="text-xs text-destructive hover:underline">Xoá</button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>

      {showNewType ? (
        <Modal title="Thêm loại thuộc tính" onClose={() => setShowNewType(false)}>
          <div className="space-y-3">
            <label className="block text-sm">Tên hiển thị
              <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="VD: Sự kiện, Kích thước" className="mt-1" />
            </label>
            <label className="block text-sm">Mã định danh
              <Input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="event_type, size..." className="mt-1" />
              <p className="mt-1 text-[10px] text-muted-foreground">Chỉ a-z, 0-9, gạch dưới. Không đổi được sau khi tạo.</p>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <GhostBtn onClick={() => setShowNewType(false)}>Huỷ</GhostBtn>
              <PrimaryBtn onClick={addType}>Tạo</PrimaryBtn>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}

/* ============================================================ K9 Phase 2 — Cross-link helpers
 * Map: product_id → Task[], inventory_item_id → Task[].
 * Tải tất cả task_projects → fetch tasks từng project song song → gộp lại.
 */
type ProjectLite = { id: string; name: string };
type TaskLinkMaps = {
  byProduct: Map<string, Task[]>;
  byInventory: Map<string, Task[]>;
  projectName: Map<string, string>;
  allTasks: Task[];
  isLoading: boolean;
};

function useTaskCrossLinks(): TaskLinkMaps {
  const projectsQ = useQuery({
    queryKey: ["task_projects"],
    queryFn: () => api.get<ProjectLite[]>("/tasks/projects"),
  });
  const projects = projectsQ.data ?? [];
  const allTasksQ = useQuery({
    queryKey: ["tasks", "cross-link-all", projects.map((p) => p.id).join(",")],
    enabled: projects.length > 0,
    queryFn: async () => {
      const lists = await Promise.all(
        projects.map((p) => api.get<Task[]>("/tasks", { project_id: p.id }).catch(() => [] as Task[]))
      );
      return lists.flat();
    },
  });

  const allTasks = allTasksQ.data ?? [];
  const byProduct = new Map<string, Task[]>();
  const byInventory = new Map<string, Task[]>();
  const projectName = new Map<string, string>();
  for (const p of projects) projectName.set(p.id, p.name);
  for (const t of allTasks) {
    for (const pid of t.product_ids ?? []) {
      const arr = byProduct.get(pid) ?? []; arr.push(t); byProduct.set(pid, arr);
    }
    // Hỗ trợ cả cột mới và legacy custom_values.__rental_items
    const invIds = new Set<string>();
    for (const id of t.inventory_item_ids ?? []) invIds.add(id);
    const legacy = (t.custom_values as any)?.[RENTAL_ITEMS_KEY];
    if (Array.isArray(legacy)) for (const id of legacy) invIds.add(String(id));
    for (const id of invIds) {
      const arr = byInventory.get(id) ?? []; arr.push(t); byInventory.set(id, arr);
    }
  }
  return { byProduct, byInventory, projectName, allTasks, isLoading: projectsQ.isLoading || allTasksQ.isLoading };
}

/** Badge nhỏ hiển thị "N task" + tooltip liệt kê tên task. */
function RelatedTasksBadge({ tasks, onNavigate }: { tasks: Task[]; onNavigate?: () => void }) {
  if (!tasks || tasks.length === 0) return null;
  const TITLE = tasks.map((t) => `• ${t.title}${t.status === "done" ? " (xong)" : ""}`).join("\n");
  const doneCount = tasks.filter((t) => t.status === "done").length;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onNavigate?.(); }}
      title={TITLE}
      className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary hover:bg-primary/20"
    >
      <KanbanSquare className="h-3 w-3" />
      {tasks.length} task{doneCount > 0 ? ` · ${doneCount} ✓` : ""}
    </button>
  );
}



function ReportsPage() {
  const [tab, setTab] = useState<"finance" | "inventory" | "tasks">(() => {
    if (typeof window === "undefined") return "finance";
    const v = window.localStorage.getItem("nana.reportsTab");
    return v === "inventory" || v === "tasks" ? v : "finance";
  });
  useEffect(() => {
    try { window.localStorage.setItem("nana.reportsTab", tab); } catch {}
  }, [tab]);

  const [preset, setPreset] = useState<FinancePeriod>("month");
  const { from, to } = useMemo(() => finComputeRange(preset), [preset]);
  const [stream] = useCurrentStream();
  const [kpiMode, setKpiMode] = useState<"growth" | "margin">(() => {
    if (typeof window === "undefined") return "growth";
    const v = window.localStorage.getItem("nana.reportsKpiMode");
    return v === "margin" ? "margin" : "growth";
  });
  useEffect(() => {
    try { window.localStorage.setItem("nana.reportsKpiMode", kpiMode); } catch {}
  }, [kpiMode]);
  const prevRange = useMemo(() => prevRangeFor(preset), [preset]);
  const [ordersPeriod, setOrdersPeriod] = useState<"week" | "month" | "quarter" | "year">("month");

  const finance = useQuery({
    queryKey: ["finance", from, to, stream],
    queryFn: () => api.get<FinanceEntry[]>("/finance", { from, to, stream }),
    enabled: tab === "finance",
  });
  const financePrev = useQuery({
    queryKey: ["finance_prev", prevRange.from, prevRange.to, stream],
    queryFn: () => api.get<FinanceEntry[]>("/finance", { from: prevRange.from, to: prevRange.to, stream }),
    enabled: tab === "finance" && preset !== "all",
  });
  const inv = useQuery({
    queryKey: ["inventory"],
    queryFn: () => api.get<InventoryItem[]>("/inventory"),
    enabled: tab === "inventory" || tab === "tasks",
  });
  const tasks = useQuery({
    queryKey: ["tasks_all_reports"],
    queryFn: () => api.get<Task[]>("/tasks"),
    enabled: tab === "tasks",
  });
  const projects = useQuery({
    queryKey: ["projects_reports"],
    queryFn: () => api.get<Project[]>("/projects"),
    enabled: tab === "tasks",
  });

  // Guard: chỉ tính entries đúng dòng đang xem — chặn DB cũ làm lẫn doanh thu.
  const entries = useMemo(
    () => (finance.data ?? []).filter((e) => (e.revenue_stream ?? "side") === stream),
    [finance.data, stream],
  );
  const entriesPrev = useMemo(
    () => (financePrev.data ?? []).filter((e) => (e.revenue_stream ?? "side") === stream),
    [financePrev.data, stream],
  );
  const inventory = inv.data ?? [];
  const allTasks = tasks.data ?? [];

  const totals = useMemo(() => {
    let income = 0, expense = 0;
    for (const e of entries) {
      if (e.kind === "income") income += Number(e.amount || 0);
      else expense += Number(e.amount || 0);
    }
    return { income, expense, profit: income - expense };
  }, [entries]);
  const totalsPrev = useMemo(() => {
    let income = 0, expense = 0;
    for (const e of entriesPrev) {
      if (e.kind === "income") income += Number(e.amount || 0);
      else expense += Number(e.amount || 0);
    }
    return { income, expense, profit: income - expense };
  }, [entriesPrev]);

  const growth = useMemo(() => {
    const pct = (curr: number, prev: number): number | null => {
      if (!prev) return curr > 0 ? 100 : null;
      return ((curr - prev) / Math.abs(prev)) * 100;
    };
    return {
      income: pct(totals.income, totalsPrev.income),
      expense: pct(totals.expense, totalsPrev.expense),
      profit: pct(totals.profit, totalsPrev.profit),
    };
  }, [totals, totalsPrev]);
  const margin = useMemo(() => {
    const curr = totals.income > 0 ? (totals.profit / totals.income) * 100 : null;
    const prev = totalsPrev.income > 0 ? (totalsPrev.profit / totalsPrev.income) * 100 : null;
    return { curr, prev };
  }, [totals, totalsPrev]);

  const daily = useMemo(() => {
    const map = new Map<string, { date: string; income: number; expense: number }>();
    for (const e of entries) {
      const d = e.occurred_on ?? "";
      if (!d) continue;
      const row = map.get(d) ?? { date: d, income: 0, expense: 0 };
      if (e.kind === "income") row.income += Number(e.amount || 0);
      else row.expense += Number(e.amount || 0);
      map.set(d, row);
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [entries]);

  const byCategory = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of entries) {
      if (e.kind !== "income") continue;
      const k = e.category ?? "khac";
      map.set(k, (map.get(k) ?? 0) + Number(e.amount || 0));
    }
    return Array.from(map.entries()).map(([k, v]) => ({ category: FIN_CATEGORY_LABEL[k] ?? k, amount: v }));
  }, [entries]);

  const expenseByCategory = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of entries) {
      if (e.kind !== "expense") continue;
      const k = e.category ?? "khac";
      map.set(k, (map.get(k) ?? 0) + Number(e.amount || 0));
    }
    return Array.from(map.entries())
      .map(([k, v]) => ({ name: FIN_CATEGORY_LABEL[k] ?? k, value: v }))
      .sort((a, b) => b.value - a.value);
  }, [entries]);

  // Đếm đơn theo danh mục × bucket cho dòng hiện tại.
  const ordersByCategory = useMemo(() => {
    const cats = stream === "main" ? ORDER_CATEGORIES_MAIN : ORDER_CATEGORIES_SIDE;
    const projectById = new Map((projects.data ?? []).map((p) => [p.id, p]));
    const invById = new Map((inventory ?? []).map((i) => [i.id, i]));
    // last 8 buckets
    const now = new Date();
    const buckets: { label: string; start: Date }[] = [];
    for (let i = 7; i >= 0; i--) {
      const d = new Date(now);
      if (ordersPeriod === "week") d.setDate(d.getDate() - i * 7);
      else if (ordersPeriod === "month") d.setMonth(d.getMonth() - i);
      else if (ordersPeriod === "quarter") d.setMonth(d.getMonth() - i * 3);
      else d.setFullYear(d.getFullYear() - i);
      buckets.push(bucketFor(d, ordersPeriod));
    }
    const labels = buckets.map((b) => b.label);
    const rows: Record<string, number | string>[] = labels.map((l) => ({ label: l }));
    for (const t of allTasks) {
      const cat = classifyTaskForOrders(t, projectById, invById, stream);
      if (!cat) continue;
      const dateStr = t.due_date ?? t.start_date ?? t.completed_at ?? null;
      if (!dateStr) continue;
      const dt = new Date(dateStr);
      if (isNaN(dt.getTime())) continue;
      const b = bucketFor(dt, ordersPeriod);
      const idx = labels.indexOf(b.label);
      if (idx < 0) continue;
      rows[idx][cat] = ((rows[idx][cat] as number) ?? 0) + 1;
    }
    // ensure all keys present
    for (const r of rows) for (const c of cats) if (r[c.key] == null) r[c.key] = 0;
    return { rows, cats: [...cats] };
  }, [allTasks, projects.data, inventory, stream, ordersPeriod]);

  const invStats = useMemo(() => {
    const total = inventory.length;
    const inUse = inventory.filter((i) => !i.available).length;
    const available = total - inUse;
    const byCat: Record<string, { total: number; inUse: number }> = {};
    for (const i of inventory) {
      const c = INV_CATEGORY_LABEL[i.category] ?? i.category;
      if (!byCat[c]) byCat[c] = { total: 0, inUse: 0 };
      byCat[c].total++;
      if (!i.available) byCat[c].inUse++;
    }
    return { total, inUse, available, byCat };
  }, [inventory]);

  const topRented = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of allTasks) {
      const cv = (t.custom_values ?? {}) as Record<string, unknown>;
      const items = (cv.__rental_items ?? cv._rented_items ?? cv._items) as unknown;
      if (Array.isArray(items)) {
        for (const it of items) {
          const name = typeof it === "string" ? it : (it as { name?: string } | null)?.name;
          if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
        }
      }
    }
    return Array.from(counts.entries()).map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count).slice(0, 10);
  }, [allTasks]);

  const stuck = useMemo(() => {
    const now = Date.now();
    return inventory.filter((i) => !i.available && i.updated_at && (now - new Date(i.updated_at).getTime()) > 14 * 86400_000).slice(0, 20);
  }, [inventory]);

  const taskKpis = useMemo(() => {
    const now = Date.now();
    let running = 0, done = 0, overdue = 0;
    for (const t of allTasks) {
      const s = (t.status ?? "").toLowerCase();
      if (s === "done" || t.completed_at) done++;
      else {
        running++;
        if (t.due_date && new Date(t.due_date).getTime() < now) overdue++;
      }
    }
    return { total: allTasks.length, running, done, overdue };
  }, [allTasks]);

  const PIE_COLORS = ["hsl(var(--primary))", "hsl(var(--muted-foreground))"];
  const BAR_COLORS = ["#10b981", "#f59e0b", "#3b82f6", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6"];

  const loading = finance.isFetching || inv.isFetching || tasks.isFetching;

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="inline-flex rounded-md border border-border bg-card p-1 gap-1">
          <ModeTab active={tab === "finance"} onClick={() => setTab("finance")}>Tài chính</ModeTab>
          <ModeTab active={tab === "inventory"} onClick={() => setTab("inventory")}>Kho cho thuê</ModeTab>
          <ModeTab active={tab === "tasks"} onClick={() => setTab("tasks")}>Tác vụ</ModeTab>
        </div>
        {tab === "finance" ? (
          <>
            <label className="text-sm">Khoảng thời gian
              <Select value={preset} onChange={(e) => setPreset(e.target.value as FinancePeriod)} className="mt-1 w-44">
                {FIN_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </Select>
            </label>
            <span className={`mb-1 inline-flex items-center gap-1 self-end rounded-md border px-2 py-1 text-xs ${stream === "main" ? "border-primary/40 bg-primary/10 text-primary" : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"}`}>
              Đang xem: <b>{streamLabel(stream)}</b>
            </span>
            <span className="pb-2 text-xs text-muted-foreground">{from} → {to}</span>
          </>
        ) : null}
        {loading ? <StatusText>Đang tải…</StatusText> : null}
      </div>

      {tab === "finance" ? (
        <>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Chỉ số so sánh:</span>
        <div className="inline-flex rounded-md border border-border bg-card p-0.5">
          <button type="button" onClick={() => setKpiMode("growth")} className={`rounded px-2.5 py-1 text-xs ${kpiMode === "growth" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted/60"}`}>% tăng kỳ trước</button>
          <button type="button" onClick={() => setKpiMode("margin")} className={`rounded px-2.5 py-1 text-xs ${kpiMode === "margin" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted/60"}`}>Biên độ dòng tiền</button>
        </div>
        {preset !== "all" ? <span className="text-xs text-muted-foreground">Kỳ trước: {prevRange.from} → {prevRange.to}</span> : null}
      </div>
      {/* KPI */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="flex items-center gap-3 rounded-md border border-border bg-card p-4">
          <div className="grid h-10 w-10 place-items-center rounded-md bg-emerald-500/15 text-emerald-600"><TrendingUp className="h-5 w-5" /></div>
          <div>
            <p className="text-xs text-muted-foreground">Doanh thu</p>
            <p className="text-xl font-bold">{formatMoney(totals.income)}</p>
            {kpiMode === "growth" ? (
              <p className={`text-xs ${(growth.income ?? 0) >= 0 ? "text-emerald-600" : "text-destructive"}`}>{fmtPct(growth.income)} vs kỳ trước</p>
            ) : (
              <p className="text-xs text-muted-foreground">Kỳ trước: {formatMoney(totalsPrev.income)}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-md border border-border bg-card p-4">
          <div className="grid h-10 w-10 place-items-center rounded-md bg-rose-500/15 text-destructive"><TrendingDown className="h-5 w-5" /></div>
          <div>
            <p className="text-xs text-muted-foreground">Chi phí</p>
            <p className="text-xl font-bold">{formatMoney(totals.expense)}</p>
            {kpiMode === "growth" ? (
              <p className={`text-xs ${(growth.expense ?? 0) <= 0 ? "text-emerald-600" : "text-destructive"}`}>{fmtPct(growth.expense)} vs kỳ trước</p>
            ) : (
              <p className="text-xs text-muted-foreground">Kỳ trước: {formatMoney(totalsPrev.expense)}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-md border border-border bg-card p-4">
          <div className={`grid h-10 w-10 place-items-center rounded-md ${totals.profit >= 0 ? "bg-primary/15 text-primary" : "bg-rose-500/15 text-destructive"}`}><Wallet className="h-5 w-5" /></div>
          <div>
            <p className="text-xs text-muted-foreground">Lợi nhuận</p>
            <p className={`text-xl font-bold ${totals.profit >= 0 ? "text-primary" : "text-destructive"}`}>{formatMoney(totals.profit)}</p>
            {kpiMode === "growth" ? (
              <p className={`text-xs ${(growth.profit ?? 0) >= 0 ? "text-emerald-600" : "text-destructive"}`}>{fmtPct(growth.profit)} vs kỳ trước</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Biên LN: <b>{margin.curr == null ? "—" : margin.curr.toFixed(1) + "%"}</b>
                {margin.prev != null ? <span className="text-muted-foreground"> · kỳ trước {margin.prev.toFixed(1)}%</span> : null}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Daily line */}
      <div className="rounded-md border border-border bg-card p-4">
        <div className="mb-3 text-sm font-semibold">Doanh thu vs Chi phí theo ngày</div>
        {daily.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Chưa có dữ liệu</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v: number) => `${Math.round(v / 1000)}k`} />
              <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} formatter={(v) => formatMoney(Number(v))} />
              <Legend />
              <Line type="monotone" dataKey="income" name="Doanh thu" stroke="#10b981" strokeWidth={2} />
              <Line type="monotone" dataKey="expense" name="Chi phí" stroke="#ef4444" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* By category */}
      <div className="rounded-md border border-border bg-card p-4">
        <div className="mb-3 text-sm font-semibold">Doanh thu theo danh mục</div>
        {byCategory.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Chưa có dữ liệu</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byCategory}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="category" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v: number) => `${Math.round(v / 1000)}k`} />
              <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} formatter={(v) => formatMoney(Number(v))} />
              <Bar dataKey="amount" name="Doanh thu" radius={[6, 6, 0, 0]}>
                {byCategory.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Pie chi phí */}
      <div className="rounded-md border border-border bg-card p-4">
        <div className="mb-3 text-sm font-semibold">Tỉ lệ chi phí theo danh mục</div>
        {expenseByCategory.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Chưa có chi phí</p>
        ) : (
          <div className="flex flex-col items-center gap-4 md:flex-row">
            <ResponsiveContainer width="55%" height={280}>
              <PieChart>
                <Pie data={expenseByCategory} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={100} label={(d) => {
                  const tot = expenseByCategory.reduce((s, x) => s + x.value, 0);
                  return tot ? `${Math.round((Number(d.value) / tot) * 100)}%` : "";
                }}>
                  {expenseByCategory.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                </Pie>
                <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} formatter={(v) => formatMoney(Number(v))} />
              </PieChart>
            </ResponsiveContainer>
            <ul className="flex-1 space-y-1 text-sm">
              {(() => { const tot = expenseByCategory.reduce((s, x) => s + x.value, 0); return expenseByCategory.map((row, i) => (
                <li key={row.name} className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: BAR_COLORS[i % BAR_COLORS.length] }} />{row.name}</span>
                  <span><b>{formatMoney(row.value)}</b> <span className="text-xs text-muted-foreground">({tot ? ((row.value / tot) * 100).toFixed(1) : "0"}%)</span></span>
                </li>
              )); })()}
            </ul>
          </div>
        )}
      </div>
        </>
      ) : null}

      {tab === "inventory" ? (
        <>
          <div className="rounded-md border border-border bg-card p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><Package className="h-4 w-4" /> Tình trạng kho cho thuê</div>
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="50%" height={180}>
                <PieChart>
                  <Pie data={[{ name: "Sẵn sàng", value: invStats.available }, { name: "Đang dùng", value: invStats.inUse }]}
                    cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value">
                    {PIE_COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                  </Pie>
                  <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Tổng món</span><span className="font-bold">{invStats.total}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Sẵn sàng</span><span className="font-bold text-emerald-600">{invStats.available}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Đang dùng</span><span className="font-bold text-amber-500">{invStats.inUse}</span></div>
                <div className="space-y-1 border-t border-border pt-2">
                  {Object.entries(invStats.byCat).map(([cat, s]) => (
                    <div key={cat} className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{cat}</span>
                      <span>{s.total - s.inUse}/{s.total} sẵn sàng</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {stuck.length > 0 ? (
            <div className="rounded-md border border-amber-500/40 bg-card p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-amber-500">
                <AlertTriangle className="h-4 w-4" /> Món bị kẹt &gt; 14 ngày ({stuck.length})
              </div>
              <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                {stuck.map((s) => {
                  const days = s.updated_at ? Math.floor((Date.now() - new Date(s.updated_at).getTime()) / 86400_000) : 0;
                  return (
                    <div key={s.id} className="flex items-center justify-between rounded bg-muted/40 p-2">
                      <div className="truncate">
                        <span className="font-medium">{s.name}</span>
                        {s.variant ? <span className="text-xs text-muted-foreground"> · {s.variant}</span> : null}
                      </div>
                      <span className="text-xs font-bold text-amber-500">{days} ngày</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {tab === "tasks" ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="rounded-md border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Tổng task</p>
              <p className="text-xl font-bold">{taskKpis.total}</p>
            </div>
            <div className="rounded-md border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Đang chạy</p>
              <p className="text-xl font-bold text-primary">{taskKpis.running}</p>
            </div>
            <div className="rounded-md border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Hoàn tất</p>
              <p className="text-xl font-bold text-emerald-600">{taskKpis.done}</p>
            </div>
            <div className="rounded-md border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Quá hạn</p>
              <p className={`text-xl font-bold ${taskKpis.overdue > 0 ? "text-destructive" : ""}`}>{taskKpis.overdue}</p>
            </div>
          </div>

          <div className="rounded-md border border-border bg-card p-4">
            <div className="mb-3 text-sm font-semibold">Top 10 món được thuê nhiều nhất</div>
            {topRented.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">Chưa có dữ liệu</p>
            ) : (
              <ol className="space-y-1.5 text-sm">
                {topRented.map((r, i) => (
                  <li key={r.name} className="flex items-center gap-2">
                    <span className="w-5 text-right text-muted-foreground">{i + 1}.</span>
                    <span className="flex-1 truncate">{r.name}</span>
                    <span className="font-bold text-primary">{r.count} lần</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="rounded-md border border-border bg-card p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-semibold">Số lượng đơn theo danh mục — {streamLabel(stream)}</div>
              <div className="inline-flex rounded-md border border-border bg-card p-0.5">
                {(["week", "month", "quarter", "year"] as const).map((p) => (
                  <button key={p} type="button" onClick={() => setOrdersPeriod(p)} className={`rounded px-2.5 py-1 text-xs ${ordersPeriod === p ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted/60"}`}>
                    {p === "week" ? "Tuần" : p === "month" ? "Tháng" : p === "quarter" ? "Quý" : "Năm"}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ordersByCategory.rows}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                <RTooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <Legend />
                {ordersByCategory.cats.map((c, i) => (
                  <Bar key={c.key} dataKey={c.key} name={c.label} fill={BAR_COLORS[i % BAR_COLORS.length]} radius={[4, 4, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : null}

    </section>
  );
}

/* SiteTextPage đã bị xoá — admin sửa văn bản trực tiếp trên main page qua EditModeToggle. */


/* ============================================================ Timeline */

type TimelinePost = {
  id: string;
  title: string;
  content: string | null;
  images: string[];
  author_id: string | null;
  created_at: string;
};

/* --- Gantt overview --- */
const GANTT_GROUPS: Array<{ key: string; label: string; color: string }> = [
  { key: "cho-thue", label: "Cho thuê cosplay", color: "hsl(280 70% 60%)" },
  { key: "lam-do",   label: "Làm props",        color: "hsl(25 90% 55%)" },
  { key: "chup-anh", label: "Chụp ảnh",         color: "hsl(200 80% 55%)" },
];

function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d: Date, n: number) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function daysBetween(a: Date, b: Date) { return Math.round((b.getTime() - a.getTime()) / 86400000); }
function fmtMonthVi(d: Date) { return d.toLocaleDateString("vi-VN", { month: "short", year: "numeric" }); }

function TimelineGantt() {
  const tasksQ = useQuery({
    queryKey: ["tasks"],
    queryFn: () => api.get<Task[]>("/tasks"),
    refetchInterval: 15000,
  });
  const projectsQ = useQuery({
    queryKey: ["task_projects"],
    queryFn: () => api.get<Project[]>("/tasks/projects"),
  });

  const [anchor, setAnchor] = useState(() => startOfMonth(new Date()));
  const [monthsToShow, setMonthsToShow] = useState(3);

  const rangeStart = anchor;
  const rangeEnd = addMonths(anchor, monthsToShow);
  const totalDays = daysBetween(rangeStart, rangeEnd);

  const months = useMemo(() => {
    const out: { label: string; days: number }[] = [];
    for (let i = 0; i < monthsToShow; i++) {
      const m = addMonths(anchor, i);
      const next = addMonths(anchor, i + 1);
      out.push({ label: fmtMonthVi(m), days: daysBetween(m, next) });
    }
    return out;
  }, [anchor, monthsToShow]);

  const projectGroupMap = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of projectsQ.data ?? []) m[p.id] = String(p.project_group ?? "");
    return m;
  }, [projectsQ.data]);

  const grouped = useMemo(() => {
    const g: Record<string, Task[]> = { "cho-thue": [], "lam-do": [], "chup-anh": [] };
    for (const t of tasksQ.data ?? []) {
      const gk = projectGroupMap[t.project_id];
      if (!gk || !g[gk]) continue;
      const s = t.start_date ? new Date(t.start_date) : t.due_date ? new Date(t.due_date) : null;
      const e = t.due_date ? new Date(t.due_date) : t.start_date ? new Date(t.start_date) : null;
      if (!s || !e) continue;
      if (e < rangeStart || s > rangeEnd) continue;
      g[gk].push(t);
    }
    return g;
  }, [tasksQ.data, projectGroupMap, rangeStart, rangeEnd]);

  const todayLeft = useMemo(() => {
    const today = new Date();
    const off = daysBetween(rangeStart, today);
    if (off < 0 || off > totalDays) return null;
    return (off / totalDays) * 100;
  }, [rangeStart, totalDays]);

  function barStyle(t: Task) {
    const s = new Date(t.start_date ?? t.due_date!);
    const e = new Date(t.due_date ?? t.start_date!);
    const startOff = Math.max(0, daysBetween(rangeStart, s));
    const endOff = Math.min(totalDays, daysBetween(rangeStart, e) + 1);
    const left = (startOff / totalDays) * 100;
    const width = Math.max(1.2, ((endOff - startOff) / totalDays) * 100);
    return { left: `${left}%`, width: `${width}%` };
  }

  const loading = tasksQ.isLoading || projectsQ.isLoading;

  return (
    <section className="overflow-hidden rounded-md border border-border bg-card">
      <header className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <CalendarDays className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">Tiến độ tổng — Cho thuê / Props / Chụp ảnh</h2>
        <div className="ml-auto flex items-center gap-1">
          <GhostBtn className="h-7 w-7 p-0" onClick={() => setAnchor(addMonths(anchor, -1))} title="Tháng trước">
            <ChevronLeft className="h-4 w-4" />
          </GhostBtn>
          <GhostBtn className="h-7 px-2 text-xs" onClick={() => setAnchor(startOfMonth(new Date()))}>Hôm nay</GhostBtn>
          <GhostBtn className="h-7 w-7 p-0" onClick={() => setAnchor(addMonths(anchor, 1))} title="Tháng sau">
            <ChevronRight className="h-4 w-4" />
          </GhostBtn>
          <select
            value={monthsToShow}
            onChange={(e) => setMonthsToShow(Number(e.target.value))}
            className="ml-2 h-7 rounded border border-border bg-background px-2 text-xs"
          >
            <option value={1}>1 tháng</option>
            <option value={3}>3 tháng</option>
            <option value={6}>6 tháng</option>
            <option value={12}>12 tháng</option>
          </select>
        </div>
      </header>

      {loading ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Đang tải…</div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="flex border-b border-border bg-muted/30 text-[11px] font-medium">
              <div className="w-44 shrink-0 border-r border-border px-3 py-1.5">Hạng mục</div>
              <div className="flex flex-1">
                {months.map((m, i) => (
                  <div key={i} className="border-r border-border/50 px-2 py-1.5 text-center" style={{ flex: `${m.days} 0 0` }}>
                    {m.label}
                  </div>
                ))}
              </div>
            </div>

            {GANTT_GROUPS.map(({ key, label, color }) => {
              const rows = grouped[key] ?? [];
              return (
                <div key={key} className="border-b border-border/50 last:border-b-0">
                  <div className="flex items-center bg-muted/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <span className="mr-2 inline-block h-2 w-2 rounded-full" style={{ background: color }} />
                    {label} <span className="ml-2 font-normal normal-case">({rows.length})</span>
                  </div>
                  {rows.length === 0 ? (
                    <div className="px-3 py-3 text-xs text-muted-foreground/70">Không có việc trong khoảng này.</div>
                  ) : rows.map((t) => (
                    <div key={t.id} className="group flex items-stretch hover:bg-accent/30">
                      <div className="w-44 shrink-0 truncate border-r border-border/50 px-3 py-2 text-xs" title={t.title}>
                        {t.title}
                      </div>
                      <div className="relative min-h-[34px] flex-1">
                        {todayLeft !== null && (
                          <div className="pointer-events-none absolute top-0 bottom-0 w-px bg-primary/60" style={{ left: `${todayLeft}%` }} />
                        )}
                        <div
                          className={`absolute top-1/2 flex h-5 -translate-y-1/2 items-center overflow-hidden whitespace-nowrap rounded-md px-2 text-[10px] font-medium text-white shadow-sm ${t.status === "done" ? "opacity-60" : ""}`}
                          style={{ ...barStyle(t), background: color }}
                          title={`${t.title}\n${t.start_date ?? "?"} → ${t.due_date ?? "?"}`}
                        >
                          {t.title}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

function TimelinePage({ user }: { user: PhpUser }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["timeline_posts"],
    queryFn: () => api.get<TimelinePost[]>("/timeline-posts"),
  });
  const posts = list.data ?? [];

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [imageDraft, setImageDraft] = useState("");
  const [saving, setSaving] = useState(false);

  function openNew() {
    setTitle(""); setContent(""); setImages([]); setImageDraft(""); setOpen(true);
  }

  function addImage() {
    const u = imageDraft.trim();
    if (!u) return;
    setImages((arr) => [...arr, u]);
    setImageDraft("");
  }

  function removeImage(i: number) {
    setImages((arr) => arr.filter((_, idx) => idx !== i));
  }

  function moveImage(i: number, dir: -1 | 1) {
    setImages((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const next = arr.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  async function save() {
    if (!title.trim()) return alert("Cần tiêu đề");
    setSaving(true);
    try {
      await api.post("/timeline-posts", {
        title: title.trim(),
        content: content.trim() || null,
        images,
        author_id: user.id,
      });
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["timeline_posts"] });
    } catch (e: any) {
      alert(e?.message ?? "Lỗi");
    } finally { setSaving(false); }
  }

  async function remove(id: string) {
    if (!(await confirmAsync("Xoá bài này?"))) return;
    try { await api.del(`/timeline-posts/${id}`); }
    catch (e: any) { return alert(e?.message ?? "Lỗi"); }
    qc.invalidateQueries({ queryKey: ["timeline_posts"] });
  }

  return (
    <section className="space-y-4">
      <header className="flex items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold">Quản lý Timeline</h1>
          <p className="text-sm text-muted-foreground">Đăng tiến độ công việc, hiển thị cho khách hàng có quyền xem.</p>
        </div>
        <PrimaryBtn onClick={openNew}><Plus className="mr-1 inline h-4 w-4" />Đăng bài</PrimaryBtn>
      </header>

      <TimelineGantt />

      {list.isLoading ? <StatusText>Đang tải…</StatusText> :
       list.isError ? <ErrorBox message={(list.error as Error)?.message ?? "Lỗi tải dữ liệu"} /> :
       posts.length === 0 ? (
        <p className="rounded-md border border-dashed border-border bg-card p-10 text-center text-sm text-muted-foreground">
          Chưa có bài nào. Bấm "Đăng bài" để bắt đầu.
        </p>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <article key={p.id} className="rounded-md border border-border bg-card p-4">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <h2 className="font-semibold">{p.title}</h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {p.created_at ? fmtDateTime(p.created_at) : ""} · {p.images.length} ảnh
                  </p>
                  {p.content ? (
                    <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/80">{p.content}</p>
                  ) : null}
                </div>
                <GhostBtn onClick={() => remove(p.id)} className="px-2 py-1 text-destructive" title="Xoá">
                  <Trash2 className="h-4 w-4" />
                </GhostBtn>
              </div>
              {p.images.length > 0 ? (
                <div className="mt-3 flex gap-1.5 overflow-x-auto">
                  {p.images.map((u, i) => (
                    <img key={i} src={u} alt="" className="h-20 w-20 shrink-0 rounded object-cover" />
                  ))}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}

      {open ? (
        <Modal title="Đăng tiến độ mới" onClose={() => setOpen(false)}>
          <div className="space-y-3">
            <label className="block">
              <span className="text-xs font-medium">Tiêu đề *</span>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="vd: Hoàn thiện vũ khí cho khách A" />
            </label>
            <label className="block">
              <span className="text-xs font-medium">Nội dung</span>
              <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={5} placeholder="Mô tả tiến độ, chất liệu, ghi chú..." />
            </label>
            <div>
              <span className="text-xs font-medium">Ảnh ({images.length})</span>
              <div className="mt-1 flex flex-wrap gap-2">
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border bg-card px-2 py-1.5 text-xs hover:bg-muted">
                  <Plus className="h-3 w-3" />
                  <span>{saving ? "Đang upload…" : "Upload từ máy → Piwigo"}</span>
                  <input
                    type="file" accept="image/*" multiple className="hidden"
                    disabled={saving}
                    onChange={async (e) => {
                      const list = e.target.files; if (!list || list.length === 0) return;
                      setSaving(true);
                      const ok: string[] = []; const warn: string[] = [];
                      for (let i = 0; i < list.length; i++) {
                        const f = list[i];
                        const fd = new FormData(); fd.append("file", f, f.name);
                        try {
                          const r = await api.upload<{ url: string; warnings?: string[] }>("/timeline-posts/upload", fd);
                          if (r?.url) ok.push(r.url);
                          if (Array.isArray(r?.warnings)) for (const w of r.warnings) warn.push(`${f.name}: ${w}`);
                        } catch (err) { warn.push(`${f.name}: ${getErrorMessage(err, "lỗi")}`); }
                      }
                      setImages((arr) => [...arr, ...ok]);
                      if (warn.length) alert("Cảnh báo:\n" + warn.slice(0, 5).join("\n"));
                      setSaving(false);
                      e.target.value = "";
                    }}
                  />
                </label>
                <Input
                  value={imageDraft}
                  onChange={(e) => setImageDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addImage(); } }}
                  placeholder="hoặc dán URL ảnh có sẵn rồi bấm +"
                  className="flex-1 min-w-[200px]"
                />
                <GhostBtn onClick={addImage} className="shrink-0"><Plus className="h-4 w-4" /></GhostBtn>
              </div>
              {images.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {images.map((u, i) => (
                    <div key={i} className="relative">
                      <img src={u} alt="" className="h-16 w-16 rounded border border-border object-cover" />
                      <div className="absolute -top-1 -right-1 flex gap-0.5">
                        <button type="button" onClick={() => moveImage(i, -1)} className="rounded-full bg-background/90 p-0.5 text-[10px] shadow" title="Trái">‹</button>
                        <button type="button" onClick={() => moveImage(i, 1)} className="rounded-full bg-background/90 p-0.5 text-[10px] shadow" title="Phải">›</button>
                        <button type="button" onClick={() => removeImage(i)} className="rounded-full bg-destructive p-0.5 text-[10px] text-white shadow" title="Xoá">×</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <GhostBtn onClick={() => setOpen(false)}>Huỷ</GhostBtn>
              <PrimaryBtn onClick={save} disabled={saving}>{saving ? "Đang đăng…" : "Đăng"}</PrimaryBtn>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}


/* ============================================================ Users */

type UserRow = {
  id: string;
  email: string;
  created_at?: string | null;
  last_sign_in_at?: string | null;
  roles: string[];
};



function UsersPage({ user }: { user: PhpUser }) {
  const list = useQuery({
    queryKey: ["admin_users"],
    queryFn: async () => {
      const res = await api.get<{ users: UserRow[] }>("/users");
      return res.users ?? [];
    },
  });
  const users = list.data ?? [];

  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<"all" | "admin" | "user">("all");

  const stats = useMemo(() => {
    const admins = users.filter((u) => u.roles.includes("admin")).length;
    return { total: users.length, admins, customers: users.length - admins };
  }, [users]);

  const filtered = useMemo(() => {
    const needle = normVi(q.trim());
    return users.filter((u) => {
      if (filter === "admin" && !u.roles.includes("admin")) return false;
      if (filter === "user" && u.roles.includes("admin")) return false;
      if (needle && !normVi(u.email).includes(needle)) return false;
      return true;
    });
  }, [users, q, filter]);

  return (
    <section className="space-y-4">
      <header>
        <h1 className="text-xl font-bold">Quản lý người dùng</h1>
        <p className="text-sm text-muted-foreground">Danh sách tài khoản truy cập hệ thống.</p>
      </header>

      <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
        Chế độ self-host (php-api) chưa hỗ trợ mời / đổi quyền / xoá tài khoản qua UI. Vui lòng thao tác trực tiếp trên DB.
      </div>

      <div className="grid grid-cols-3 gap-3">
        <UsersStatCard label="Tổng tài khoản" value={stats.total} active={filter === "all"} onClick={() => setFilter("all")} />
        <UsersStatCard label="Admin" value={stats.admins} active={filter === "admin"} accent onClick={() => setFilter("admin")} />
        <UsersStatCard label="Customer" value={stats.customers} active={filter === "user"} onClick={() => setFilter("user")} />
      </div>

      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm theo email..." />

      {list.isLoading ? <StatusText>Đang tải…</StatusText> :
       list.isError ? <ErrorBox message={(list.error as Error)?.message ?? "Lỗi tải dữ liệu"} /> :
       filtered.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-card p-10 text-center text-sm text-muted-foreground">
          {q ? `Không tìm thấy tài khoản nào khớp "${q}"` : "Chưa có tài khoản nào trong bộ lọc này."}
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border bg-card">
          {filtered.map((u) => {
            const isAdmin = u.roles.includes("admin");
            const isSelf = u.id === user.id;
            return (
              <li key={u.id} className="flex items-center gap-3 p-4">
                <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${isAdmin ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                  <Users className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-medium">{u.email}</p>
                    {isSelf ? <span className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">Bạn</span> : null}
                    <span className={`rounded px-1.5 py-0.5 text-[10px] ${isAdmin ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                      {isAdmin ? "Admin" : "Customer"}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {u.last_sign_in_at
                      ? `Lần cuối ${fmtDate(u.last_sign_in_at)}`
                      : "Chưa từng đăng nhập"}
                    {u.created_at ? ` · Tạo ${fmtDate(u.created_at)}` : ""}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function UsersStatCard({ label, value, active, accent, onClick }: {
  label: string; value: number; active: boolean; accent?: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border p-4 text-left transition ${active ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border bg-card hover:bg-muted"}`}
    >
      <div className={`text-xs font-medium ${accent ? "text-primary" : "text-muted-foreground"}`}>{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
    </button>
  );
}

/* ============================================================ Media (Thư viện ảnh) */

type Media = {
  id: string;
  url: string;
  path: string | null;
  title: string | null;
  tags: string[];
  sort_order: number;
  folder: string;
  created_at?: string | null;
};

function mediaSanitizeFolderSegment(s: string): string {
  return (s ?? "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d").replace(/Đ/g, "D")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function MediaPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["media"],
    queryFn: () => api.get<Media[]>("/media"),
  });
  const items = list.data ?? [];

  const [q, setQ] = useState("");
  const [currentFolder, setCurrentFolder] = useState("");
  const [selected, setSelected] = useState<Media | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  // Multi-select + ZIP download
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState(false);
  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function sanitizeFile(s: string): string {
    return (s ?? "").replace(/[\\/:*?"<>|]+/g, "_").trim().slice(0, 120) || "untitled";
  }
  async function downloadSelected() {
    const picks = items.filter((i) => selectedIds.has(i.id));
    if (picks.length === 0) return;
    setDownloading(true);
    try {
      const zip = new JSZip();
      let okCount = 0, failCount = 0;
      for (const m of picks) {
        try {
          const res = await fetch(m.url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const ext = ((m.url.split(".").pop() ?? "jpg").split("?")[0]).slice(0, 5);
          const base = sanitizeFile(m.title ?? m.id);
          const folder = m.folder || "_goc";
          zip.file(`${folder}/${base}.${ext}`, blob);
          const txt = [
            `Tiêu đề: ${m.title ?? "(không tên)"}`,
            `Thư mục: ${m.folder || "(gốc)"}`,
            `Tags: ${m.tags.join(", ") || "(không)"}`,
            `URL: ${m.url}`,
            `Thứ tự sắp xếp: ${m.sort_order}`,
            `ID: ${m.id}`,
          ].join("\n");
          zip.file(`${folder}/${base}.txt`, txt);
          okCount++;
        } catch (e) { console.warn("Skip", m.url, e); failCount++; }
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      mediaSaveAs(blob, `thu-vien-anh-${stamp}.zip`);
      alert(`Đã tải ${okCount} ảnh${failCount ? ` (lỗi ${failCount})` : ""}.`);
    } catch (e: any) {
      alert(e?.message ?? "Tải về thất bại");
    } finally { setDownloading(false); }
  }

  // Sync ảnh từ sản phẩm + timeline
  const [syncing, setSyncing] = useState(false);
  async function syncFromProducts(silent = false) {
    setSyncing(true);
    try {
      const r = await api.post<{ added: number }>("/media/sync-from-products", {});
      if (!silent) {
        if ((r?.added ?? 0) === 0) alert("Thư viện đã đồng bộ, không có ảnh mới.");
        else alert(`Đã thêm ${r.added} ảnh mới vào thư viện.`);
      }
      qc.invalidateQueries({ queryKey: ["media"] });
    } catch (e: any) {
      if (!silent) alert(e?.message ?? "Đồng bộ thất bại");
      else console.warn("Auto-sync media:", e);
    } finally { setSyncing(false); }
  }
  // Tự động đồng bộ lần đầu khi vào trang (sau khi đã load xong)
  const [autoSynced, setAutoSynced] = useState(false);
  useEffect(() => {
    if (!list.isLoading && !autoSynced && !syncing) {
      setAutoSynced(true);
      void syncFromProducts(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.isLoading, autoSynced]);

  const folderTree = useMemo(() => {
    const set = new Set<string>();
    for (const it of items) {
      const f = it.folder ?? "";
      if (!f) continue;
      const parts = f.split("/").filter(Boolean);
      for (let i = 1; i <= parts.length; i++) set.add(parts.slice(0, i).join("/"));
    }
    return Array.from(set).sort();
  }, [items]);

  const childFolders = useMemo(() => {
    const prefix = currentFolder ? currentFolder + "/" : "";
    const direct = new Set<string>();
    for (const f of folderTree) {
      if (currentFolder === ""
        ? !f.includes("/")
        : (f.startsWith(prefix) && !f.slice(prefix.length).includes("/"))) {
        direct.add(f);
      }
    }
    return Array.from(direct).sort();
  }, [folderTree, currentFolder]);

  const filtered = useMemo(() => {
    const needle = normVi(q.trim());
    const base = needle
      ? items.filter((i) =>
          normVi(i.title ?? "").includes(needle) ||
          i.tags.some((t) => normVi(t).includes(needle)) ||
          normVi(i.folder ?? "").includes(needle)
        )
      : items.filter((i) => (i.folder ?? "") === currentFolder);
    return [...base].sort((a, b) => (b.sort_order ?? 0) - (a.sort_order ?? 0));
  }, [items, q, currentFolder]);

  const reorder = useMutation({
    mutationFn: (updates: { id: string; sort_order: number }[]) =>
      api.post("/media/reorder", { updates }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media"] }),
  });

  // DnD-kit sensors + handler để kéo-thả sắp xếp ảnh
  const mediaDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );
  function onMediaDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = filtered.findIndex((i) => i.id === active.id);
    const newIdx = filtered.findIndex((i) => i.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(filtered, oldIdx, newIdx);
    const n = reordered.length;
    const updates = reordered.map((m, i) => ({ id: m.id, sort_order: n - i }));
    reorder.mutate(updates);
  }

  async function remove(item: Media) {
    try { await api.del(`/media/${item.id}`); }
    catch (e: any) { return alert(e?.message ?? "Lỗi"); }
    setSelected(null);
    qc.invalidateQueries({ queryKey: ["media"] });
  }

  function createFolder() {
    const name = prompt(`Tên thư mục mới trong "${currentFolder || "/"}":`);
    if (!name) return;
    const seg = mediaSanitizeFolderSegment(name);
    if (!seg) return alert("Tên không hợp lệ");
    const full = currentFolder ? `${currentFolder}/${seg}` : seg;
    setCurrentFolder(full);
    setShowAdd(true);
  }

  const crumbs = currentFolder ? currentFolder.split("/") : [];

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold">Thư viện ảnh</h1>
          <p className="text-sm text-muted-foreground">Quản lý ảnh theo thư mục. Kéo-thả để sắp xếp, chọn nhiều ảnh để tải .zip, đồng bộ ảnh từ sản phẩm & timeline.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedIds.size > 0 ? (
            <>
              <span className="text-xs text-muted-foreground hidden sm:inline">Đã chọn {selectedIds.size}</span>
              <GhostBtn onClick={() => setSelectedIds(new Set())}>
                <X className="mr-1 inline h-4 w-4" />Bỏ chọn
              </GhostBtn>
              <PrimaryBtn onClick={downloadSelected} disabled={downloading}>
                <Download className={`mr-1 inline h-4 w-4 ${downloading ? "animate-spin" : ""}`} />
                {downloading ? "Đang tải…" : "Tải về (.zip)"}
              </PrimaryBtn>
            </>
          ) : null}
          <GhostBtn onClick={() => syncFromProducts(false)} disabled={syncing} title="Đồng bộ ảnh từ sản phẩm & timeline">
            <RefreshCw className={`mr-1 inline h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Đang đồng bộ…" : "Đồng bộ"}
          </GhostBtn>
          <GhostBtn onClick={createFolder}><FolderPlus className="mr-1 inline h-4 w-4" />Tạo thư mục</GhostBtn>
          <PrimaryBtn onClick={() => setShowAdd(true)}><Plus className="mr-1 inline h-4 w-4" />Thêm ảnh</PrimaryBtn>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
        <aside className="self-start rounded-md border border-border bg-card p-2">
          <div className="px-2 py-1.5 text-[11px] font-semibold uppercase text-muted-foreground">Cây thư mục</div>
          <button
            type="button"
            onClick={() => { setCurrentFolder(""); setQ(""); }}
            className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-sm ${currentFolder === "" ? "bg-primary/15 text-primary" : "hover:bg-muted"}`}
          >
            <span className="inline-flex items-center gap-1.5"><Home className="h-3.5 w-3.5" />Tất cả ảnh</span>
            <span className="text-[10px] text-muted-foreground">{items.filter((i) => !i.folder).length}</span>
          </button>
          <MediaFolderTree
            folders={folderTree}
            items={items}
            current={currentFolder}
            onSelect={(p) => { setCurrentFolder(p); setQ(""); }}
          />
        </aside>

        <div>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Tìm theo tên, tag, hoặc thư mục..."
            className="mb-3"
          />

          {!q ? (
            <div className="mb-3 flex flex-wrap items-center gap-1 text-sm">
              <button type="button" onClick={() => setCurrentFolder("")} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
                <Home className="h-3.5 w-3.5" />Gốc
              </button>
              {crumbs.map((c, i) => {
                const path = crumbs.slice(0, i + 1).join("/");
                const last = i === crumbs.length - 1;
                return (
                  <span key={path} className="inline-flex items-center gap-1">
                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <button
                      type="button"
                      onClick={() => setCurrentFolder(path)}
                      className={last ? "font-semibold" : "text-muted-foreground hover:text-foreground"}
                    >{c}</button>
                  </span>
                );
              })}
            </div>
          ) : null}

          {!q && childFolders.length > 0 ? (
            <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
              {childFolders.map((f) => {
                const segName = f.split("/").pop();
                const count = items.filter((i) => (i.folder ?? "") === f || (i.folder ?? "").startsWith(f + "/")).length;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setCurrentFolder(f)}
                    className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-left hover:bg-muted"
                  >
                    <Folder className="h-4 w-4 shrink-0 text-primary" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{segName}</div>
                      <div className="text-[10px] text-muted-foreground">{count} ảnh</div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : null}

          {list.isLoading ? <StatusText>Đang tải…</StatusText> :
           list.isError ? <ErrorBox message={(list.error as Error)?.message ?? "Lỗi tải dữ liệu"} /> :
           items.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-card p-10 text-center">
              <ImageIcon className="mx-auto h-8 w-8 text-muted-foreground opacity-40" />
              <p className="mt-3 text-sm text-muted-foreground">Thư viện trống. Bấm "Thêm ảnh" để bắt đầu.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-card p-10 text-center text-sm text-muted-foreground">
              {q ? `Không tìm thấy ảnh nào khớp "${q}"` : "Thư mục này chưa có ảnh."}
            </div>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs text-muted-foreground">
                  {q ? `Tìm thấy ${filtered.length} ảnh khớp "${q}"` : `${filtered.length} ảnh trong "${currentFolder || "Gốc"}". Kéo-thả để sắp xếp.`}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    const allSelected = filtered.length > 0 && filtered.every((i) => selectedIds.has(i.id));
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (allSelected) filtered.forEach((i) => next.delete(i.id));
                      else filtered.forEach((i) => next.add(i.id));
                      return next;
                    });
                  }}
                  className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                >
                  {filtered.length > 0 && filtered.every((i) => selectedIds.has(i.id))
                    ? (<><CheckSquare className="h-3.5 w-3.5" />Bỏ chọn tất cả</>)
                    : (<><Square className="h-3.5 w-3.5" />Chọn tất cả ({filtered.length})</>)}
                </button>
              </div>
              <DndContext sensors={mediaDndSensors} collisionDetection={closestCenter} onDragEnd={onMediaDragEnd}>
                <SortableContext items={filtered.map((i) => i.id)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                    {filtered.map((m) => (
                      <MediaSortableCard
                        key={m.id}
                        item={m}
                        onClick={() => setSelected(m)}
                        showFolder={!!q}
                        selected={selectedIds.has(m.id)}
                        onToggleSelect={() => toggleSelect(m.id)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </>
          )}
        </div>
      </div>

      {selected ? (
        <MediaEditModal
          item={selected}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); qc.invalidateQueries({ queryKey: ["media"] }); }}
          onDelete={() => remove(selected)}
        />
      ) : null}

      {showAdd ? (
        <MediaAddModal
          defaultFolder={currentFolder}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); qc.invalidateQueries({ queryKey: ["media"] }); }}
        />
      ) : null}
    </section>
  );
}

function MediaFolderTree({ folders, items, current, onSelect }: {
  folders: string[]; items: Media[]; current: string; onSelect: (p: string) => void;
}) {
  if (folders.length === 0) return null;
  return (
    <div className="mt-1 space-y-0.5">
      {folders.map((f) => {
        const depth = f.split("/").length - 1;
        const count = items.filter((i) => (i.folder ?? "") === f).length;
        const active = current === f;
        return (
          <button
            key={f}
            type="button"
            onClick={() => onSelect(f)}
            className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-sm ${active ? "bg-primary/15 text-primary" : "hover:bg-muted"}`}
            style={{ paddingLeft: 8 + depth * 12 }}
          >
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <Folder className="h-3.5 w-3.5 shrink-0 opacity-70" />
              <span className="truncate">{f.split("/").pop()}</span>
            </span>
            <span className="text-[10px] text-muted-foreground">{count}</span>
          </button>
        );
      })}
    </div>
  );
}

function MediaSortableCard({ item, onClick, showFolder, selected, onToggleSelect }: {
  item: Media; onClick: () => void; showFolder?: boolean; selected?: boolean; onToggleSelect?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={`group relative aspect-square overflow-hidden rounded-md border bg-muted cursor-grab active:cursor-grabbing ${selected ? "border-primary ring-2 ring-primary" : "border-border"}`}
    >
      <img src={item.url} alt={item.title ?? ""} loading="lazy" decoding="async" className="h-full w-full object-cover" />
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onToggleSelect?.(); }}
        className={`absolute top-1.5 left-1.5 z-10 grid place-items-center h-6 w-6 rounded border transition ${selected ? "bg-primary border-primary text-primary-foreground opacity-100" : "bg-background/80 border-border/80 text-transparent hover:text-foreground opacity-0 group-hover:opacity-100"}`}
        aria-label={selected ? "Bỏ chọn" : "Chọn"}
      >
        <CheckSquare className="h-3.5 w-3.5" />
      </button>
      {showFolder && item.folder ? (
        <div className="absolute top-1.5 right-1.5 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded inline-flex items-center gap-1">
          <Folder className="h-2.5 w-2.5" />{item.folder}
        </div>
      ) : null}
      {item.title ? (
        <div className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1.5 py-0.5 text-[11px] text-white">
          {item.title}
        </div>
      ) : null}
    </div>
  );
}

function MediaAddModal({ defaultFolder, onClose, onCreated }: {
  defaultFolder: string; onClose: () => void; onCreated: () => void;
}) {
  const [mode, setMode] = useState<"upload" | "url">("upload");
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [folder, setFolder] = useState(defaultFolder);
  const [tags, setTags] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [fileKey, setFileKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function pick(list: FileList | null) {
    if (!list) return;
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
    const ok: File[] = []; const bad: string[] = [];
    for (const f of Array.from(list)) {
      if (!allowed.includes(f.type)) { bad.push(`${f.name} (định dạng)`); continue; }
      if (f.size > 20 * 1024 * 1024) { bad.push(`${f.name} (>20MB)`); continue; }
      ok.push(f);
    }
    setFiles(ok);
    setErr(bad.length > 0 ? "Bỏ qua: " + bad.join(", ") : null);
  }

  async function saveUrl() {
    if (!url.trim()) { setErr("Cần URL ảnh"); return; }
    setSaving(true); setErr(null);
    try {
      await api.post("/media", {
        url: url.trim(),
        title: title.trim() || null,
        folder: folder.trim(),
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      onCreated();
    } catch (e) {
      setErr(getErrorMessage(e, "Lỗi"));
    } finally { setSaving(false); }
  }

  async function saveUpload() {
    if (files.length === 0) { setErr("Chưa chọn file"); return; }
    setSaving(true); setErr(null);
    let okN = 0; const failed: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress(`Đang upload ${i + 1}/${files.length}: ${f.name}`);
      try {
        const fd = new FormData();
        fd.append("file", f, f.name);
        fd.append("folder", folder.trim());
        if (title.trim() && files.length === 1) fd.append("title", title.trim());
        if (tags.trim()) fd.append("tags", tags.trim());
        await api.upload("/media/upload", fd);
        okN++;
      } catch (e) {
        failed.push(`${f.name}: ${getErrorMessage(e, "lỗi")}`);
      }
    }
    setProgress(null);
    setSaving(false);
    if (failed.length === 0) {
      onCreated();
    } else {
      setErr(`Upload ${okN}/${files.length}. Lỗi: ${failed.slice(0, 3).join("; ")}`);
      setFiles([]); setFileKey((k) => k + 1);
      if (okN > 0) onCreated();
    }
  }

  return (
    <Modal title="Thêm ảnh" onClose={onClose}>
      <div className="space-y-3">
        <div className="flex gap-1 rounded-md border border-border p-0.5 text-xs">
          <button type="button" onClick={() => setMode("upload")}
            className={`flex-1 rounded px-2 py-1.5 ${mode === "upload" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
            Upload từ máy
          </button>
          <button type="button" onClick={() => setMode("url")}
            className={`flex-1 rounded px-2 py-1.5 ${mode === "url" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
            Dán URL
          </button>
        </div>

        {mode === "upload" ? (
          <label className="block">
            <span className="text-xs font-medium">Chọn ảnh (JPG/PNG/WebP/GIF ≤ 20MB)</span>
            <Input
              key={fileKey}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.gif"
              multiple
              onChange={(e) => pick(e.target.files)}
              disabled={saving}
            />
            {files.length > 0 ? (
              <p className="mt-1 text-[11px] text-muted-foreground">Đã chọn {files.length} file ({Math.round(files.reduce((s, f) => s + f.size, 0) / 1024)} KB)</p>
            ) : null}
          </label>
        ) : (
          <label className="block">
            <span className="text-xs font-medium">URL ảnh *</span>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." autoFocus />
          </label>
        )}

        <label className="block">
          <span className="text-xs font-medium">Tiêu đề {mode === "upload" && files.length > 1 ? "(bỏ qua khi upload nhiều)" : ""}</span>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs font-medium">Thư mục</span>
          <Input value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="vd: cosplay/hokage" />
        </label>
        <label className="block">
          <span className="text-xs font-medium">Tags (phân cách bởi dấu phẩy)</span>
          <Input value={tags} onChange={(e) => setTags(e.target.value)} />
        </label>

        {progress ? <p className="text-xs text-muted-foreground">{progress}</p> : null}
        {err ? <ErrorBox message={err} /> : null}

        <div className="flex justify-end gap-2 pt-1">
          <GhostBtn onClick={onClose} disabled={saving}>Huỷ</GhostBtn>
          <PrimaryBtn
            onClick={mode === "upload" ? saveUpload : saveUrl}
            disabled={saving || (mode === "upload" ? files.length === 0 : !url.trim())}
          >
            {saving ? "Đang lưu…" : mode === "upload" ? `Upload${files.length > 0 ? ` (${files.length})` : ""}` : "Thêm"}
          </PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}

function MediaEditModal({ item, onClose, onSaved, onDelete }: {
  item: Media; onClose: () => void; onSaved: () => void; onDelete: () => void;
}) {
  const [title, setTitle] = useState(item.title ?? "");
  const [folder, setFolder] = useState(item.folder ?? "");
  const [tags, setTags] = useState((item.tags ?? []).join(", "));
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.put(`/media/${item.id}`, {
        title: title.trim() || null,
        folder: folder.trim(),
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      onSaved();
    } catch (e: any) {
      alert(e?.message ?? "Lỗi");
    } finally { setSaving(false); }
  }

  return (
    <Modal title="Chi tiết ảnh" onClose={onClose}>
      <div className="space-y-3">
        <div className="overflow-hidden rounded-md border border-border bg-muted">
          <img src={item.url} alt={title} className="max-h-72 w-full object-contain" />
        </div>
        <label className="block">
          <span className="text-xs font-medium">Tiêu đề</span>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs font-medium">Thư mục</span>
          <Input value={folder} onChange={(e) => setFolder(e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs font-medium">Tags (phân cách bởi dấu phẩy)</span>
          <Input value={tags} onChange={(e) => setTags(e.target.value)} />
        </label>
        <div className="flex flex-wrap justify-between gap-2 pt-1">
          <ConfirmBtn message={`Xoá ảnh "${title || "không tên"}"?`} onConfirm={onDelete}>
            <Trash2 className="mr-1 inline h-3.5 w-3.5" />Xoá
          </ConfirmBtn>
          <div className="flex gap-2">
            <GhostBtn onClick={onClose}>Đóng</GhostBtn>
            <PrimaryBtn onClick={save} disabled={saving}>{saving ? "Đang lưu…" : "Lưu"}</PrimaryBtn>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* ============================================================ Phase H — Products */

type ProductRow = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  category: string | null;
  fandom: string | null;
  character_name: string | null;
  materials: string | null;
  price: string | null;
  mode: string;
  images: string[];
  cover_image: string | null;
  published: boolean;
  is_public: boolean;
  revenue_stream: "main" | "side";
  deposit_amount: number;
  internal_note: string | null;
  sort_order: number;
  attributes: Record<string, string>;
  created_at?: string;
  updated_at?: string;
  group_name?: string | null;
};

const PRODUCT_MODE_FALLBACK: Record<string, string> = {
  craft: "Hàng đặt", rental: "Cho thuê", sample: "Sample / Ảnh chụp", both: "Đặt & Thuê",
};
const PRODUCT_NATIVE_ATTRS = new Set(["category", "fandom", "material", "price_tier", "mode"]);

function productSlugify(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d").replace(/[^a-z0-9\s-]/g, "")
    .trim().replace(/\s+/g, "-").slice(0, 80) || `item-${Date.now()}`;
}

function normalizeProductRow(row: any): ProductRow {
  const parseList = (v: unknown): string[] => {
    if (Array.isArray(v)) return v.filter(Boolean).map(String);
    if (typeof v === "string" && v.trim()) {
      try { const p = JSON.parse(v); if (Array.isArray(p)) return p.filter(Boolean).map(String); } catch {}
    }
    return [];
  };
  const parseRec = (v: unknown): Record<string, string> => {
    if (v && typeof v === "object" && !Array.isArray(v)) return v as Record<string, string>;
    if (typeof v === "string" && v.trim()) {
      try { const p = JSON.parse(v); if (p && typeof p === "object" && !Array.isArray(p)) return p as Record<string, string>; } catch {}
    }
    return {};
  };
  return {
    id: String(row?.id ?? ""),
    slug: String(row?.slug ?? row?.id ?? ""),
    name: String(row?.name ?? ""),
    description: row?.description ?? null,
    category: row?.category ?? null,
    fandom: row?.fandom ?? null,
    character_name: row?.character_name ?? null,
    materials: row?.materials ?? null,
    price: row?.price ?? null,
    mode: String(row?.mode ?? "craft"),
    images: parseList(row?.images),
    cover_image: row?.cover_image ?? null,
    published: row?.published !== false && row?.published !== 0 && row?.published !== "0",
    is_public: row?.is_public !== false && row?.is_public !== 0 && row?.is_public !== "0",
    revenue_stream: (String(row?.revenue_stream ?? "main").toLowerCase() === "side" ? "side" : "main"),
    deposit_amount: Number(row?.deposit_amount ?? 0) || 0,
    internal_note: row?.internal_note ?? null,
    sort_order: Number(row?.sort_order ?? 0),
    attributes: parseRec(row?.attributes),
    created_at: row?.created_at,
    updated_at: row?.updated_at,
  };
}

function ProductsPage() {
  const qc = useQueryClient();
  const productsQ = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const rows = await api.get<any[]>("/products");
      return (rows ?? []).map(normalizeProductRow);
    },
  });
  const modesQ = useQuery({
    queryKey: ["product_attributes", "mode"],
    queryFn: () => api.get<AttrValue[]>("/product-attributes", { type: "mode" }),
  });
  const attrTypesQ = useQuery({
    queryKey: ["attribute_types"],
    queryFn: () => api.get<AttrType[]>("/attribute-types"),
  });
  const attrValuesQ = useQuery({
    queryKey: ["product_attributes"],
    queryFn: () => api.get<AttrValue[]>("/product-attributes"),
  });

  const products = productsQ.data ?? [];
  const modeOpts = useMemo<{ value: string; label: string }[]>(() => {
    const m = (modesQ.data ?? []).map((m) => ({ value: m.value, label: m.label }));
    if (m.length) return m;
    return Object.entries(PRODUCT_MODE_FALLBACK).map(([value, label]) => ({ value, label }));
  }, [modesQ.data]);
  const modeLabelOf = useCallback((v: string) =>
    modeOpts.find((m) => m.value === v)?.label ?? PRODUCT_MODE_FALLBACK[v] ?? v,
    [modeOpts],
  );

  const valuesByType = useMemo(() => {
    const map: Record<string, AttrValue[]> = {};
    for (const v of attrValuesQ.data ?? []) (map[v.type] ??= []).push(v);
    return map;
  }, [attrValuesQ.data]);
  const customAttrTypes = useMemo(
    () => (attrTypesQ.data ?? []).filter((t) => !PRODUCT_NATIVE_ATTRS.has(t.key)),
    [attrTypesQ.data],
  );

  const [activeMode, setActiveMode] = useState<string>("all");
  const [view, setView] = useState<"grid" | "table">(() => {
    if (typeof window === "undefined") return "grid";
    return (localStorage.getItem("admin.products.view") as "grid" | "table") || "grid";
  });
  useEffect(() => { localStorage.setItem("admin.products.view", view); }, [view]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ProductRow | null>(null);

  const filtered = useMemo(() => {
    const q = normVi(search.trim());
    let list = activeMode === "all" ? products : products.filter((p) => p.mode === activeMode);
    if (q) {
      list = list.filter((p) =>
        normVi(p.name).includes(q) ||
        normVi(p.fandom ?? "").includes(q) ||
        normVi(p.category ?? "").includes(q) ||
        normVi(p.character_name ?? "").includes(q),
      );
    }
    return list;
  }, [products, activeMode, search]);

  function openNew() {
    setEditing(null);
    setShowForm(true);
  }
  function openEdit(p: ProductRow) {
    setEditing(p);
    setShowForm(true);
  }

  const deleteOne = useMutation({
    mutationFn: (id: string) => api.del(`/products/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
  const bulkDelete = useMutation({
    mutationFn: (ids: string[]) => api.post("/products/bulk-delete", { ids }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["products"] });
      setSelected(new Set());
    },
  });
  const reorder = useMutation({
    mutationFn: (updates: { id: string; sort_order: number }[]) =>
      api.post("/products/reorder", { updates }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
  const inlinePatch = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<ProductRow> }) =>
      api.put(`/products/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });

  async function handleDelete(p: ProductRow) {
    if (!(await confirmAsync(`Xoá "${p.name}"?`))) return;
    deleteOne.mutate(p.id);
  }
  async function handleBulkDelete() {
    if (selected.size === 0) return;
    if (!(await confirmAsync(`Xoá ${selected.size} sản phẩm đã chọn?`))) return;
    bulkDelete.mutate(Array.from(selected));
  }
  function togglePublished(p: ProductRow) {
    inlinePatch.mutate({ id: p.id, patch: { published: !p.published } });
  }
  function moveRow(p: ProductRow, dir: -1 | 1) {
    const idx = filtered.findIndex((x) => x.id === p.id);
    const swapIdx = idx + dir;
    if (idx < 0 || swapIdx < 0 || swapIdx >= filtered.length) return;
    const other = filtered[swapIdx];
    const total = filtered.length;
    // assign new sort_order so that order matches the swap
    const newOrderForP = total - swapIdx;
    const newOrderForOther = total - idx;
    reorder.mutate([
      { id: p.id, sort_order: newOrderForP },
      { id: other.id, sort_order: newOrderForOther },
    ]);
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Quản lý sản phẩm</h2>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border bg-card p-1">
            <button
              type="button" onClick={() => setView("grid")}
              className={`rounded px-2 py-1 ${view === "grid" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              title="Lưới"
            ><LayoutGrid className="h-3.5 w-3.5" /></button>
            <button
              type="button" onClick={() => setView("table")}
              className={`rounded px-2 py-1 ${view === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
              title="Bảng"
            ><Rows3 className="h-3.5 w-3.5" /></button>
          </div>
          <PrimaryBtn onClick={openNew}><Plus className="mr-1 inline h-4 w-4" />Thêm</PrimaryBtn>
        </div>
      </div>

      {/* Mode tabs + search */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Select
          value={activeMode}
          onChange={(e) => setActiveMode(e.target.value)}
          className="w-full sm:w-auto sm:min-w-[200px]"
          title="Lọc theo hình thức"
        >
          <option value="all">Tất cả hình thức ({products.length})</option>
          {modeOpts.map((m) => {
            const n = products.filter((p) => p.mode === m.value).length;
            return (
              <option key={m.value} value={m.value}>{m.label} ({n})</option>
            );
          })}
        </Select>
        <div className="relative w-full sm:w-auto sm:max-w-xs sm:flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Tìm tên / fandom / loại..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-7"
          />
        </div>
      </div>

      {selected.size > 0 ? (
        <div className="flex items-center gap-3 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-sm">
          <span className="font-medium">{selected.size} đã chọn</span>
          <GhostBtn onClick={() => setSelected(new Set())}>Bỏ chọn</GhostBtn>
          <DangerBtn onClick={handleBulkDelete}>
            <Trash2 className="mr-1 inline h-3.5 w-3.5" />Xoá
          </DangerBtn>
        </div>
      ) : null}

      {productsQ.isLoading ? <StatusText>Đang tải sản phẩm…</StatusText> : null}
      {productsQ.error ? <ErrorBox message={getErrorMessage(productsQ.error, "Lỗi tải sản phẩm")} /> : null}

      {filtered.length === 0 && !productsQ.isLoading ? (
        <div className="rounded-xl border border-dashed border-border p-16 text-center">
          <ImagePlus className="mx-auto h-10 w-10 text-muted-foreground opacity-40" />
          <p className="mt-4 text-muted-foreground">Chưa có sản phẩm. Bấm "Thêm" để bắt đầu.</p>
        </div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4 xl:grid-cols-5">
          {filtered.map((p) => (
            <div key={p.id} className="group relative overflow-hidden rounded-lg border border-border bg-card shadow-sm transition hover:shadow-md">
              <label className="absolute left-2 top-2 z-20 opacity-0 group-hover:opacity-100 has-[:checked]:opacity-100">
                <input
                  type="checkbox" checked={selected.has(p.id)}
                  onChange={(e) => setSelected((s) => {
                    const n = new Set(s); e.target.checked ? n.add(p.id) : n.delete(p.id); return n;
                  })}
                />
              </label>
              <div className="relative aspect-[3/4] overflow-hidden bg-muted/50">
                {p.cover_image || p.images[0] ? (
                  <img
                    src={p.cover_image ?? p.images[0]}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                ) : (
                  <div className="grid h-full place-items-center text-muted-foreground"><ImagePlus className="h-10 w-10 opacity-40" /></div>
                )}
                {/* mode badge top-left */}
                <span className="absolute left-2 top-2 z-10 rounded-full bg-background/70 px-2 py-0.5 text-[10px] font-medium backdrop-blur group-has-[:checked]:opacity-0">
                  {modeLabelOf(p.mode)}
                </span>
                {!p.published ? (
                  <span className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-full bg-background/80 px-2 py-0.5 text-[10px] backdrop-blur">
                    <EyeOff className="h-3 w-3" />Ẩn
                  </span>
                ) : null}
                {/* caption overlay */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-2.5 pt-8">
                  <div className="line-clamp-1 text-sm font-medium text-white">{p.name}</div>
                  <div className="line-clamp-1 text-[11px] text-white/70">
                    {[p.fandom, p.category].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                {/* hover actions */}
                <div className="absolute inset-x-2 bottom-2 z-20 flex translate-y-2 gap-1 opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100">
                  <button
                    type="button" onClick={() => openEdit(p)}
                    className="flex-1 rounded-md bg-background/90 px-2 py-1 text-[11px] font-medium backdrop-blur hover:bg-background"
                    title="Sửa"
                  ><Pencil className="mr-1 inline h-3 w-3" />Sửa</button>
                  <button
                    type="button" onClick={() => togglePublished(p)}
                    className="rounded-md bg-background/90 px-2 py-1 backdrop-blur hover:bg-background"
                    title={p.published ? "Đang hiện" : "Đang ẩn"}
                  >{p.published ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}</button>
                  <button
                    type="button" onClick={() => handleDelete(p)}
                    className="rounded-md bg-destructive/90 px-2 py-1 text-destructive-foreground backdrop-blur hover:bg-destructive"
                    title="Xoá"
                  ><Trash2 className="h-3 w-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="w-10 px-2 py-2"></th>
                <th className="w-8 px-2 py-2">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((p) => selected.has(p.id))}
                    onChange={(e) => setSelected(e.target.checked ? new Set(filtered.map((p) => p.id)) : new Set())}
                  />
                </th>
                <th className="w-14 px-2 py-2 text-left">Ảnh</th>
                <th className="px-2 py-2 text-left">Tên</th>
                <th className="px-2 py-2 text-left">Hình thức</th>
                <th className="px-2 py-2 text-left">Fandom</th>
                <th className="px-2 py-2 text-left">Loại</th>
                <th className="px-2 py-2 text-left">Giá</th>
                <th className="px-2 py-2 text-center">Hiện</th>
                <th className="w-24 px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {filtered.map((p, i) => (
                <tr key={p.id} className="hover:bg-muted/30">
                  <td className="px-1 py-1.5 text-center text-muted-foreground">
                    <div className="flex items-center justify-center gap-0.5">
                      <button type="button" onClick={() => moveRow(p, -1)} disabled={i === 0}
                        className="rounded p-0.5 hover:bg-muted disabled:opacity-30" title="Lên">
                        <ArrowUp className="h-3 w-3" />
                      </button>
                      <button type="button" onClick={() => moveRow(p, 1)} disabled={i === filtered.length - 1}
                        className="rounded p-0.5 hover:bg-muted disabled:opacity-30" title="Xuống">
                        <ArrowDown className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox" checked={selected.has(p.id)}
                      onChange={(e) => setSelected((s) => {
                        const n = new Set(s); e.target.checked ? n.add(p.id) : n.delete(p.id); return n;
                      })}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    {p.cover_image || p.images[0] ? (
                      <img src={p.cover_image ?? p.images[0]} alt="" loading="lazy" className="h-10 w-10 rounded object-cover" />
                    ) : (
                      <div className="grid h-10 w-10 place-items-center rounded bg-muted">
                        <ImagePlus className="h-3 w-3 opacity-40" />
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <ProductInlineInput
                      value={p.name}
                      onCommit={(v) => { if (v && v !== p.name) inlinePatch.mutate({ id: p.id, patch: { name: v } }); }}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Select value={p.mode} onChange={(e) => inlinePatch.mutate({ id: p.id, patch: { mode: e.target.value } })} className="!py-1 text-xs">
                      {modeOpts.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </Select>
                  </td>
                  <td className="px-2 py-1.5">
                    <ProductInlineInput
                      value={p.fandom ?? ""}
                      onCommit={(v) => { if (v !== (p.fandom ?? "")) inlinePatch.mutate({ id: p.id, patch: { fandom: v || null } }); }}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <ProductInlineInput
                      value={p.category ?? ""}
                      onCommit={(v) => { if (v !== (p.category ?? "")) inlinePatch.mutate({ id: p.id, patch: { category: v || null } }); }}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <ProductInlineInput
                      value={p.price ?? ""}
                      onCommit={(v) => { if (v !== (p.price ?? "")) inlinePatch.mutate({ id: p.id, patch: { price: v || null } }); }}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <input type="checkbox" checked={p.published} onChange={() => togglePublished(p)} />
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex justify-end gap-1">
                      <GhostBtn onClick={() => openEdit(p)} className="!px-2 !py-1"><Pencil className="h-3.5 w-3.5" /></GhostBtn>
                      <DangerBtn onClick={() => handleDelete(p)} className="!px-2 !py-1"><Trash2 className="h-3.5 w-3.5" /></DangerBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm ? (
        <ProductFormModal
          initial={editing}
          modeOpts={modeOpts}
          valuesByType={valuesByType}
          customAttrTypes={customAttrTypes}
          defaultMode={activeMode === "all" ? "craft" : activeMode}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); qc.invalidateQueries({ queryKey: ["products"] }); }}
        />
      ) : null}
    </section>
  );
}

function ProductInlineInput({ value, onCommit }: { value: string; onCommit: (v: string) => void }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <Input
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => onCommit(v.trim())}
      className="!py-1 text-xs"
    />
  );
}

function ModeTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button" onClick={onClick}
      className={`rounded-sm px-3 py-1.5 text-xs font-medium transition-colors ${
        active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function ProductFormModal({
  initial, modeOpts, valuesByType, customAttrTypes, defaultMode, onClose, onSaved,
}: {
  initial: ProductRow | null;
  modeOpts: { value: string; label: string }[];
  valuesByType: Record<string, AttrValue[]>;
  customAttrTypes: AttrType[];
  defaultMode: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [category, setCategory] = useState(initial?.category ?? "");
  const [fandom, setFandom] = useState(initial?.fandom ?? "");
  const [characterName, setCharacterName] = useState(initial?.character_name ?? "");
  const [materials, setMaterials] = useState(initial?.materials ?? "");
  const [price, setPrice] = useState(initial?.price ?? "");
  const [mode, setMode] = useState(initial?.mode ?? defaultMode ?? "craft");
  const [images, setImages] = useState<string[]>(initial?.images ?? []);
  const [coverImage, setCoverImage] = useState<string>(initial?.cover_image ?? (initial?.images?.[0] ?? ""));
  const [published, setPublished] = useState<boolean>(initial?.published ?? true);
  const [isPublic, setIsPublic] = useState<boolean>(initial?.is_public ?? true);
  const [revenueStream, setRevenueStream] = useState<"main" | "side">(initial?.revenue_stream ?? "main");
  const [depositAmount, setDepositAmount] = useState<string>(String(initial?.deposit_amount ?? 0));
  const [internalNote, setInternalNote] = useState<string>(initial?.internal_note ?? "");
  const [sortOrder] = useState<number>(initial?.sort_order ?? 0);
  const [attributes, setAttributes] = useState<Record<string, string>>(initial?.attributes ?? {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!name.trim()) { setError("Nhập tên sản phẩm"); return; }
    setSaving(true); setError(null);
    const slug = productSlugify(name) + (initial ? "" : `-${Math.random().toString(36).slice(2, 6)}`);
    const payload = {
      slug,
      name: name.trim(),
      description: description.trim() || null,
      category: category.trim() || null,
      fandom: fandom.trim() || null,
      character_name: characterName.trim() || null,
      materials: materials.trim() || null,
      price: price.trim() || null,
      mode,
      images,
      cover_image: coverImage || images[0] || null,
      published,
      is_public: isPublic,
      revenue_stream: revenueStream,
      deposit_amount: Number(depositAmount) || 0,
      internal_note: internalNote.trim() || null,
      sort_order: sortOrder,
      attributes,
    };
    try {
      if (initial) await api.put(`/products/${initial.id}`, payload);
      else await api.post("/products", payload);
      onSaved();
    } catch (e) {
      setError(getErrorMessage(e, "Lưu thất bại"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={initial ? "Sửa sản phẩm" : "Thêm sản phẩm mới"} onClose={onClose}>
      <div className="space-y-4">
        {error ? <ErrorBox message={error} /> : null}

        {/* Images */}
        <ImageInput
          label="Ảnh sản phẩm"
          value={images}
          onChange={setImages}
          cover={coverImage}
          onCoverChange={setCoverImage}
          folder={`san-pham/${mode || "khac"}/${(category || "chua-phan-loai").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        />

        <label className="block">
          <span className="text-xs font-medium">Tên sản phẩm *</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium">Hình thức</span>
            <Select value={mode} onChange={(e) => setMode(e.target.value)}>
              {modeOpts.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </Select>
          </label>
          <ProductSuggestField
            label="Loại phụ kiện" value={category} onChange={setCategory}
            options={valuesByType["category"] ?? []}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <ProductSuggestField
            label="Fandom" value={fandom} onChange={setFandom}
            options={valuesByType["fandom"] ?? []}
          />
          <label className="block">
            <span className="text-xs font-medium">Nhân vật</span>
            <Input value={characterName} onChange={(e) => setCharacterName(e.target.value)} />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <ProductSuggestField
            label="Chất liệu" value={materials} onChange={setMaterials}
            options={valuesByType["material"] ?? []}
          />
          <ProductSuggestField
            label="Giá" value={price} onChange={setPrice}
            options={valuesByType["price_tier"] ?? []}
          />
        </div>

        {customAttrTypes.length > 0 ? (
          <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
              <Tag className="h-3 w-3" />Thuộc tính tuỳ chỉnh
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {customAttrTypes.map((t) => (
                <ProductSuggestField
                  key={t.id}
                  label={t.label}
                  value={attributes[t.key] ?? ""}
                  onChange={(v) => setAttributes((a) => {
                    const next = { ...a };
                    if (!v.trim()) delete next[t.key]; else next[t.key] = v;
                    return next;
                  })}
                  options={valuesByType[t.key] ?? []}
                />
              ))}
            </div>
          </div>
        ) : null}

        <label className="block">
          <span className="text-xs font-medium">Mô tả chi tiết</span>
          <Textarea
            value={description ?? ""}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium">Dòng doanh thu</span>
            <Select value={revenueStream} onChange={(e) => setRevenueStream(e.target.value === "side" ? "side" : "main")}>
              <option value="main">Dòng chính</option>
              <option value="side">Dòng phụ</option>
            </Select>
          </label>
          <label className="block">
            <span className="text-xs font-medium">Tiền cọc gợi ý (VND)</span>
            <Input type="number" min="0" step="1000" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} />
          </label>
        </div>

        <label className="block">
          <span className="text-xs font-medium">Ghi chú nội bộ (không hiển thị công khai)</span>
          <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} rows={2} />
        </label>

        <label className="flex items-center justify-between rounded-md border border-border p-3 text-sm">
          <span>Đã xuất bản (published)</span>
          <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
        </label>
        <label className="flex items-center justify-between rounded-md border border-border p-3 text-sm">
          <span>Hiển thị công khai (is_public)</span>
          <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
        </label>

        <div className="flex gap-2 pt-2">
          <GhostBtn type="button" onClick={onClose} className="flex-1">Huỷ</GhostBtn>
          <PrimaryBtn type="button" onClick={save} disabled={saving} className="flex-1">
            {saving ? "Đang lưu…" : initial ? "Lưu" : "Thêm"}
          </PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}

function ProductSuggestField({
  label, value, onChange, options,
}: {
  label: string; value: string; onChange: (v: string) => void; options: AttrValue[];
}) {
  const listId = `ps-${label.replace(/\s+/g, "-")}-${Math.random().toString(36).slice(2, 7)}`;
  return (
    <label className="block">
      <span className="text-xs font-medium">{label}</span>
      <Input value={value} onChange={(e) => onChange(e.target.value)} list={listId} />
      {options.length > 0 ? (
        <datalist id={listId}>
          {options.map((o) => <option key={o.value} value={o.label} />)}
        </datalist>
      ) : null}
    </label>
  );
}

function MediaPickerModal({
  onPick, onClose, defaultFolder = "", multiple = false, onPickMany,
}: {
  onPick: (url: string) => void;
  onClose: () => void;
  defaultFolder?: string;
  multiple?: boolean;
  onPickMany?: (urls: string[]) => void;
}) {
  const mediaQ = useQuery({
    queryKey: ["media_library"],
    queryFn: async () => {
      const rows = await api.get<any[]>("/media");
      return (rows ?? []) as Media[];
    },
  });
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState<string>(defaultFolder);
  const [picked, setPicked] = useState<string[]>([]);

  const all = mediaQ.data ?? [];
  const folderTree = useMemo(() => {
    const set = new Set<string>();
    for (const m of all) {
      const f = (m.folder ?? "").trim();
      if (!f) continue;
      const parts = f.split("/");
      for (let i = 1; i <= parts.length; i++) set.add(parts.slice(0, i).join("/"));
    }
    return Array.from(set).sort();
  }, [all]);

  const items = useMemo(() => {
    const n = normVi(q.trim());
    let list = all;
    if (!n && folder) {
      list = all.filter((m) => (m.folder ?? "") === folder || (m.folder ?? "").startsWith(folder + "/"));
    }
    if (n) {
      list = all.filter((m) =>
        normVi(m.title ?? "").includes(n) ||
        normVi(m.folder ?? "").includes(n) ||
        (m.tags ?? []).some((t) => normVi(t).includes(n)),
      );
    }
    return list;
  }, [all, q, folder]);

  function toggle(url: string) {
    if (!multiple) { onPick(url); return; }
    setPicked((p) => p.includes(url) ? p.filter((u) => u !== url) : [...p, url]);
  }

  return (
    <Modal title="Chọn ảnh từ thư viện" onClose={onClose}>
      <div className="space-y-3">
        <Input placeholder="Tìm theo tên / tag / folder (toàn thư viện)..." value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="grid gap-3 md:grid-cols-[200px_1fr]">
          <aside className="max-h-[55vh] overflow-y-auto rounded-md border border-border bg-card p-2">
            <button
              type="button"
              onClick={() => { setFolder(""); setQ(""); }}
              className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-sm ${folder === "" && !q ? "bg-primary/15 text-primary" : "hover:bg-muted"}`}
            >
              <span className="inline-flex items-center gap-1.5"><Home className="h-3.5 w-3.5" />Tất cả</span>
              <span className="text-[10px] text-muted-foreground">{all.length}</span>
            </button>
            <MediaFolderTree
              folders={folderTree}
              items={all}
              current={folder}
              onSelect={(p) => { setFolder(p); setQ(""); }}
            />
          </aside>
          <div className="min-w-0">
            <div className="mb-2 text-[11px] text-muted-foreground">
              {q ? `Tìm "${q}" — ${items.length} kết quả` :
                folder ? `Thư mục: ${folder} — ${items.length} ảnh` :
                `Tất cả — ${items.length} ảnh`}
            </div>
            {mediaQ.isLoading ? <StatusText>Đang tải…</StatusText> : null}
            {items.length === 0 && !mediaQ.isLoading ? (
              <p className="text-sm text-muted-foreground">Không có ảnh.</p>
            ) : (
              <div className="grid max-h-[50vh] grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
                {items.map((m) => {
                  const sel = picked.includes(m.url);
                  return (
                    <button
                      key={m.id} type="button" onClick={() => toggle(m.url)}
                      className={`group relative aspect-square overflow-hidden rounded-md border bg-muted ${sel ? "border-primary ring-2 ring-primary" : "border-border hover:border-primary"}`}
                      title={m.title ?? m.url}
                    >
                      <img src={m.url} alt={m.title ?? ""} loading="lazy" className="h-full w-full object-cover" />
                      {sel ? <span className="absolute right-1 top-1 rounded bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">✓</span> : null}
                      {m.title ? <div className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1 py-0.5 text-[10px] text-white">{m.title}</div> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{multiple ? `Đã chọn ${picked.length}` : ""}</span>
          <div className="flex gap-2">
            <GhostBtn type="button" onClick={onClose}>Huỷ</GhostBtn>
            {multiple ? (
              <PrimaryBtn type="button" disabled={picked.length === 0}
                onClick={() => { onPickMany?.(picked); onClose(); }}>
                Thêm {picked.length} ảnh
              </PrimaryBtn>
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
}


/* ============================================================ History & Undo (Phase 8) */

/* ============================================================ ImageInput (dùng chung)
 * Upload nhiều file (drag-drop + click), chọn từ thư viện, dán URL.
 * Hiển thị ô preview giữ đúng tỉ lệ (object-contain, max-h cố định) thay vì crop vuông.
 * Tự sync ảnh đầu tiên làm cover nếu cover trống.
 */
function ImageInput({
  value, onChange, folder, multiple = true, cover, onCoverChange, label = "Ảnh",
}: {
  value: string[];
  onChange: (urls: string[]) => void;
  folder: string;
  multiple?: boolean;
  cover?: string | null;
  onCoverChange?: (url: string) => void;
  label?: string;
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  function addUrl(url: string) {
    const u = url.trim();
    if (!u || value.includes(u)) return;
    const next = multiple ? [...value, u] : [u];
    onChange(next);
    if (!cover && onCoverChange) onCoverChange(u);
  }
  function removeUrl(url: string) {
    const next = value.filter((v) => v !== url);
    onChange(next);
    if (onCoverChange && cover === url) onCoverChange(next[0] ?? "");
  }

  async function uploadFiles(list: FileList | File[]) {
    const files = Array.from(list);
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
    const ok = files.filter((f) => allowed.includes(f.type) && f.size <= 20 * 1024 * 1024);
    if (ok.length === 0) { setErr("Không có file hợp lệ (ảnh ≤ 20MB)"); return; }
    setErr(null);
    const newOnes: string[] = [];
    for (let i = 0; i < ok.length; i++) {
      const f = ok[i];
      setBusy(`Đang upload ${i + 1}/${ok.length}: ${f.name}`);
      try {
        const fd = new FormData();
        fd.append("file", f, f.name);
        fd.append("folder", folder);
        const res = await api.upload<{ url: string }>("/media/upload", fd);
        if (res?.url) {
          newOnes.push(res.url);
          if (!multiple) break;
        }
      } catch (e) {
        setErr(getErrorMessage(e, "Upload lỗi"));
      }
    }
    setBusy(null);
    if (newOnes.length > 0) {
      const merged = multiple
        ? [...value, ...newOnes.filter((u) => !value.includes(u))]
        : [newOnes[0]];
      onChange(merged);
      if (!cover && onCoverChange) onCoverChange(newOnes[0]);
    }
  }

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
        {value.length > 0 ? (
          <span className="text-[10px] text-muted-foreground">{value.length} ảnh · folder: <code className="rounded bg-muted px-1">{folder || "—"}</code></span>
        ) : null}
      </div>

      {value.length > 0 ? (
        <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {value.map((url) => (
            <div key={url} className="group relative overflow-hidden rounded-md border border-border bg-muted">
              <img src={url} alt="" loading="lazy" className="block max-h-40 w-full object-contain" />
              {onCoverChange && multiple ? (
                <button type="button" onClick={() => onCoverChange(url)} title="Đặt làm ảnh bìa"
                  className={`absolute left-1 top-1 rounded-full p-1 transition ${
                    cover === url ? "bg-primary text-primary-foreground" : "bg-background/70 opacity-0 group-hover:opacity-100"
                  }`}><Star className="h-3 w-3" /></button>
              ) : null}
              <button type="button" onClick={() => removeUrl(url)} title="Xoá ảnh"
                className="absolute right-1 top-1 rounded-full bg-background/70 p-1 opacity-0 group-hover:opacity-100"
              ><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      ) : null}

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
        }}
        className={`rounded-md border-2 border-dashed p-3 text-center text-xs transition ${
          dragOver ? "border-primary bg-primary/5" : "border-border bg-muted/30"
        }`}
      >
        {busy ? (
          <p className="text-muted-foreground">{busy}</p>
        ) : (
          <p className="text-muted-foreground">Kéo-thả ảnh vào đây (nhiều file), hoặc dùng các nút bên dưới</p>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif" multiple={multiple} className="hidden"
          onChange={(e) => { if (e.target.files?.length) uploadFiles(e.target.files); e.currentTarget.value = ""; }} />
        <GhostBtn type="button" onClick={() => inputRef.current?.click()}>
          <Plus className="mr-1 inline h-3.5 w-3.5" />Upload từ máy
        </GhostBtn>
        <GhostBtn type="button" onClick={() => setShowPicker(true)}>
          <ImageIcon className="mr-1 inline h-3.5 w-3.5" />Chọn từ thư viện
        </GhostBtn>
        <div className="flex flex-1 gap-1 min-w-[180px]">
          <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="Dán URL ảnh..."
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(urlInput); setUrlInput(""); } }} />
          <GhostBtn type="button" onClick={() => { addUrl(urlInput); setUrlInput(""); }}>Thêm URL</GhostBtn>
        </div>
      </div>

      {err ? <p className="mt-1 text-xs text-destructive">{err}</p> : null}

      {showPicker ? (
        <MediaPickerModal
          defaultFolder={folder}
          multiple={multiple}
          onPick={(url) => { addUrl(url); setShowPicker(false); }}
          onPickMany={(urls) => {
            const fresh = urls.filter((u) => !value.includes(u));
            if (fresh.length === 0) return;
            const merged = multiple ? [...value, ...fresh] : [fresh[0]];
            onChange(merged);
            if (!cover && onCoverChange) onCoverChange(fresh[0]);
          }}
          onClose={() => setShowPicker(false)}
        />
      ) : null}
    </div>
  );
}

type AuditEntity = "tasks" | "rental_inventory";
type AuditAction = "create" | "update" | "delete";
type AuditEntry = {
  id: string;
  entity_type: AuditEntity;
  entity_id: string;
  action: AuditAction;
  before: any | null;
  after: any | null;
  summary: string | null;
  actor_id: string | null;
  undone_at: string | null;
  created_at: string;
};

const AUDIT_ENTITY_LABEL: Record<AuditEntity, string> = {
  tasks: "Task",
  rental_inventory: "Kho hàng",
};
const AUDIT_ACTION_LABEL: Record<AuditAction, string> = {
  create: "Tạo",
  update: "Sửa",
  delete: "Xoá",
};

/* ============================================================ Site Text Page (A5) */

type SiteTextRow = { key: string; value: string; updated_at?: string };

function SiteTextPage() {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["site-texts", "all"],
    queryFn: () => api.get<SiteTextRow[]>("/site-texts"),
  });
  const [q, setQ] = useState("");
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [showNew, setShowNew] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  const filtered = useMemo(
    () => rows.filter(r =>
      !q ||
      r.key.toLowerCase().includes(q.toLowerCase()) ||
      (r.value ?? "").toLowerCase().includes(q.toLowerCase())
    ),
    [rows, q]
  );

  async function saveOne(key: string) {
    const value = edits[key];
    if (value === undefined) return;
    try {
      await api.put("/site-texts/" + encodeURIComponent(key), { value });
      setEdits(e => { const n = { ...e }; delete n[key]; return n; });
      qc.invalidateQueries({ queryKey: ["site-texts"] });
      alert("Đã lưu");
    } catch (e: any) { alert(e?.message ?? "Lỗi lưu"); }
  }

  async function saveAll() {
    const keys = Object.keys(edits);
    if (!keys.length) return;
    try {
      for (const k of keys) {
        await api.put("/site-texts/" + encodeURIComponent(k), { value: edits[k] });
      }
      setEdits({});
      qc.invalidateQueries({ queryKey: ["site-texts"] });
      alert(`Đã lưu ${keys.length} thay đổi`);
    } catch (e: any) { alert(e?.message ?? "Lỗi lưu"); }
  }

  async function addNew() {
    if (!newKey.trim()) { alert("Nhập key"); return; }
    try {
      await api.post("/site-texts", { key: newKey.trim(), value: newValue });
      setNewKey(""); setNewValue(""); setShowNew(false);
      qc.invalidateQueries({ queryKey: ["site-texts"] });
    } catch (e: any) { alert(e?.message ?? "Lỗi tạo"); }
  }

  async function remove(key: string) {
    if (!confirm(`Xoá key "${key}"?`)) return;
    try {
      await api.del("/site-texts/" + encodeURIComponent(key));
      qc.invalidateQueries({ queryKey: ["site-texts"] });
    } catch (e: any) { alert(e?.message ?? "Lỗi xoá"); }
  }

  const dirty = Object.keys(edits).length;

  return (
    <div>
      <p className="mb-4 text-sm text-muted-foreground">
        Tất cả đoạn văn bản hiển thị trên trang công khai. Sửa rồi nhấn lưu.
      </p>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm theo key hoặc nội dung..." className="pl-8" />
        </div>
        <GhostBtn onClick={() => setShowNew(true)}><Plus className="inline h-4 w-4 mr-1" />Thêm key</GhostBtn>
        {dirty > 0 && (
          <PrimaryBtn onClick={saveAll}>Lưu tất cả ({dirty})</PrimaryBtn>
        )}
      </div>

      <div className="rounded-lg border border-border/60 bg-card overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-sm text-muted-foreground">Đang tải...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">Không có kết quả.</div>
        ) : (
          <ul className="divide-y divide-border/40">
            {filtered.map(r => {
              const draft = edits[r.key] ?? r.value;
              const isDirty = edits[r.key] !== undefined && edits[r.key] !== r.value;
              return (
                <li key={r.key} className="p-3 hover:bg-muted/20">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <code className="text-[11px] text-muted-foreground break-all">{r.key}</code>
                        {isDirty && <span className="text-[10px] text-primary">● chưa lưu</span>}
                      </div>
                      <Textarea
                        value={draft}
                        onChange={(e) => setEdits(ed => ({ ...ed, [r.key]: e.target.value }))}
                        rows={Math.max(1, Math.min(6, (draft || "").split("\n").length))}
                        className="text-sm min-h-[2.25rem]"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        disabled={!isDirty}
                        onClick={() => saveOne(r.key)}
                        title="Lưu"
                        className="h-7 w-7 inline-flex items-center justify-center rounded border border-border bg-card hover:bg-muted disabled:opacity-50"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(r.key)}
                        title="Xoá"
                        className="h-7 w-7 inline-flex items-center justify-center rounded border border-border bg-card hover:bg-muted"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showNew && (
        <Modal title="Thêm key mới" onClose={() => setShowNew(false)}>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Key</label>
              <Input value={newKey} onChange={e => setNewKey(e.target.value)} placeholder="vd: home.hero.tagline" />
            </div>
            <div>
              <label className="text-xs font-medium">Giá trị</label>
              <Textarea value={newValue} onChange={e => setNewValue(e.target.value)} />
            </div>
            <div className="flex gap-2 pt-2">
              <GhostBtn className="flex-1" onClick={() => setShowNew(false)}>Huỷ</GhostBtn>
              <PrimaryBtn className="flex-1" onClick={addNew}>Tạo</PrimaryBtn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function HistoryPage() {
  const qc = useQueryClient();
  const entries = useQuery({
    queryKey: ["audit", 200],
    queryFn: () => api.get<AuditEntry[]>("/audit?limit=200"),
  });
  const undo = useMutation({
    mutationFn: (e: AuditEntry) => api.post(`/audit/${e.id}/undo`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["audit"] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Lịch sử & hoàn tác</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Mọi thao tác tạo / sửa / xoá Task và Kho hàng đều được ghi lại. Bấm <strong>Hoàn tác</strong> để quay về trạng thái trước.
        </p>
      </div>

      {entries.isLoading ? (
        <StatusText>Đang tải…</StatusText>
      ) : (entries.data?.length ?? 0) === 0 ? (
        <div className="rounded-md border border-dashed border-border/60 py-12 text-center text-sm text-muted-foreground italic">
          <Clock className="h-8 w-8 mx-auto mb-2 opacity-40" />
          Chưa có lịch sử nào.
        </div>
      ) : (
        <div className="rounded-xl border border-border/60 divide-y divide-border/40 overflow-hidden bg-card">
          {entries.data!.map((e) => {
            const Icon = e.action === "create" ? Plus : e.action === "update" ? Pencil : Trash2;
            const tone =
              e.action === "create" ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/30"
              : e.action === "update" ? "bg-blue-500/15 text-blue-600 border-blue-500/30"
              : "bg-red-500/15 text-red-600 border-red-500/30";
            const undone = !!e.undone_at;
            const when = new Date(e.created_at);
            return (
              <div key={e.id} className={`px-4 py-3 flex items-center gap-3 ${undone ? "opacity-50" : "hover:bg-muted/30"}`}>
                <div className={`h-8 w-8 rounded-full border grid place-items-center ${tone}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {e.summary ?? `${AUDIT_ACTION_LABEL[e.action]} ${AUDIT_ENTITY_LABEL[e.entity_type]}`}
                  </div>
                  <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                    <span className="rounded border border-border/60 px-1.5 py-0.5">{AUDIT_ENTITY_LABEL[e.entity_type]}</span>
                    <span>{fmtDateTime(when)}</span>
                    {undone && <span className="text-orange-600">· đã hoàn tác</span>}
                  </div>
                </div>
                <GhostBtn
                  type="button"
                  onClick={() => undo.mutate(e)}
                  disabled={undone || undo.isPending}
                >
                  <Undo2 className="h-3.5 w-3.5" />
                  <span className="ml-1">Hoàn tác</span>
                </GhostBtn>
              </div>
            );
          })}
        </div>
      )}
      {undo.error ? (
        <p className="text-xs text-destructive">{getErrorMessage(undo.error, "Hoàn tác thất bại")}</p>
      ) : null}
    </div>
  );
}

/* ============================================================ Admin Gallery (Bộ sưu tập)
 * Mỗi product = một bộ sưu tập. Admin có thể:
 *   - Upload nhiều ảnh: file >10MB giữ định dạng gốc + báo lại; còn lại convert
 *     sang webp + lưu Piwigo. Ảnh gốc luôn được copy vào BACKUP_DIR (cấu hình ở trang Sao lưu).
 *   - Kéo-thả ảnh để sắp xếp (HTML5 drag).
 *   - Sửa caption + prompt_log cho từng ảnh.
 *   - Xoá ảnh khỏi bộ sưu tập.
 * Public lightbox sẽ hiển thị caption; prompt_log chỉ admin xem.
 */

type AdminProduct = {
  id: string; name: string; slug: string;
  category?: string | null; mode?: string;
  images: string[];
  cover_image?: string | null;
  image_meta?: Record<string, { caption?: string; prompt_log?: string }> | Record<string, never>;
  piwigo_album_url?: string | null;
};

function BrandLogoAdminCard() {
  const current = useSiteText("brand.logo.url", "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const dropInputRef = useRef<HTMLInputElement | null>(null);

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    const file = files[0];
    setErr(null); setMsg(null); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      fd.append("folder", "branding");
      const res = await api.upload<{ url: string }>("/media/upload", fd);
      if (!res?.url) throw new Error("Server không trả về URL");
      await upsertSiteText("brand.logo.url", res.url);
      setMsg("Đã cập nhật logo. Đang tải lại…");
      setTimeout(() => { if (typeof window !== "undefined") window.location.reload(); }, 400);
    } catch (e) {
      setErr(getErrorMessage(e, "Upload thất bại"));
      setBusy(false);
    }
  }

  async function clear() {
    if (!confirm("Xoá logo? Trang sẽ dùng biểu tượng mặc định.")) return;
    setErr(null); setMsg(null); setBusy(true);
    try {
      await upsertSiteText("brand.logo.url", "");
      setMsg("Đã xoá logo. Đang tải lại…");
      setTimeout(() => { if (typeof window !== "undefined") window.location.reload(); }, 400);
    } catch (e) {
      setErr(getErrorMessage(e, "Xoá thất bại"));
      setBusy(false);
    }
  }

  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
          {current ? (
            <img src={current} alt="Logo" className="h-full w-full object-contain" />
          ) : (
            <span className="text-[10px] text-muted-foreground">Chưa có</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Logo trang</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {current ? "Logo hiện đang hiển thị trên header." : "Chưa có logo — đang dùng biểu tượng mặc định."}
            {" "}Hỗ trợ PNG / JPG / WEBP / SVG / ICO.
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <label className="inline-flex cursor-pointer items-center rounded border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted">
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml,image/x-icon,image/vnd.microsoft.icon,.ico,.svg"
              className="hidden"
              disabled={busy}
              onChange={(e) => upload(e.target.files)}
            />
            {busy ? "Đang xử lý…" : (current ? "Đổi logo" : "Tải lên logo")}
          </label>
          {current && !busy && (
            <button
              type="button"
              onClick={clear}
              className="rounded border border-border bg-background px-3 py-1.5 text-xs font-medium text-destructive hover:bg-muted"
            >
              Xoá
            </button>
          )}
        </div>
      </div>
      {err ? <div className="mt-2 text-xs text-destructive">{err}</div> : null}
      {msg ? <div className="mt-2 text-xs text-muted-foreground">{msg}</div> : null}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          if (busy) return;
          const files = e.dataTransfer.files;
          if (files && files.length > 0) upload(files);
        }}
        onClick={() => { if (!busy) dropInputRef.current?.click(); }}
        className={`mt-3 cursor-pointer rounded-md border-2 border-dashed px-4 py-5 text-center text-xs transition ${
          dragOver ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/60 hover:bg-muted/40"
        } ${busy ? "pointer-events-none opacity-60" : ""}`}
      >
        <input
          ref={dropInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml,image/x-icon,image/vnd.microsoft.icon,.ico,.svg"
          className="hidden"
          disabled={busy}
          onChange={(e) => { upload(e.target.files); e.currentTarget.value = ""; }}
        />
        <div className="font-medium">Kéo thả ảnh logo vào đây, hoặc bấm để chọn từ máy</div>
        <div className="mt-0.5 text-muted-foreground">PNG / JPG / WEBP / SVG / ICO</div>
      </div>
    </div>
  );
}

function BackgroundAdminCard() {
  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="mb-3">
        <div className="text-sm font-semibold">Ảnh nền trang chủ</div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          Bản máy tính và bản điện thoại lưu riêng. Bỏ trống để dùng lại nền gradient neon mặc định.
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <BackgroundSlot
          siteTextKey="home.bg.desktop.url"
          label="Máy tính (màn rộng ≥ 768px)"
        />
        <BackgroundSlot
          siteTextKey="home.bg.mobile.url"
          label="Điện thoại (màn hẹp < 768px)"
        />
      </div>
      <BackgroundOverlayControl />
    </div>
  );
}

function BackgroundOverlayControl() {
  const current = useSiteText("home.bg.overlay", "45");
  const initial = (() => {
    const n = parseInt(current, 10);
    return Number.isNaN(n) ? 45 : Math.max(0, Math.min(80, n));
  })();
  const [value, setValue] = useState<number>(initial);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => { setValue(initial); }, [initial]);

  async function save(next: number) {
    setBusy(true); setMsg(null);
    try {
      await upsertSiteText("home.bg.overlay", String(next));
      setMsg("Đã lưu. F5 để xem thay đổi.");
    } catch (e) {
      setMsg(getErrorMessage(e, "Lưu thất bại"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 rounded-md border border-border/60 bg-background p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-medium">Độ mờ lớp phủ trên ảnh nền</div>
          <div className="text-[11px] text-muted-foreground">
            Tăng để chữ và các ô nổi rõ hơn. 0 = ảnh nguyên bản, 80 = nền gần như tối hoàn toàn.
          </div>
        </div>
        <div className="shrink-0 text-xs font-semibold tabular-nums">{value}%</div>
      </div>
      <input
        type="range"
        min={0}
        max={80}
        step={5}
        value={value}
        disabled={busy}
        onChange={(e) => setValue(parseInt(e.target.value, 10))}
        onPointerUp={() => save(value)}
        onKeyUp={() => save(value)}
        className="mt-2 w-full accent-primary"
      />
      {msg ? <div className="mt-1 text-[11px] text-muted-foreground">{msg}</div> : null}
    </div>
  );
}

function BackgroundSlot({ siteTextKey, label }: { siteTextKey: string; label: string }) {
  const current = useSiteText(siteTextKey, "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const dropInputRef = useRef<HTMLInputElement | null>(null);

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    const file = files[0];
    setErr(null); setMsg(null); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      fd.append("folder", "branding");
      const res = await api.upload<{ url: string }>("/media/upload", fd);
      if (!res?.url) throw new Error("Server không trả về URL");
      await upsertSiteText(siteTextKey, res.url);
      setMsg("Đã cập nhật. Đang tải lại…");
      setTimeout(() => { if (typeof window !== "undefined") window.location.reload(); }, 400);
    } catch (e) {
      setErr(getErrorMessage(e, "Upload thất bại"));
      setBusy(false);
    }
  }

  async function clear() {
    if (!confirm("Xoá ảnh nền? Trang chủ sẽ dùng gradient neon mặc định.")) return;
    setErr(null); setMsg(null); setBusy(true);
    try {
      await upsertSiteText(siteTextKey, "");
      setMsg("Đã xoá. Đang tải lại…");
      setTimeout(() => { if (typeof window !== "undefined") window.location.reload(); }, 400);
    } catch (e) {
      setErr(getErrorMessage(e, "Xoá thất bại"));
      setBusy(false);
    }
  }

  return (
    <div className="rounded-md border border-border/60 bg-background p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="grid h-16 w-24 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
          {current ? (
            <img src={current} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-[10px] text-muted-foreground">Chưa có</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium">{label}</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {current ? "Đang dùng ảnh tuỳ chỉnh." : "Đang dùng gradient mặc định."}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <label className="inline-flex cursor-pointer items-center rounded border border-border bg-card px-2.5 py-1 text-xs font-medium hover:bg-muted">
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="hidden"
              disabled={busy}
              onChange={(e) => upload(e.target.files)}
            />
            {busy ? "Đang xử lý…" : (current ? "Đổi ảnh" : "Tải lên")}
          </label>
          {current && !busy && (
            <button
              type="button"
              onClick={clear}
              className="rounded border border-border bg-card px-2.5 py-1 text-xs font-medium text-destructive hover:bg-muted"
            >
              Xoá
            </button>
          )}
        </div>
      </div>
      {err ? <div className="mt-2 text-xs text-destructive">{err}</div> : null}
      {msg ? <div className="mt-2 text-xs text-muted-foreground">{msg}</div> : null}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          if (busy) return;
          const files = e.dataTransfer.files;
          if (files && files.length > 0) upload(files);
        }}
        onClick={() => { if (!busy) dropInputRef.current?.click(); }}
        className={`mt-2 cursor-pointer rounded-md border-2 border-dashed px-3 py-3 text-center text-[11px] transition ${
          dragOver ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/60 hover:bg-muted/40"
        } ${busy ? "pointer-events-none opacity-60" : ""}`}
      >
        <input
          ref={dropInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          className="hidden"
          disabled={busy}
          onChange={(e) => { upload(e.target.files); e.currentTarget.value = ""; }}
        />
        Kéo thả ảnh vào đây hoặc bấm để chọn (PNG / JPG / WEBP)
      </div>
    </div>
  );
}

function AdminGalleryPage({ user }: { user: PhpUser }) {
  if ((user.role ?? "") !== "admin") {
    return <ErrorBox message="Chỉ admin mới quản lý bộ sưu tập." />;
  }
  const { data: products = [], isLoading, error } = useQuery({
    queryKey: ["products", "admin-gallery"],
    queryFn: () => api.get<AdminProduct[]>("/products"),
  });
  const [openId, setOpenId] = useState<string | null>(null);
  const opened = products.find((p) => p.id === openId) || null;

  // BrandLogoAdminCard inline tránh export thừa — chỉ dùng ở đây.

  return (
    <section className="space-y-4">
      <BrandLogoAdminCard />
      <BackgroundAdminCard />

      <div className="rounded-md border border-border bg-card p-4">
        <h2 className="text-lg font-semibold">Bộ sưu tập</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Chọn một sản phẩm để mở bộ ảnh. Ảnh upload sẽ tự động chuyển webp + lưu Piwigo,
          ảnh gốc được sao lưu vào thư mục cấu hình trong trang <strong>Sao lưu</strong>.
          File &gt;10MB sẽ giữ định dạng gốc và báo lại.
        </p>
      </div>

      {error ? <ErrorBox message={getErrorMessage(error, "Không tải được danh sách")} /> : null}
      {isLoading ? <StatusText>Đang tải…</StatusText> : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {products.map((p) => {
          const cover = p.cover_image || p.images?.[0] || null;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setOpenId(p.id)}
              className="group flex flex-col overflow-hidden rounded-md border border-border bg-card text-left hover:border-primary"
            >
              <div className="relative aspect-[4/3] bg-muted">
                {cover ? (
                  <img src={cover} alt={p.name} loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-xs text-muted-foreground">
                    Chưa có ảnh
                  </div>
                )}
                <span className="absolute right-2 top-2 rounded bg-background/80 px-1.5 py-0.5 text-[10px]">
                  {p.images?.length ?? 0} ảnh
                </span>
              </div>
              <div className="p-2.5">
                <div className="line-clamp-1 text-sm font-medium">{p.name}</div>
                {p.category ? (
                  <div className="text-[11px] text-muted-foreground">{p.category}</div>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>

      {opened ? (
        <AdminGalleryEditor product={opened} onClose={() => setOpenId(null)} />
      ) : null}
    </section>
  );
}

function AdminGalleryEditor({ product, onClose }: { product: AdminProduct; onClose: () => void }) {
  const qc = useQueryClient();
  const [images, setImages] = useState<string[]>(product.images ?? []);
  const [meta, setMeta] = useState<Record<string, { caption?: string; prompt_log?: string }>>(
    (product.image_meta as any) ?? {}
  );
  const [coverImage, setCoverImage] = useState<string | null>(product.cover_image ?? null);
  const [editing, setEditing] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const dragIdx = useRef<number | null>(null);
  const [dropOver, setDropOver] = useState(false);
  const dropFileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setImages(product.images ?? []);
    setMeta((product.image_meta as any) ?? {});
    setCoverImage(product.cover_image ?? null);
  }, [product.id]);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["products"] });
  }

  async function handleUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true); setUploadMsg(null); setWarnings([]);
    const all: string[] = [];
    const warns: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setUploadMsg(`Đang upload ${i + 1}/${files.length}: ${f.name}…`);
      const fd = new FormData();
      fd.append("product_id", product.id);
      fd.append("file", f);
      try {
        const r = await api.upload<{ ok: boolean; url: string; warnings?: string[]; kept_original?: boolean }>(
          "/product-images", fd
        );
        if (r?.url) all.push(r.url);
        if (Array.isArray(r?.warnings)) {
          for (const w of r.warnings) warns.push(`${f.name}: ${w}`);
        }
      } catch (e) {
        warns.push(`${f.name}: ${getErrorMessage(e, "upload thất bại")}`);
      }
    }
    setImages((prev) => [...prev, ...all]);
    setWarnings(warns);
    setUploadMsg(all.length ? `Đã upload ${all.length}/${files.length}` : "Không có ảnh nào upload thành công");
    setUploading(false);
    invalidate();
    if (fileRef.current) fileRef.current.value = "";
  }

  async function persistOrder(next: string[]) {
    setImages(next);
    try {
      await api.put("/product-images/reorder", { product_id: product.id, images: next });
      invalidate();
    } catch (e) {
      setUploadMsg(getErrorMessage(e, "Lưu thứ tự thất bại"));
    }
  }

  async function deleteImage(url: string) {
    if (!(await confirmAsync("Xoá ảnh này khỏi bộ sưu tập? (Ảnh trên Piwigo không bị xoá)"))) return;
    try {
      await api.del("/product-images", { product_id: product.id, url });
      setImages((prev) => prev.filter((u) => u !== url));
      const { [url]: _drop, ...rest } = meta;
      setMeta(rest);
      invalidate();
    } catch (e) {
      setUploadMsg(getErrorMessage(e, "Xoá thất bại"));
    }
  }

  async function saveMeta(url: string, caption: string, prompt_log: string) {
    try {
      await api.put("/product-images/meta", { product_id: product.id, url, caption, prompt_log });
      setMeta((prev) => ({ ...prev, [url]: { caption, prompt_log } }));
      invalidate();
      setEditing(null);
    } catch (e) {
      setUploadMsg(getErrorMessage(e, "Lưu thông tin ảnh thất bại"));
    }
  }

  async function setAsCover(url: string) {
    const prev = coverImage;
    setCoverImage(url);
    try {
      await api.put(`/products/${product.id}`, { cover_image: url });
      setUploadMsg("Đã đặt làm ảnh đại diện.");
      invalidate();
    } catch (e) {
      setCoverImage(prev);
      setUploadMsg(getErrorMessage(e, "Đặt ảnh đại diện thất bại"));
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-3">
      <div className="mt-6 w-full max-w-5xl rounded-md border border-border bg-card p-5 shadow-xl">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold">{product.name}</h2>
            <p className="text-xs text-muted-foreground">
              {images.length} ảnh
              {product.piwigo_album_url ? (
                <> · <a href={product.piwigo_album_url} target="_blank" rel="noreferrer"
                  className="text-primary underline">Mở album Piwigo</a></>
              ) : null}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" multiple accept="image/*"
              onChange={(e) => handleUpload(e.target.files)} className="hidden" id="gallery-upload" />
            <PrimaryBtn type="button" disabled={uploading}
              onClick={() => fileRef.current?.click()}>
              <ImagePlus className="mr-1 inline h-4 w-4" />
              {uploading ? "Đang upload…" : "+ Thêm ảnh"}
            </PrimaryBtn>
            <GhostBtn type="button" onClick={onClose}>Đóng</GhostBtn>
          </div>
        </div>

        {uploadMsg ? (
          <p className="mb-3 rounded-md border border-border bg-muted px-3 py-2 text-xs">{uploadMsg}</p>
        ) : null}
        {warnings.length > 0 ? (
          <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
            <div className="mb-1 font-medium">Có ảnh cần chú ý:</div>
            <ul className="list-disc space-y-0.5 pl-5">
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        ) : null}

        <div
          onDragOver={(e) => { e.preventDefault(); setDropOver(true); }}
          onDragLeave={() => setDropOver(false)}
          onDrop={(e) => {
            e.preventDefault(); setDropOver(false);
            if (uploading) return;
            const files = e.dataTransfer.files;
            if (files && files.length > 0) handleUpload(files);
          }}
          onClick={() => { if (!uploading) dropFileRef.current?.click(); }}
          className={`mb-3 cursor-pointer rounded-md border-2 border-dashed px-4 py-5 text-center text-xs transition ${
            dropOver ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/60 hover:bg-muted/40"
          } ${uploading ? "pointer-events-none opacity-60" : ""}`}
        >
          <input
            ref={dropFileRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            disabled={uploading}
            onChange={(e) => { handleUpload(e.target.files); e.currentTarget.value = ""; }}
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-2 font-medium">
              <Loader2 className="h-4 w-4 animate-spin" />
              {uploadMsg || "Đang upload…"}
            </div>
          ) : (
            <>
              <div className="font-medium">Kéo thả ảnh vào đây để upload, hoặc bấm để chọn từ máy</div>
              <div className="mt-0.5 text-muted-foreground">Hỗ trợ chọn nhiều ảnh cùng lúc (JPG / PNG / WEBP)</div>
            </>
          )}
        </div>

        {images.length === 0 ? null : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {images.map((url, idx) => {
              const m = meta[url] || {};
              return (
                <div
                  key={url}
                  draggable
                  onDragStart={() => { dragIdx.current = idx; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = dragIdx.current;
                    dragIdx.current = null;
                    if (from === null || from === idx) return;
                    const next = images.slice();
                    const [moved] = next.splice(from, 1);
                    next.splice(idx, 0, moved);
                    persistOrder(next);
                  }}
                  className="group relative overflow-hidden rounded-md border border-border bg-background"
                >
                  <div className="flex h-48 items-center justify-center bg-muted/30">
                    <img src={url} alt={m.caption || ""} loading="lazy"
                      className="max-h-48 w-full cursor-move object-contain" />
                  </div>
                  <div className="p-2 text-[11px]">
                    <div className="line-clamp-2 min-h-[2.2em] text-muted-foreground">
                      {m.caption || <span className="italic opacity-60">(chưa có caption)</span>}
                    </div>
                  </div>
                  <div className="absolute inset-x-0 top-0 flex justify-between gap-1 bg-gradient-to-b from-black/60 to-transparent p-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button type="button" onClick={() => setEditing(url)}
                      className="rounded bg-background/90 px-2 py-1 text-[11px] hover:bg-background">
                      <Pencil className="inline h-3 w-3" /> Sửa
                    </button>
                    <button type="button" onClick={() => setAsCover(url)}
                      title={coverImage === url ? "Đang là ảnh đại diện" : "Đặt làm ảnh đại diện (hiện trong showcase)"}
                      className={`rounded px-2 py-1 text-[11px] ${coverImage === url ? "bg-primary text-primary-foreground" : "bg-background/90 hover:bg-background"}`}>
                      <Star className={`inline h-3 w-3 ${coverImage === url ? "fill-current" : ""}`} />
                    </button>
                    <button type="button" onClick={() => deleteImage(url)}
                      className="rounded bg-background/90 px-2 py-1 text-[11px] text-destructive hover:bg-background">
                      <Trash2 className="inline h-3 w-3" />
                    </button>
                  </div>
                  <span className="absolute bottom-1 right-1 rounded bg-background/90 px-1.5 py-0.5 text-[10px]">#{idx + 1}</span>
                  {coverImage === url ? (
                    <span className="absolute bottom-1 left-1 inline-flex items-center gap-0.5 rounded bg-primary/90 px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                      <Star className="h-2.5 w-2.5 fill-current" /> Cover
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {editing ? (
          <ImageMetaEditor
            url={editing}
            initial={meta[editing] || {}}
            onClose={() => setEditing(null)}
            onSave={(c, p) => saveMeta(editing, c, p)}
          />
        ) : null}
      </div>
    </div>
  );
}

function ImageMetaEditor({ url, initial, onClose, onSave }: {
  url: string; initial: { caption?: string; prompt_log?: string };
  onClose: () => void; onSave: (caption: string, prompt_log: string) => void;
}) {
  const [caption, setCaption] = useState(initial.caption || "");
  const [promptLog, setPromptLog] = useState(initial.prompt_log || "");
  return (
    <Modal title="Thông tin ảnh" onClose={onClose}>
      <div className="space-y-3">
        <img src={url} alt="" className="max-h-48 w-full rounded border border-border object-contain" />
        <div>
          <label className="mb-1 block text-xs font-medium">Caption (hiển thị cho khách)</label>
          <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={2}
            placeholder="Mô tả ngắn về ảnh — sẽ hiện trong lightbox public" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">Prompt log (chỉ admin xem)</label>
          <Textarea value={promptLog} onChange={(e) => setPromptLog(e.target.value)} rows={5}
            placeholder="Paste prompt cũ đã nhắn với Lovable / AI để dễ tra cứu sau này…" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <GhostBtn type="button" onClick={onClose}>Huỷ</GhostBtn>
          <PrimaryBtn type="button" onClick={() => onSave(caption, promptLog)}>Lưu</PrimaryBtn>
        </div>
      </div>
    </Modal>
  );
}

/* ============================================================ Customer contact icons (FB/Phone/IG) */

function normalizeFbUrl(s?: string | null): string | null {
  if (!s) return null;
  const t = s.trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  // user nhập username hoặc fb.com/abc
  const clean = t.replace(/^@/, "").replace(/^facebook\.com\//i, "").replace(/^fb\.com\//i, "");
  return `https://fb.com/${clean}`;
}
function normalizeIgUrl(s?: string | null): string | null {
  if (!s) return null;
  const t = s.trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  return `https://instagram.com/${t.replace(/^@/, "")}`;
}

export function CustomerContactIcons({ cust, compact = false }: { cust: Customer | null | undefined; compact?: boolean }) {
  if (!cust) return null;
  const fb = normalizeFbUrl(cust.facebook);
  const ig = normalizeIgUrl(cust.instagram);
  const tel = cust.phone ? `tel:${cust.phone.replace(/\s+/g, "")}` : null;
  if (!fb && !ig && !tel) return null;
  const cls = compact
    ? "inline-flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
    : "inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs hover:bg-muted";
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return (
    <span className={compact ? "inline-flex items-center gap-0.5" : "inline-flex flex-wrap items-center gap-1.5"}>
      {fb ? (
        <a href={fb} target="_blank" rel="noreferrer" onClick={stop} className={cls} title="Mở Facebook">
          <Facebook className="h-3.5 w-3.5" />{compact ? null : <span>Facebook</span>}
        </a>
      ) : null}
      {ig ? (
        <a href={ig} target="_blank" rel="noreferrer" onClick={stop} className={cls} title="Mở Instagram">
          <Instagram className="h-3.5 w-3.5" />{compact ? null : <span>Instagram</span>}
        </a>
      ) : null}
      {tel ? (
        <a href={tel} onClick={stop} className={cls} title={`Gọi ${cust.phone}`}>
          <Phone className="h-3.5 w-3.5" />{compact ? null : <span>{cust.phone}</span>}
        </a>
      ) : null}
    </span>
  );
}

/* ============================================================ Partners & Competitors page */

type Partner = {
  id: string;
  kind: "partner" | "competitor" | string;
  name: string;
  contact?: string | null;
  link?: string | null;
  note?: string | null;
  tags?: string[] | null;
  created_at?: string | null;
  updated_at?: string | null;
};

function PartnersPage() {
  const qc = useQueryClient();
  const [kind, setKind] = useState<"partner" | "competitor">("partner");
  const [editing, setEditing] = useState<Partner | "new" | null>(null);
  const list = useQuery({
    queryKey: ["partners", kind],
    queryFn: () => api.get<Partner[]>("/partners", { kind }),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/partners/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["partners"] }),
  });
  const rows = list.data ?? [];
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-border bg-card p-1 gap-1">
          <ModeTab active={kind === "partner"} onClick={() => setKind("partner")}>Đối tác</ModeTab>
          <ModeTab active={kind === "competitor"} onClick={() => setKind("competitor")}>Đối thủ</ModeTab>
        </div>
        <span className="text-xs text-muted-foreground">{rows.length} mục</span>
        <div className="flex-1" />
        <PrimaryBtn onClick={() => setEditing("new")}>
          <span className="inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Thêm {kind === "partner" ? "đối tác" : "đối thủ"}</span>
        </PrimaryBtn>
      </div>
      {list.error ? <ErrorBox message={getErrorMessage(list.error, "Không tải được danh sách")} /> : null}
      <div className="rounded-md border border-border bg-card">
        {rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">Chưa có mục nào.</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Tên</th>
                <th className="px-3 py-2 font-medium">Liên hệ</th>
                <th className="px-3 py-2 font-medium">Link</th>
                <th className="px-3 py-2 font-medium">Ghi chú</th>
                <th className="px-3 py-2 font-medium">Tags</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-border hover:bg-muted/40">
                  <td className="px-3 py-2 align-top font-medium">{p.name}</td>
                  <td className="px-3 py-2 align-top text-xs">{p.contact || "—"}</td>
                  <td className="px-3 py-2 align-top text-xs">
                    {p.link ? (
                      <a href={/^https?:\/\//i.test(p.link) ? p.link : `https://${p.link}`} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-primary hover:underline">
                        <ExternalLink className="h-3 w-3" /> Mở
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 align-top text-xs">{p.note || "—"}</td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex flex-wrap gap-1">
                      {(p.tags ?? []).map((t) => (
                        <span key={t} className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex gap-1">
                      <GhostBtn onClick={() => setEditing(p)}><Pencil className="h-3.5 w-3.5" /></GhostBtn>
                      <ConfirmBtn message={`Xoá "${p.name}"?`} onConfirm={() => del.mutate(p.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </ConfirmBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing ? (
        <PartnerForm
          kind={kind}
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["partners"] }); }}
        />
      ) : null}
    </section>
  );
}

function PartnerForm({ kind, initial, onClose, onSaved }: {
  kind: "partner" | "competitor"; initial: Partner | null;
  onClose: () => void; onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [k, setK] = useState<"partner" | "competitor">((initial?.kind as any) ?? kind);
  const [contact, setContact] = useState(initial?.contact ?? "");
  const [link, setLink] = useState(initial?.link ?? "");
  const [note, setNote] = useState(initial?.note ?? "");
  const [tagsStr, setTagsStr] = useState((initial?.tags ?? []).join(", "));
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () => {
      const tags = tagsStr.split(",").map((t) => t.trim()).filter(Boolean);
      const payload = { kind: k, name: name.trim(), contact: contact || null, link: link || null, note: note || null, tags };
      return initial
        ? await api.put<Partner>(`/partners/${initial.id}`, payload)
        : await api.post<Partner>(`/partners`, payload);
    },
    onSuccess: () => onSaved(),
    onError: (e: any) => setErr(e?.message ?? "Lỗi lưu"),
  });
  return (
    <Modal title={initial ? "Sửa" : "Thêm mới"} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); if (!name.trim()) { setErr("Thiếu tên"); return; } save.mutate(); }} className="space-y-3">
        <label className="block text-sm">Loại
          <Select value={k} onChange={(e) => setK(e.target.value as any)} className="mt-1">
            <option value="partner">Đối tác</option>
            <option value="competitor">Đối thủ</option>
          </Select>
        </label>
        <label className="block text-sm">Tên <span className="text-destructive">*</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" autoFocus />
        </label>
        <label className="block text-sm">Liên hệ
          <Input value={contact ?? ""} onChange={(e) => setContact(e.target.value)} className="mt-1" placeholder="SĐT / email / người phụ trách" />
        </label>
        <label className="block text-sm">Link
          <Input value={link ?? ""} onChange={(e) => setLink(e.target.value)} className="mt-1" placeholder="fb.com/... hoặc website" />
        </label>
        <label className="block text-sm">Ghi chú
          <Textarea value={note ?? ""} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1" />
        </label>
        <label className="block text-sm">Tags (phẩy cách)
          <Input value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} className="mt-1" placeholder="vd: cosplay, props, ship nhanh" />
        </label>
        {err ? <ErrorBox message={err} /> : null}
        <div className="flex justify-end gap-2 pt-1">
          <GhostBtn type="button" onClick={onClose}>Huỷ</GhostBtn>
          <PrimaryBtn type="submit" disabled={save.isPending}>{save.isPending ? "Đang lưu…" : "Lưu"}</PrimaryBtn>
        </div>
      </form>
    </Modal>
  );
}

/* ============================================================ Finance — Manage categories modal */

export function FinanceCategoriesModal({ stream, onClose }: { stream: Stream; onClose: () => void }) {
  const qc = useQueryClient();
  const cats = useFinanceCategories(stream);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const rename = useMutation({
    mutationFn: async (vars: { from: string; to: string }) =>
      api.post<{ updated: number }>(`/finance/rename-category`, vars),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance"] });
      qc.invalidateQueries({ queryKey: ["finance_categories"] });
      setRenaming(null); setNewName(""); setErr(null);
    },
    onError: (e: any) => setErr(e?.message ?? "Lỗi cập nhật"),
  });
  const known = useMemo(() => {
    const set = new Set<string>([...Object.keys(FIN_CATEGORY_LABEL), ...(cats.data ?? [])]);
    set.delete("khac");
    return Array.from(set).sort((a, b) => (FIN_CATEGORY_LABEL[a] ?? a).localeCompare(FIN_CATEGORY_LABEL[b] ?? b, "vi"));
  }, [cats.data]);
  return (
    <Modal title="Quản lý danh mục tài chính" onClose={onClose}>
      <p className="text-xs text-muted-foreground">
        Đổi tên sẽ <b>cập nhật tất cả giao dịch cũ</b> sang tên mới. Áp dụng cho cả hai dòng.
      </p>
      {err ? <ErrorBox message={err} /> : null}
      <div className="mt-3 max-h-[60vh] space-y-1 overflow-y-auto">
        {known.map((key) => {
          const isRenaming = renaming === key;
          return (
            <div key={key} className="flex items-center gap-2 rounded border border-border bg-card px-3 py-2">
              <div className="flex-1 min-w-0">
                <div className="truncate text-sm font-medium">{FIN_CATEGORY_LABEL[key] ?? key}</div>
                <div className="truncate text-[10px] text-muted-foreground">key: {key}</div>
              </div>
              {isRenaming ? (
                <>
                  <Input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
                    placeholder="Tên mới" className="!w-48" />
                  <PrimaryBtn type="button" disabled={!newName.trim() || rename.isPending}
                    onClick={() => rename.mutate({ from: key, to: newName.trim() })}>
                    Lưu
                  </PrimaryBtn>
                  <GhostBtn type="button" onClick={() => { setRenaming(null); setNewName(""); }}>Huỷ</GhostBtn>
                </>
              ) : (
                <GhostBtn type="button" onClick={() => { setRenaming(key); setNewName(FIN_CATEGORY_LABEL[key] ?? key); }}>
                  <Pencil className="h-3.5 w-3.5" /> Đổi tên
                </GhostBtn>
              )}
            </div>
          );
        })}
        {known.length === 0 ? <p className="text-sm text-muted-foreground">Chưa có danh mục.</p> : null}
      </div>
      <div className="mt-3 flex justify-end">
        <GhostBtn type="button" onClick={onClose}>Đóng</GhostBtn>
      </div>
    </Modal>
  );
}

/* ============================================================ Reports helpers */

/** Tính kì trước cùng độ dài với preset. Trả về [prevFrom, prevTo] dạng YYYY-MM-DD. */
function prevRangeFor(p: FinancePeriod): { from: string; to: string } {
  const today = new Date();
  const y = today.getFullYear(), m = today.getMonth(), d = today.getDate();
  const fmt = (dt: Date) => dt.toISOString().slice(0, 10);
  switch (p) {
    case "today": {
      const dt = new Date(y, m, d - 1);
      return { from: fmt(dt), to: fmt(dt) };
    }
    case "week": {
      const dow = (today.getDay() + 6) % 7;
      const start = new Date(y, m, d - dow - 7);
      const end = new Date(y, m, d - dow - 1);
      return { from: fmt(start), to: fmt(end) };
    }
    case "month": {
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0);
      return { from: fmt(start), to: fmt(end) };
    }
    case "quarter": {
      const qStartMonth = Math.floor(m / 3) * 3;
      const start = new Date(y, qStartMonth - 3, 1);
      const end = new Date(y, qStartMonth, 0);
      return { from: fmt(start), to: fmt(end) };
    }
    case "year": {
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    }
    default: return { from: fmt(new Date(2000, 0, 1)), to: fmt(today) };
  }
}

/** Tên danh mục đơn theo dòng. */
const ORDER_CATEGORIES_MAIN = [
  { key: "rental-costume", label: "Cho thuê / Trang phục" },
  { key: "lam-do-props",   label: "Làm đồ / Props + Phụ kiện cosplay" },
  { key: "chup-anh",       label: "Chụp ảnh" },
] as const;
const ORDER_CATEGORIES_SIDE = [
  { key: "rental-gun", label: "Cho thuê / Mô hình súng" },
  { key: "order",      label: "Order" },
] as const;

function classifyTaskForOrders(
  t: Task,
  projectById: Map<string, Project>,
  invById: Map<string, InventoryItem>,
  stream: Stream,
): string | null {
  const proj = projectById.get(t.project_id);
  const grp = (proj?.project_group ?? "") as string;
  const invIds = Array.isArray(t.inventory_item_ids) ? t.inventory_item_ids : [];
  const invCats = new Set(invIds.map((id) => invById.get(id)?.category).filter(Boolean) as string[]);
  if (stream === "main") {
    if (grp === "chup-anh") return "chup-anh";
    if (grp === "lam-do") return "lam-do-props";
    if (grp === "cho-thue") {
      // Nếu có ít nhất 1 phụ kiện cosplay → tính vào Làm đồ/Props+phụ kiện.
      if (invCats.has("phu-kien-cosplay") && !invCats.has("trang-phuc")) return "lam-do-props";
      return "rental-costume";
    }
    return null;
  }
  // side
  if (grp === "order" || proj?.key === "order") return "order";
  if (proj?.key === "rental-gun" || invCats.has("mo-hinh-sung")) return "rental-gun";
  return null;
}

/** Trả về [bucketLabel, bucketStart] cho 1 task theo period — bucket=last N kì gần nhất. */
function bucketFor(date: Date, period: "week" | "month" | "quarter" | "year"): { label: string; start: Date } {
  if (period === "week") {
    const d = new Date(date);
    const dow = (d.getDay() + 6) % 7;
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dow);
    return { label: `T${getISOWeek(start)}`, start };
  }
  if (period === "month") {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    return { label: `${start.getMonth() + 1}/${start.getFullYear()}`, start };
  }
  if (period === "quarter") {
    const q = Math.floor(date.getMonth() / 3);
    const start = new Date(date.getFullYear(), q * 3, 1);
    return { label: `Q${q + 1}/${start.getFullYear()}`, start };
  }
  const start = new Date(date.getFullYear(), 0, 1);
  return { label: String(start.getFullYear()), start };
}
function getISOWeek(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function fmtPct(n: number | null): string {
  if (n == null || !isFinite(n)) return "—";
  const s = (n >= 0 ? "+" : "") + n.toFixed(1) + "%";
  return s;
}


/* CSV helpers (templates, download, import client) đã chuyển sang src/shared/csv/* — re-export ở đầu file. */
/* CsvImporter floating panel removed — CSV import is wired into per-page UIs. */

